import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  IconButton,
  Chip,
  Grid,
  Alert,
  TextField,
  InputAdornment,
  useTheme,
  useMediaQuery,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  Collapse,
  Link,
  Tooltip,
  LinearProgress,
  ButtonBase,
  Fade,
  Snackbar,
} from '@mui/material';
import {
  PersonAdd,
  Close,
  SwapHoriz,
  Star,
  Search,
  CheckCircle,
  Lock as LockIcon,
  Bolt as BoltIcon,
  WarningAmberRounded,
  InfoOutlined,
  CheckCircleOutline,
  ErrorOutline,
  History as HistoryIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AutoAwesome,
  EmojiEvents,
  SportsTennis,
  KeyboardArrowDown,
  AccountBalance,
  ShoppingCart,
  Male,
  Female,
  WarningAmber,
} from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AppHeader from '../components/common/AppHeader';
import LeagueNav from '../components/common/LeagueNav';
import StatusChip from '../components/common/StatusChip';
import LeagueAssistant from '../components/LeagueAssistant';
import TransferModal, { TransferData } from '../components/squad/TransferModal';
import TennisTransferModal from '../components/tennis/TennisTransferModal';
import { playerPoolService, leagueService, squadService, squadPlayerUtils, leaderboardSnapshotService, tennisSquadService, tennisPlayerPoolService } from '../services/firestore';
import type { League, Player, PlayerWithPerformance, SquadPlayer, LeagueSquad } from '../types/database';
import { calculatePlayerContribution as calculatePlayerContributionUtil, calculateSquadPoints as calculateSquadPointsUtil } from '../utils/pointsCalculation';
import { computeCricketTransferUpdate } from '../utils/cricketTransfer';
import themeColors from '../theme/colors';
import { flagEmoji, cricketFlagEmoji } from '../utils/countryFlags';
import { formatMatchForDropdown } from '../utils/scheduleParser';
import EnhancedAlert, { SquadStatusItem, EnhancedAlertAction } from '../components/common/EnhancedAlert';
import { TeamLogo } from '../utils/teamLogos';
import { vibrate } from '../utils/haptics';
import { getLastMatchLabel, getLastMatchPoints, shouldShowRemoveButton } from '../utils/squadPlayerCard';

interface SelectedPlayer extends PlayerWithPerformance {
  position: 'regular' | 'bench';
  isOverseas: boolean;
}

const SquadSelectionPage: React.FC = () => {
  const { leagueId } = useParams<{ leagueId: string }>();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [league, setLeague] = useState<League | null>(null);
  const [availablePlayers, setAvailablePlayers] = useState<PlayerWithPerformance[]>([]);
  const [selectedPlayers, setSelectedPlayers] = useState<SelectedPlayer[]>([]);
  const [captainId, setCaptainId] = useState<string | null>(null);
  const [viceCaptainId, setViceCaptainId] = useState<string | null>(null);
  const [xFactorId, setXFactorId] = useState<string | null>(null);
  const [powerplayMatch, setPowerplayMatch] = useState('');
  const [ppActivatedAt, setPpActivatedAt] = useState<Date | null>(null);
  const [squadSummaryOpen, setSquadSummaryOpen] = useState(true);
  const [ppMatchConfirmOpen, setPpMatchConfirmOpen] = useState(false);
  const [ppMatchPending, setPpMatchPending] = useState('');
  const [ppDialogOpen, setPpDialogOpen] = useState(false);
  const [ppDialogStep, setPpDialogStep] = useState<'select' | 'confirm' | 'success'>('select');
  const [hiddenPlayerId, setHiddenPlayerId] = useState<string | null>(null);
  const [hiddenPlayerSearch, setHiddenPlayerSearch] = useState('');
  const [filterRole, setFilterRole] = useState<string>('all');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Enhanced error state
  const [enhancedError, setEnhancedError] = useState<{
    severity: 'error' | 'warning' | 'info';
    title: string;
    message: string;
    squadStatus?: SquadStatusItem[];
    suggestions?: string[];
    actions?: EnhancedAlertAction[];
    errorCode?: string;
    isCriticalError?: boolean;
  } | null>(null);

  const [existingSquad, setExistingSquad] = useState<LeagueSquad | null>(null);
  const [isDeadlinePassed, setIsDeadlinePassed] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [isLeagueStarted, setIsLeagueStarted] = useState(false);
  const [canMakeTransfer, setCanMakeTransfer] = useState(false);
  const [tennisTransferOpen, setTennisTransferOpen] = useState(false);
  const [noticeDismissed, setNoticeDismissed] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastIsUpdate, setToastIsUpdate] = useState(false);
  const [draftSavedOpen, setDraftSavedOpen] = useState(false);

  // Predictions state
  const [topRunScorer, setTopRunScorer] = useState('');
  const [topWicketTaker, setTopWicketTaker] = useState('');
  const [winningTeam, setWinningTeam] = useState('');

  // Tennis squad state: tennisSlots[i] = { gender, slotIndex, player? }
  // men slots: indices 0..menSlots-1, women: indices 0..womenSlots-1
  const [tennismenSlots, setTennismenSlots] = useState<(string | null)[]>([null, null, null, null, null]);
  const [tenniswomenSlots, setTenniswomenSlots] = useState<(string | null)[]>([null, null, null, null, null]);
  const [mensPredictionId, setMensPredictionId] = useState<string>('');
  const [womensPredictionId, setWomensPredictionId] = useState<string>('');
  // Pool entries for tennis (filtered by gender)
  const [tennisPool, setTennisPool] = useState<import('../types/database').PlayerPoolEntry[]>([]);

  // Hidden player conflict alert
  const [hiddenConflictAlert, setHiddenConflictAlert] = useState('');

  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Auto-open transfer modal when navigated here with ?openTransfer=true
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('openTransfer') === 'true' && canMakeTransfer) {
      setTransferModalOpen(true);
    }
  }, [location.search, canMakeTransfer]);

  // Show once-per-submit toast when the user returns to the squad page after submitting
  useEffect(() => {
    if (!leagueId) return;
    const action = sessionStorage.getItem(`fcl_squad_action_${leagueId}`);
    if (action) {
      setToastIsUpdate(action === 'update');
      setToastOpen(true);
      sessionStorage.removeItem(`fcl_squad_action_${leagueId}`);
    }
  }, [leagueId]);


  const playerNameOptions = useMemo(() => {
    const names = availablePlayers.map(p => p.name);
    return names.filter((n, i) => names.indexOf(n) === i).sort();
  }, [availablePlayers]);

  const teamOptions = useMemo(() => {
    const teams = availablePlayers.map(p => p.team).filter((t): t is string => Boolean(t));
    return teams.filter((t, i) => teams.indexOf(t) === i).sort();
  }, [availablePlayers]);

  useEffect(() => {
    const loadLeagueAndPlayers = async () => {
      if (!leagueId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Load league from Firestore
        const league = await leagueService.getById(leagueId);

        if (!league) {
          console.error('League not found');
          setLoading(false);
          return;
        }

        setLeague(league);

        // Check if deadline has passed
        const deadlinePassed = new Date() > new Date(league.squadDeadline);
        setIsDeadlinePassed(deadlinePassed);
        if (deadlinePassed) setSquadSummaryOpen(false);

        // Check if league has started
        const now = new Date();
        const leagueStarted = league.status === 'active' ||
                             league.tournamentStarted === true ||
                             now >= new Date(league.startDate);
        setIsLeagueStarted(leagueStarted);

        // Check if transfers can be made
        const isMidSeasonWindowOpen = !!(league.transferTypes?.midSeasonTransfers.enabled &&
          now >= new Date(league.transferTypes.midSeasonTransfers.windowStartDate) &&
          now <= new Date(league.transferTypes.midSeasonTransfers.windowEndDate));

        const transfersEnabled = !!(leagueStarted && (
          (league.sport === 'tennis' && league.tennisTransfersEnabled === true) ||
          league.flexibleChangesEnabled === true ||
          league.benchChangesEnabled === true ||
          league.wildcardChangesEnabled === true ||
          isMidSeasonWindowOpen
        ));
        setCanMakeTransfer(transfersEnabled);

        // Load existing squad if user has one
        if (user) {
          try {
            const squadSvc = league.sport === 'tennis' ? tennisSquadService : squadService;
            const userSquad = await squadSvc.getByUserAndLeague(user.uid, leagueId);
            setExistingSquad(userSquad);
          } catch (err) {
            setExistingSquad(null);
          }
        }

        // Fetch player pool if league has a playerPoolId
        if (league.playerPoolId) {
          try {
            const poolSvc = league.sport === 'tennis' ? tennisPlayerPoolService : playerPoolService;
            const playerPool = await poolSvc.getById(league.playerPoolId);

            if (playerPool && playerPool.players.length > 0) {
              if (league.sport === 'tennis') {
                setTennisPool(playerPool.players);
                setAvailablePlayers([]);
              } else {
                const playersFromPool: PlayerWithPerformance[] = playerPool.players.map(entry => ({
                  id: entry.playerId,
                  name: entry.name,
                  team: entry.team,
                  country: 'India',
                  role: entry.role,
                  isActive: true,
                  availability: 'available' as const,
                  isOverseas: entry.isOverseas ?? false,
                  battingInnings: entry.battingInnings,
                  bowlingSpells: entry.bowlingSpells,
                  fieldingPerformances: entry.fieldingPerformances,
                  stats: {
                    T20: { matches: 0, runs: 0, wickets: 0, economy: 0, strikeRate: 0, catches: 0, recentForm: entry.points },
                    ODI: { matches: 0, runs: 0, wickets: 0, economy: 0, strikeRate: 0, catches: 0, recentForm: entry.points },
                    Test: { matches: 0, runs: 0, wickets: 0, economy: 0, strikeRate: 0, catches: 0, recentForm: entry.points }
                  },
                  createdAt: new Date(),
                  updatedAt: new Date()
                }));
                setAvailablePlayers(playersFromPool);
              }
            } else {
              console.error('Player pool not found or empty');
              setAvailablePlayers([]);
            }
          } catch (error) {
            console.error('Error fetching player pool:', error);
            setAvailablePlayers([]);
          }
        } else {
          // No pool selected - show empty state
          setAvailablePlayers([]);
        }

        setLoading(false);
      } catch (error) {
        console.error('Error loading league data:', error);
        setLoading(false);
      }
    };

    loadLeagueAndPlayers();
  }, [leagueId, user]);

  // Check if the deadline/transfer notice was dismissed in the last 24h
  useEffect(() => {
    if (!user?.uid || !leagueId) return;
    const raw = localStorage.getItem(`fcl_notice_${user.uid}_${leagueId}`);
    if (raw) {
      try {
        const { dismissedAt } = JSON.parse(raw);
        if (Date.now() - dismissedAt < 24 * 60 * 60 * 1000) setNoticeDismissed(true);
      } catch { /* ignore malformed entry */ }
    }
  }, [user?.uid, leagueId]);

  const handleDismissNotice = () => {
    if (!user?.uid || !leagueId) return;
    localStorage.setItem(`fcl_notice_${user.uid}_${leagueId}`, JSON.stringify({ dismissedAt: Date.now() }));
    setNoticeDismissed(true);
  };

  const handleOpenPpDialog = () => {
    setPpDialogStep('select');
    setPpMatchPending('');
    setPpDialogOpen(true);
  };

  // Populate selectedPlayers from existing squad after players are loaded
  useEffect(() => {
    if (!existingSquad || !league) return;

    // ── Tennis: restore directly from Firestore data — does not need availablePlayers ──
    if (league.sport === 'tennis') {
      if (existingSquad.mensPrediction) setMensPredictionId(existingSquad.mensPrediction);
      if (existingSquad.womensPrediction) setWomensPredictionId(existingSquad.womensPrediction);

      const menSlots  = league.tennisSquadRules?.menSlots  ?? 5;
      const womenSlots = league.tennisSquadRules?.womenSlots ?? 5;
      const menArr:   (string | null)[] = Array(menSlots).fill(null);
      const womenArr: (string | null)[] = Array(womenSlots).fill(null);
      for (const sp of existingSquad.players) {
        if (sp.gender === 'men'   && sp.slotIndex !== undefined && sp.slotIndex < menSlots)
          menArr[sp.slotIndex]   = sp.playerId;
        if (sp.gender === 'women' && sp.slotIndex !== undefined && sp.slotIndex < womenSlots)
          womenArr[sp.slotIndex] = sp.playerId;
      }
      setTennismenSlots(menArr);
      setTenniswomenSlots(womenArr);
      return;
    }

    // ── Cricket: requires availablePlayers to be loaded ──
    if (!availablePlayers.length) return;

    // Restore captain, vice-captain, and X-factor IDs
    setCaptainId(existingSquad.captainId || null);
    setViceCaptainId(existingSquad.viceCaptainId || null);
    setXFactorId(existingSquad.xFactorId || null);

    // Load powerplay match number if it exists
    if (existingSquad.powerplayMatchNumber) {
      setPowerplayMatch(existingSquad.powerplayMatchNumber.toString());
    }

    // Load ppActivatedAt if it exists (activation mode)
    if (existingSquad.ppActivatedAt) {
      setPpActivatedAt(new Date(existingSquad.ppActivatedAt));
    }

    // Load hidden player if it exists
    if (existingSquad.hiddenPlayerId) {
      setHiddenPlayerId(existingSquad.hiddenPlayerId);
    }

    // Load predictions if they exist
    if (existingSquad.predictions) {
      setTopRunScorer(existingSquad.predictions.topRunScorer || '');
      setTopWicketTaker(existingSquad.predictions.topWicketTaker || '');
      setWinningTeam(existingSquad.predictions.winningTeam || '');
    }

    // Convert SquadPlayer[] to SelectedPlayer[]
    const squadPlayers: SelectedPlayer[] = existingSquad.players.map((squadPlayer, index) => {
      // Find the full player data from availablePlayers
      const fullPlayer = availablePlayers.find(p => p.id === squadPlayer.playerId);

      if (!fullPlayer) {
        // If player not found in pool, create a basic player object
        return {
          id: squadPlayer.playerId,
          name: squadPlayer.playerName,
          team: squadPlayer.team,
          country: 'India',
          role: squadPlayer.role,
          isActive: true,
          availability: 'available' as const,
          isOverseas: false, // Default to false if player not found in pool
          stats: {
            T20: { matches: 0, runs: 0, wickets: 0, economy: 0, strikeRate: 0, catches: 0, recentForm: squadPlayer.points },
            ODI: { matches: 0, runs: 0, wickets: 0, economy: 0, strikeRate: 0, catches: 0, recentForm: squadPlayer.points },
            Test: { matches: 0, runs: 0, wickets: 0, economy: 0, strikeRate: 0, catches: 0, recentForm: squadPlayer.points }
          },
          createdAt: new Date(),
          updatedAt: new Date(),
          position: index >= league.squadSize ? 'bench' : 'regular'
        };
      }

      // Determine position (only regular or bench)
      const position: 'regular' | 'bench' = index >= league.squadSize ? 'bench' : 'regular';

      return {
        ...fullPlayer,
        isOverseas: fullPlayer.isOverseas ?? false, // Ensure isOverseas is always a boolean
        position
      };
    });

    setSelectedPlayers(squadPlayers);
  }, [existingSquad, availablePlayers, league, tennisPool]);

  const getPositionCounts = () => {
    const counts = {
      batsman: selectedPlayers.filter(p => p.role === 'batsman' && p.position !== 'bench').length,
      bowler: selectedPlayers.filter(p => p.role === 'bowler' && p.position !== 'bench').length,
      allrounder: selectedPlayers.filter(p => p.role === 'allrounder' && p.position !== 'bench').length,
      wicketkeeper: selectedPlayers.filter(p => p.role === 'wicketkeeper' && p.position !== 'bench').length,
      bench: selectedPlayers.filter(p => p.position === 'bench').length,
    };
    return counts;
  };

  const isTennisSquadValid = () => {
    if (!league || league.sport !== 'tennis') return false;
    const menSlots = league.tennisSquadRules?.menSlots ?? 5;
    const womenSlots = league.tennisSquadRules?.womenSlots ?? 5;
    const menFilled = tennismenSlots.slice(0, menSlots).every(s => s !== null);
    const womenFilled = tenniswomenSlots.slice(0, womenSlots).every(s => s !== null);
    return menFilled && womenFilled && !!mensPredictionId && !!womensPredictionId;
  };

  const isSquadValid = () => {
    if (!league) return false;
    if (league.sport === 'tennis') return isTennisSquadValid();
    const counts = getPositionCounts();
    const mainSquadCount = counts.batsman + counts.bowler + counts.allrounder + counts.wicketkeeper;
    const benchRequired = league.transferTypes?.benchTransfers?.enabled ? league.transferTypes.benchTransfers.benchSlots : 0;

    // Check overseas player limit if enabled
    let overseasValid = true;
    if (league.squadRules.overseasPlayersEnabled) {
      const mainSquadPlayers = selectedPlayers.filter(p => p.position !== 'bench');
      const overseasCount = mainSquadPlayers.filter((p: any) => p.isOverseas).length;
      const maxOverseas = league.squadRules.maxOverseasPlayers || 4;
      overseasValid = overseasCount <= maxOverseas;
    }

    // Check powerplay match selection if powerplay is enabled
    // In activation mode, match selection is optional at squad submission time —
    // the user activates and picks their match during the league, not upfront.
    let powerplayValid = true;
    if (league.powerplayEnabled && (league.ppMatchMode ?? 'fixed') === 'fixed') {
      powerplayValid = powerplayMatch.trim() !== '';
    }

    // Hidden player is mandatory if feature is enabled
    let hiddenPlayerValid = true;
    if (league.hiddenPlayerEnabled) {
      hiddenPlayerValid = hiddenPlayerId !== null && hiddenPlayerId !== '';
    }

    // Hidden player must not be in main squad or bench
    let hiddenPlayerNotInSquad = true;
    if (hiddenPlayerId) {
      hiddenPlayerNotInSquad = !selectedPlayers.some(p => p.id === hiddenPlayerId);
    }

    return mainSquadCount === league.squadSize &&
           counts.batsman >= league.squadRules.minBatsmen &&
           counts.bowler >= league.squadRules.minBowlers &&
           counts.wicketkeeper >= league.squadRules.minWicketkeepers &&
           counts.bench >= benchRequired &&
           overseasValid &&
           powerplayValid &&
           hiddenPlayerValid &&
           hiddenPlayerNotInSquad &&
           captainId !== null &&
           viceCaptainId !== null &&
           xFactorId !== null &&
           captainId !== viceCaptainId &&
           captainId !== xFactorId &&
           viceCaptainId !== xFactorId &&
           topRunScorer.trim() !== '' &&
           topWicketTaker.trim() !== '' &&
           winningTeam.trim() !== '';
  };

  // Get detailed validation errors for enhanced error display
  const getDetailedValidationErrors = (): {
    squadStatus: SquadStatusItem[];
    suggestions: string[];
    isValid: boolean;
  } => {
    if (!league) return { squadStatus: [], suggestions: [], isValid: false };

    const counts = getPositionCounts();
    const mainSquadCount = counts.batsman + counts.bowler + counts.allrounder + counts.wicketkeeper;
    const benchRequired = league.transferTypes?.benchTransfers?.enabled ? league.transferTypes.benchTransfers.benchSlots : 0;

    const mainSquadPlayers = selectedPlayers.filter(p => p.position !== 'bench');
    const overseasCount = mainSquadPlayers.filter((p: any) => p.isOverseas).length;
    const maxOverseas = league.squadRules.maxOverseasPlayers || 4;

    const squadStatus: SquadStatusItem[] = [
      {
        label: 'Main Squad',
        current: mainSquadCount,
        required: league.squadSize,
        isValid: mainSquadCount === league.squadSize
      },
      {
        label: 'Batsmen',
        current: counts.batsman,
        required: league.squadRules.minBatsmen,
        isValid: counts.batsman >= league.squadRules.minBatsmen
      },
      {
        label: 'Bowlers',
        current: counts.bowler,
        required: league.squadRules.minBowlers,
        isValid: counts.bowler >= league.squadRules.minBowlers
      },
      {
        label: 'Wicketkeepers',
        current: counts.wicketkeeper,
        required: league.squadRules.minWicketkeepers,
        isValid: counts.wicketkeeper >= league.squadRules.minWicketkeepers
      }
    ];

    if (benchRequired > 0) {
      squadStatus.push({
        label: 'Bench',
        current: counts.bench,
        required: benchRequired,
        isValid: counts.bench >= benchRequired
      });
    }

    if (league.squadRules.overseasPlayersEnabled) {
      squadStatus.push({
        label: 'Overseas',
        current: overseasCount,
        required: maxOverseas,
        isValid: overseasCount <= maxOverseas
      });
    }

    // Build suggestions
    const suggestions: string[] = [];

    if (mainSquadCount < league.squadSize) {
      suggestions.push(`Add ${league.squadSize - mainSquadCount} more player${league.squadSize - mainSquadCount > 1 ? 's' : ''} to main squad`);
    } else if (mainSquadCount > league.squadSize) {
      suggestions.push(`Remove ${mainSquadCount - league.squadSize} player${mainSquadCount - league.squadSize > 1 ? 's' : ''} from main squad or move to bench`);
    }

    if (counts.batsman < league.squadRules.minBatsmen) {
      suggestions.push(`Add ${league.squadRules.minBatsmen - counts.batsman} more batsmen`);
    }

    if (counts.bowler < league.squadRules.minBowlers) {
      suggestions.push(`Add ${league.squadRules.minBowlers - counts.bowler} more bowler${league.squadRules.minBowlers - counts.bowler > 1 ? 's' : ''}`);
    }

    if (counts.wicketkeeper < league.squadRules.minWicketkeepers) {
      suggestions.push(`Add ${league.squadRules.minWicketkeepers - counts.wicketkeeper} more wicketkeeper${league.squadRules.minWicketkeepers - counts.wicketkeeper > 1 ? 's' : ''}`);
    }

    if (benchRequired > 0 && counts.bench < benchRequired) {
      suggestions.push(`Add ${benchRequired - counts.bench} more bench player${benchRequired - counts.bench > 1 ? 's' : ''}`);
    }

    if (league.squadRules.overseasPlayersEnabled && overseasCount > maxOverseas) {
      suggestions.push(`Remove ${overseasCount - maxOverseas} overseas player${overseasCount - maxOverseas > 1 ? 's' : ''} or replace with local player${overseasCount - maxOverseas > 1 ? 's' : ''}`);
    }

    if (!captainId) {
      suggestions.push('Select a Captain');
    }

    if (!viceCaptainId) {
      suggestions.push('Select a Vice-Captain');
    }

    if (!xFactorId) {
      suggestions.push('Select an X-Factor');
    }

    if (league.powerplayEnabled && (league.ppMatchMode ?? 'fixed') === 'fixed' && powerplayMatch.trim() === '') {
      suggestions.push('Select a Powerplay match');
    }

    if (league.hiddenPlayerEnabled && !hiddenPlayerId) {
      suggestions.push('Select your 12th Hidden Player (mandatory)');
    }

    if (hiddenPlayerId && selectedPlayers.some(p => p.id === hiddenPlayerId)) {
      suggestions.push('Your 12th Hidden Player is in your squad — remove them from squad/bench or pick a different hidden player');
    }

    if (topRunScorer.trim() === '') {
      suggestions.push('Enter your Top Run Scorer prediction');
    }

    if (topWicketTaker.trim() === '') {
      suggestions.push('Enter your Top Wicket Taker prediction');
    }

    if (winningTeam.trim() === '') {
      suggestions.push('Enter your Winning Team prediction');
    }

    return {
      squadStatus,
      suggestions,
      isValid: isSquadValid()
    };
  };

  // Use shared calculation utility for consistency across the app
  const calculateSquadPoints = (
    players: SquadPlayer[],
    captainId: string | null,
    viceCaptainId: string | null,
    xFactorId: string | null,
    bankedPoints: number = 0
  ): { totalPoints: number; captainPoints: number; viceCaptainPoints: number; xFactorPoints: number } => {
    const squadSize = league?.squadSize || 11;
    return calculateSquadPointsUtil(
      players,
      squadSize,
      captainId || undefined,
      viceCaptainId || undefined,
      xFactorId || undefined,
      bankedPoints
    );
  };

  const handleSubmitSquad = async () => {
    if (!user || !league || !leagueId) return;

    // Show detailed validation errors if squad is not valid
    if (!isSquadValid()) {
      const validationResult = getDetailedValidationErrors();
      setEnhancedError({
        severity: 'error',
        title: 'Cannot Submit Squad',
        message: 'Your squad doesn\'t meet the requirements. Please fix the issues below:',
        squadStatus: validationResult.squadStatus,
        suggestions: validationResult.suggestions
      });
      return;
    }

    try {
      setSubmitting(true);
      setSubmitError('');
      setEnhancedError(null);

      // Tennis squad submission path
      if (league.sport === 'tennis') {
        const menSlots = league.tennisSquadRules?.menSlots ?? 5;
        const womenSlots = league.tennisSquadRules?.womenSlots ?? 5;
        const multipliers = league.tennisSquadRules?.menMultipliers ?? [2, 1.75, 1.5, 1.25, 1];
        const wMultipliers = league.tennisSquadRules?.womenMultipliers ?? [2, 1.75, 1.5, 1.25, 1];

        const tennisSquadPlayers: SquadPlayer[] = [];
        for (let i = 0; i < menSlots; i++) {
          const pid = tennismenSlots[i];
          const entry = tennisPool.find(p => p.playerId === pid);
          if (entry) {
            tennisSquadPlayers.push({
              playerId: entry.playerId,
              playerName: entry.name,
              team: entry.team,
              role: 'men',
              points: entry.points,
              matchPerformances: {},
              pointsAtJoining: entry.points,
              gender: 'men',
              slotIndex: i,
              isOverseas: entry.isOverseas,
              price: entry.price,
            });
          }
        }
        for (let i = 0; i < womenSlots; i++) {
          const pid = tenniswomenSlots[i];
          const entry = tennisPool.find(p => p.playerId === pid);
          if (entry) {
            tennisSquadPlayers.push({
              playerId: entry.playerId,
              playerName: entry.name,
              team: entry.team,
              role: 'women',
              points: entry.points,
              matchPerformances: {},
              pointsAtJoining: entry.points,
              gender: 'women',
              slotIndex: i,
              isOverseas: entry.isOverseas,
              price: entry.price,
            });
          }
        }

        // Calculate total points
        let totalPoints = 0;
        for (const sp of tennisSquadPlayers) {
          const mult = sp.gender === 'men'
            ? (multipliers[sp.slotIndex ?? 0] ?? 1)
            : (wMultipliers[sp.slotIndex ?? 0] ?? 1);
          totalPoints += (sp.points - (sp.pointsAtJoining ?? 0)) * mult;
        }

        const existingTennisSquad = await tennisSquadService.getByUserAndLeague(user.uid, leagueId);
        const tennisSquadData: any = {
          userId: user.uid,
          leagueId,
          squadName: user.displayName || 'User',
          players: tennisSquadPlayers,
          isSubmitted: true,
          totalPoints,
          captainPoints: 0,
          viceCaptainPoints: 0,
          xFactorPoints: 0,
          rank: 0,
          matchPoints: {},
          transfersUsed: 0,
          benchTransfersUsed: 0,
          flexibleTransfersUsed: 0,
          midSeasonTransfersUsed: 0,
          transferHistory: [],
          bankedPoints: 0,
          isValid: true,
          validationErrors: [],
          mensPrediction: mensPredictionId || undefined,
          womensPrediction: womensPredictionId || undefined,
        };

        if (existingTennisSquad) {
          tennisSquadData.lastUpdated = new Date();
          if (!existingTennisSquad.isSubmitted) tennisSquadData.submittedAt = new Date();
          await tennisSquadService.update(existingTennisSquad.id, tennisSquadData);
          setExistingSquad({ ...existingTennisSquad, ...tennisSquadData });
        } else {
          tennisSquadData.submittedAt = new Date();
          const squadId = await tennisSquadService.create(tennisSquadData);
          setExistingSquad({ ...tennisSquadData, id: squadId, createdAt: new Date() });
        }

        try { await leaderboardSnapshotService.create(leagueId); } catch {}
        sessionStorage.setItem(`fcl_squad_action_${leagueId}`, existingTennisSquad ? 'update' : 'submit');
        navigate(`/leagues/${leagueId}`);
        return;
      }

      // Sort players: main squad first, then bench
      // This ensures consistent order for loading
      const sortedPlayers = [
        ...selectedPlayers.filter(p => p.position !== 'bench'),
        ...selectedPlayers.filter(p => p.position === 'bench')
      ];

      // Convert selected players to SquadPlayer format using utility function
      const squadPlayers: SquadPlayer[] = sortedPlayers.map(player => {
        const squadPlayer = squadPlayerUtils.createInitialSquadPlayer({
          playerId: player.id,
          playerName: player.name,
          team: player.team,
          role: player.role,
          points: player.stats[league.format as 'T20' | 'ODI' | 'Test'].recentForm || 0, // Use current points from stats
        });

        // Set pointsWhenRoleAssigned for C/VC/X at squad creation
        if (player.id === captainId || player.id === viceCaptainId || player.id === xFactorId) {
          squadPlayer.pointsWhenRoleAssigned = squadPlayer.points;
        }

        return squadPlayer;
      });

      // Calculate total points based on current player points and multipliers
      const calculatedPoints = calculateSquadPoints(squadPlayers, captainId, viceCaptainId, xFactorId);

      // Check if squad already exists
      const existingSquad = await squadService.getByUserAndLeague(user.uid, leagueId);

      if (existingSquad) {
        // Update existing squad
        const updateData: any = {
          players: squadPlayers,
          ...(captainId && { captainId }),
          ...(viceCaptainId && { viceCaptainId }),
          ...(xFactorId && { xFactorId }),
          totalPoints: calculatedPoints.totalPoints,
          captainPoints: calculatedPoints.captainPoints,
          viceCaptainPoints: calculatedPoints.viceCaptainPoints,
          xFactorPoints: calculatedPoints.xFactorPoints,
          ...(powerplayMatch && { powerplayMatchNumber: parseInt(powerplayMatch) }),
          ...(ppActivatedAt && { ppActivatedAt }),
          ...(hiddenPlayerId && league?.hiddenPlayerEnabled && (() => {
            const hp = availablePlayers.find(p => p.id === hiddenPlayerId);
            return hp ? {
              hiddenPlayerId,
              hiddenPlayerName: hp.name,
              hiddenPlayerRole: hp.role as 'batsman' | 'bowler' | 'allrounder' | 'wicketkeeper',
              hiddenPlayerTeam: hp.team,
              hiddenPlayerPoints: existingSquad.hiddenPlayerPoints || 0,
            } : {};
          })()),
          predictions: {
            topRunScorer: topRunScorer.trim(),
            topWicketTaker: topWicketTaker.trim(),
            winningTeam: winningTeam.trim(),
          },
          isSubmitted: true,
          lastUpdated: new Date(),
        };

        // Only set submittedAt if not already submitted
        if (!existingSquad.isSubmitted) {
          updateData.submittedAt = new Date();
        }

        await squadService.update(existingSquad.id, updateData);

        // Update local state to reflect the changes
        setExistingSquad({
          ...existingSquad,
          ...updateData
        });
      } else {
        // Create new squad
        const squadData: any = {
          userId: user.uid,
          leagueId: leagueId,
          squadName: user.displayName || 'User',
          players: squadPlayers,
          isSubmitted: true,
          submittedAt: new Date(),
          totalPoints: calculatedPoints.totalPoints,
          captainPoints: calculatedPoints.captainPoints,
          viceCaptainPoints: calculatedPoints.viceCaptainPoints,
          xFactorPoints: calculatedPoints.xFactorPoints,
          ...(powerplayMatch && { powerplayMatchNumber: parseInt(powerplayMatch) }),
          ...(ppActivatedAt && { ppActivatedAt }),
          ...(hiddenPlayerId && league?.hiddenPlayerEnabled && (() => {
            const hp = availablePlayers.find(p => p.id === hiddenPlayerId);
            return hp ? {
              hiddenPlayerId,
              hiddenPlayerName: hp.name,
              hiddenPlayerRole: hp.role as 'batsman' | 'bowler' | 'allrounder' | 'wicketkeeper',
              hiddenPlayerTeam: hp.team,
              hiddenPlayerPoints: 0,
            } : {};
          })()),
          predictions: {
            topRunScorer: topRunScorer.trim(),
            topWicketTaker: topWicketTaker.trim(),
            winningTeam: winningTeam.trim(),
          },
          rank: 0,
          matchPoints: {},
          transfersUsed: 0,
          benchTransfersUsed: 0,
          flexibleTransfersUsed: 0,
          midSeasonTransfersUsed: 0,
          wildcardTransfersUsed: 0,
          transferHistory: [],
          bankedPoints: 0, // Initialize banked points to 0
          isValid: true,
          validationErrors: [],
          ...(captainId && { captainId }),
          ...(viceCaptainId && { viceCaptainId }),
          ...(xFactorId && { xFactorId }),
        };

        const squadId = await squadService.create(squadData);

        // Update local state with the newly created squad
        setExistingSquad({
          ...squadData,
          id: squadId,
          createdAt: new Date()
        });
      }

      // Create/update leaderboard snapshot for this league
      try {
        await leaderboardSnapshotService.create(leagueId);
      } catch (snapshotError) {
        console.error('Error creating leaderboard snapshot:', snapshotError);
        // Don't fail the submission if snapshot creation fails
      }

      // Navigate to league dashboard
      sessionStorage.setItem(`fcl_squad_action_${leagueId}`, existingSquad ? 'update' : 'submit');
      navigate(`/leagues/${leagueId}`);
    } catch (error: any) {
      console.error('Error submitting squad:', error);

      // Check if it's a network/connection error
      const isNetworkError = error.code === 'unavailable' || error.message?.toLowerCase().includes('network') || error.message?.toLowerCase().includes('connection');

      setEnhancedError({
        severity: 'error',
        title: isNetworkError ? 'Connection Error' : 'Failed to Submit Squad',
        message: isNetworkError
          ? 'We couldn\'t save your squad due to a network issue.'
          : 'An error occurred while submitting your squad.',
        suggestions: isNetworkError
          ? [
              'Check your internet connection',
              'Try again in a moment',
              'Your squad data is safe - just resubmit when ready'
            ]
          : [
              'Try submitting again',
              'If the problem persists, contact the league admin',
              'Your progress has been saved as a draft'
            ],
        actions: [
          {
            label: 'Try Again',
            onClick: () => {
              setEnhancedError(null);
              handleSubmitSquad();
            },
            variant: 'contained',
            color: 'primary'
          },
          {
            label: 'Save Draft Instead',
            onClick: () => {
              setEnhancedError(null);
              handleSaveDraft();
            },
            variant: 'outlined'
          }
        ]
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSavePredictionsAndHiddenPlayer = async () => {
    if (!user || !league || !leagueId || !existingSquad) return;
    if (isDeadlinePassed) return;

    try {
      setSubmitting(true);
      setSubmitError('');

      const updates: Partial<LeagueSquad> = {
        predictions: {
          topRunScorer: topRunScorer.trim(),
          topWicketTaker: topWicketTaker.trim(),
          winningTeam: winningTeam.trim(),
        },
        lastUpdated: new Date(),
      };

      if (league.hiddenPlayerEnabled && hiddenPlayerId) {
        const hp = availablePlayers.find(p => p.id === hiddenPlayerId);
        if (hp) {
          updates.hiddenPlayerId = hiddenPlayerId;
          updates.hiddenPlayerName = hp.name;
          updates.hiddenPlayerRole = hp.role as 'batsman' | 'bowler' | 'allrounder' | 'wicketkeeper';
          updates.hiddenPlayerTeam = hp.team;
          updates.hiddenPlayerPoints = existingSquad.hiddenPlayerPoints || 0;
        }
      }

      await squadService.update(existingSquad.id, updates);
      setExistingSquad({ ...existingSquad, ...updates });
    } catch (error: any) {
      setSubmitError(error.message || 'Failed to save predictions');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!user || !league || !leagueId) return;

    // Require at least one player to save draft
    if (league.sport === 'tennis') {
      const anyFilled = tennismenSlots.some(Boolean) || tenniswomenSlots.some(Boolean);
      if (!anyFilled) {
        setSubmitError('Please add at least one player before saving draft');
        return;
      }
    } else {
      if (selectedPlayers.length === 0) {
        setSubmitError('Please select at least one player before saving draft');
        return;
      }
    }

    try {
      setSubmitting(true);
      setSubmitError('');

      // Tennis leagues: save to tennisSquads collection
      if (league.sport === 'tennis') {
        const menSlots   = league.tennisSquadRules?.menSlots   ?? 5;
        const womenSlots = league.tennisSquadRules?.womenSlots ?? 5;
        const multipliers  = league.tennisSquadRules?.menMultipliers   ?? [2, 1.75, 1.5, 1.25, 1];
        const wMultipliers = league.tennisSquadRules?.womenMultipliers ?? [2, 1.75, 1.5, 1.25, 1];

        const tennisPlayers: SquadPlayer[] = [];
        for (let i = 0; i < menSlots; i++) {
          const pid = tennismenSlots[i];
          const entry = tennisPool.find(p => p.playerId === pid);
          if (entry) tennisPlayers.push({
            playerId: entry.playerId, playerName: entry.name, team: entry.team,
            role: 'men', points: entry.points, matchPerformances: {},
            pointsAtJoining: entry.points, gender: 'men', slotIndex: i, isOverseas: entry.isOverseas,
          });
        }
        for (let i = 0; i < womenSlots; i++) {
          const pid = tenniswomenSlots[i];
          const entry = tennisPool.find(p => p.playerId === pid);
          if (entry) tennisPlayers.push({
            playerId: entry.playerId, playerName: entry.name, team: entry.team,
            role: 'women', points: entry.points, matchPerformances: {},
            pointsAtJoining: entry.points, gender: 'women', slotIndex: i, isOverseas: entry.isOverseas,
          });
        }

        let totalPoints = 0;
        for (const sp of tennisPlayers) {
          const mult = sp.gender === 'men'
            ? (multipliers[sp.slotIndex ?? 0] ?? 1)
            : (wMultipliers[sp.slotIndex ?? 0] ?? 1);
          totalPoints += (sp.points - (sp.pointsAtJoining ?? 0)) * mult;
        }

        const existing = await tennisSquadService.getByUserAndLeague(user.uid, leagueId);
        const draftData: any = {
          userId: user.uid, leagueId,
          squadName: user.displayName || 'User',
          players: tennisPlayers,
          isSubmitted: false,
          totalPoints, captainPoints: 0, viceCaptainPoints: 0, xFactorPoints: 0,
          rank: 0, matchPoints: {}, transfersUsed: 0,
          benchTransfersUsed: 0, flexibleTransfersUsed: 0,
          midSeasonTransfersUsed: 0, wildcardTransfersUsed: 0,
          transferHistory: [], bankedPoints: 0,
          isValid: false, validationErrors: [],
          mensPrediction: mensPredictionId || undefined,
          womensPrediction: womensPredictionId || undefined,
          lastUpdated: new Date(),
        };

        if (existing) {
          await tennisSquadService.update(existing.id, draftData);
          setExistingSquad({ ...existing, ...draftData });
        } else {
          const id = await tennisSquadService.create(draftData);
          setExistingSquad({ ...draftData, id, createdAt: new Date() });
        }

        setSubmitError('');
        setDraftSavedOpen(true);
        return;
      }

      // Sort players: main squad first, then bench
      const sortedPlayers = [
        ...selectedPlayers.filter(p => p.position !== 'bench'),
        ...selectedPlayers.filter(p => p.position === 'bench')
      ];

      // Convert selected players to SquadPlayer format
      const squadPlayers: SquadPlayer[] = sortedPlayers.map(player => {
        const squadPlayer = squadPlayerUtils.createInitialSquadPlayer({
          playerId: player.id,
          playerName: player.name,
          team: player.team,
          role: player.role,
          points: player.stats[league.format as 'T20' | 'ODI' | 'Test'].recentForm || 0,
        });

        // Set pointsWhenRoleAssigned for C/VC/X if assigned
        if (player.id === captainId || player.id === viceCaptainId || player.id === xFactorId) {
          squadPlayer.pointsWhenRoleAssigned = squadPlayer.points;
        }

        return squadPlayer;
      });

      // Calculate points (even if squad is incomplete)
      const calculatedPoints = calculateSquadPoints(squadPlayers, captainId, viceCaptainId, xFactorId);

      // Check if squad already exists
      const existingSquad = await squadService.getByUserAndLeague(user.uid, leagueId);

      if (existingSquad) {
        // Update existing draft
        await squadService.update(existingSquad.id, {
          players: squadPlayers,
          ...(captainId && { captainId }),
          ...(viceCaptainId && { viceCaptainId }),
          ...(xFactorId && { xFactorId }),
          totalPoints: calculatedPoints.totalPoints,
          captainPoints: calculatedPoints.captainPoints,
          viceCaptainPoints: calculatedPoints.viceCaptainPoints,
          xFactorPoints: calculatedPoints.xFactorPoints,
          ...(powerplayMatch && { powerplayMatchNumber: parseInt(powerplayMatch) }),
          ...(ppActivatedAt && { ppActivatedAt }),
          ...(hiddenPlayerId && league?.hiddenPlayerEnabled && (() => {
            const hp = availablePlayers.find(p => p.id === hiddenPlayerId);
            return hp ? {
              hiddenPlayerId,
              hiddenPlayerName: hp.name,
              hiddenPlayerRole: hp.role as 'batsman' | 'bowler' | 'allrounder' | 'wicketkeeper',
              hiddenPlayerTeam: hp.team,
              hiddenPlayerPoints: existingSquad.hiddenPlayerPoints || 0,
            } : {};
          })()),
          predictions: {
            topRunScorer: topRunScorer.trim(),
            topWicketTaker: topWicketTaker.trim(),
            winningTeam: winningTeam.trim(),
          },
          isSubmitted: false, // Keep as draft
          lastUpdated: new Date(),
        });

        setExistingSquad({
          ...existingSquad,
          players: squadPlayers,
          captainId: captainId || undefined,
          viceCaptainId: viceCaptainId || undefined,
          xFactorId: xFactorId || undefined,
          totalPoints: calculatedPoints.totalPoints,
          isSubmitted: false,
        });

        setSubmitError(''); // Clear any errors
        setDraftSavedOpen(true);
      } else {
        // Create new draft
        const squadData: any = {
          userId: user.uid,
          leagueId: leagueId,
          squadName: user.displayName || 'User',
          players: squadPlayers,
          isSubmitted: false, // Save as draft
          totalPoints: calculatedPoints.totalPoints,
          captainPoints: calculatedPoints.captainPoints,
          viceCaptainPoints: calculatedPoints.viceCaptainPoints,
          xFactorPoints: calculatedPoints.xFactorPoints,
          ...(powerplayMatch && { powerplayMatchNumber: parseInt(powerplayMatch) }),
          ...(ppActivatedAt && { ppActivatedAt }),
          ...(hiddenPlayerId && league?.hiddenPlayerEnabled && (() => {
            const hp = availablePlayers.find(p => p.id === hiddenPlayerId);
            return hp ? {
              hiddenPlayerId,
              hiddenPlayerName: hp.name,
              hiddenPlayerRole: hp.role as 'batsman' | 'bowler' | 'allrounder' | 'wicketkeeper',
              hiddenPlayerTeam: hp.team,
              hiddenPlayerPoints: 0,
            } : {};
          })()),
          predictions: {
            topRunScorer: topRunScorer.trim(),
            topWicketTaker: topWicketTaker.trim(),
            winningTeam: winningTeam.trim(),
          },
          rank: 0,
          matchPoints: {},
          transfersUsed: 0,
          benchTransfersUsed: 0,
          flexibleTransfersUsed: 0,
          midSeasonTransfersUsed: 0,
          wildcardTransfersUsed: 0,
          transferHistory: [],
          bankedPoints: 0,
          isValid: false, // Draft may not be valid yet
          validationErrors: [],
          ...(captainId && { captainId }),
          ...(viceCaptainId && { viceCaptainId }),
          ...(xFactorId && { xFactorId }),
        };

        const squadId = await squadService.create(squadData);

        setExistingSquad({
          ...squadData,
          id: squadId,
          createdAt: new Date()
        });

        setSubmitError(''); // Clear any errors
        setDraftSavedOpen(true);
      }
    } catch (error: any) {
      console.error('Error saving draft:', error);
      setSubmitError(error.message || 'Failed to save draft');
    } finally {
      setSubmitting(false);
    }
  };

  // Use shared utility for calculating player contribution
  // This ensures consistency across transfers and pool updates
  const calculatePlayerContribution = calculatePlayerContributionUtil;

  const handleTennisTransferComplete = async (updatedSquad: LeagueSquad) => {
    setExistingSquad(updatedSquad);
    const menSlots  = league?.tennisSquadRules?.menSlots  ?? 5;
    const womenSlots = league?.tennisSquadRules?.womenSlots ?? 5;
    const menArr:   (string | null)[] = Array(menSlots).fill(null);
    const womenArr: (string | null)[] = Array(womenSlots).fill(null);
    for (const sp of updatedSquad.players) {
      if (sp.gender === 'men'   && sp.slotIndex !== undefined) menArr[sp.slotIndex]   = sp.playerId;
      if (sp.gender === 'women' && sp.slotIndex !== undefined) womenArr[sp.slotIndex] = sp.playerId;
    }
    setTennismenSlots(menArr);
    setTenniswomenSlots(womenArr);
    try { await leaderboardSnapshotService.create(leagueId!); } catch {}
  };

  const handleTransferSubmit = async (transferData: TransferData) => {
    if (!user || !league || !leagueId || !existingSquad) return;

    try {
      // All transfer computation (banking, role swaps, auto-slotting, squad-rule
      // validation, point-stability guards PT-001/PT-002 and the history entry)
      // lives in the shared util so the admin-on-behalf panel runs the EXACT
      // same logic. See utils/cricketTransfer.computeCricketTransferUpdate.
      const { updatePayload } = computeCricketTransferUpdate({
        existingSquad,
        transferData,
        availablePlayers,
        league,
      });
      await squadService.update(existingSquad.id, updatePayload);

      // Reload the squad
      const updatedSquad = await squadService.getByUserAndLeague(user.uid, leagueId);
      setExistingSquad(updatedSquad);

      // Update leaderboard snapshot
      try {
        await leaderboardSnapshotService.create(leagueId);
      } catch (snapshotError) {
        console.error('Error creating leaderboard snapshot:', snapshotError);
      }

      // Show success message
      setSubmitError('');
      setEnhancedError(null);
    } catch (error: any) {
      console.error('Error submitting transfer:', error);

      // Check if it's a critical system error
      const errorMessage = error.message || '';
      const isCriticalError = errorMessage.startsWith('SYSTEM_ERROR:');

      if (isCriticalError) {
        // Parse the error: SYSTEM_ERROR:ERROR_TYPE:User message
        const parts = errorMessage.split(':');
        const errorCode = parts[1] || 'UNKNOWN';
        const userMessage = parts.slice(2).join(':') || 'A system error occurred. Please contact the admin.';

        setEnhancedError({
          severity: 'error',
          title: 'System Error Detected',
          message: userMessage,
          suggestions: [
            'Your squad has not been changed',
            'This error has been logged for the admin',
            'Try refreshing the page'
          ],
          errorCode,
          isCriticalError: true
        });
        return; // Don't re-throw, we've handled it
      }

      // For other errors, re-throw to let the modal handle it
      throw error;
    }
  };

  const addPlayerToSquad = (player: Player, targetPosition: 'regular' | 'bench') => {
    if (!league) return;

    const benchRequired = league.transferTypes?.benchTransfers?.enabled ? league.transferTypes.benchTransfers.benchSlots : 0;

    // Use functional setState to ensure we check against the LATEST state
    setSelectedPlayers(prev => {
      // Check if player is already selected (using latest state) - CRITICAL for preventing duplicates/stacking
      const existingPlayer = prev.find(p => p.id === player.id);
      if (existingPlayer) {
        console.warn(`Player ${player.name} is already in the squad at position ${existingPlayer.position}`);
        setSubmitError(`${player.name} is already selected`);
        setTimeout(() => setSubmitError(''), 3000);
        return prev; // Don't add duplicate
      }

      // Check if player is the hidden player
      if (hiddenPlayerId && player.id === hiddenPlayerId) {
        setHiddenConflictAlert(`⚠️ ${player.name} is your 12th Hidden Player! Remove them as hidden player first, or choose a different player.`);
        setTimeout(() => setHiddenConflictAlert(''), 5000);
        return prev;
      }

      // Calculate current counts from LATEST state
      const currentMainSquad = prev.filter(p => p.position !== 'bench').length;
      const currentBench = prev.filter(p => p.position === 'bench').length;

      // Validate squad limits with latest state
      if (targetPosition === 'bench') {
        if (currentBench >= benchRequired) {
          setSubmitError(`Maximum ${benchRequired} bench players allowed`);
          setTimeout(() => setSubmitError(''), 3000);
          return prev; // Don't add, return unchanged state
        }
      } else {
        if (currentMainSquad >= league.squadSize) {
          setSubmitError(`Maximum ${league.squadSize} main squad players allowed (excluding bench)`);
          setTimeout(() => setSubmitError(''), 3000);
          return prev; // Don't add, return unchanged state
        }

        // Check if there's an available slot for this specific role
        // This prevents the issue where players stack in the same slot
        const mainSquadPlayers = prev.filter(p => p.position !== 'bench');

        // Count players of each role
        const roleCounts = {
          batsman: mainSquadPlayers.filter(p => p.role === 'batsman').length,
          bowler: mainSquadPlayers.filter(p => p.role === 'bowler').length,
          allrounder: mainSquadPlayers.filter(p => p.role === 'allrounder').length,
          wicketkeeper: mainSquadPlayers.filter(p => p.role === 'wicketkeeper').length
        };

        const role = player.role as 'batsman' | 'bowler' | 'allrounder' | 'wicketkeeper';
        const minRequired = league.squadRules[
          role === 'batsman' ? 'minBatsmen' :
          role === 'bowler' ? 'minBowlers' :
          role === 'allrounder' ? 'minAllrounders' :
          'minWicketkeepers'
        ];

        // Check if there's space in required slots for this role
        const hasRequiredSlot = roleCounts[role] < minRequired;

        // Calculate how many flexible slots are available
        const totalRequiredSlots = league.squadRules.minBatsmen + league.squadRules.minBowlers +
                                   league.squadRules.minAllrounders + league.squadRules.minWicketkeepers;
        const totalFlexibleSlots = league.squadSize - totalRequiredSlots;

        // Count how many players are currently occupying flexible slots
        const playersInFlexible =
          Math.max(0, roleCounts.batsman - league.squadRules.minBatsmen) +
          Math.max(0, roleCounts.bowler - league.squadRules.minBowlers) +
          Math.max(0, roleCounts.allrounder - league.squadRules.minAllrounders) +
          Math.max(0, roleCounts.wicketkeeper - league.squadRules.minWicketkeepers);

        const hasFlexibleSlot = playersInFlexible < totalFlexibleSlots;

        // Player must have either a required slot OR a flexible slot available
        if (!hasRequiredSlot && !hasFlexibleSlot) {
          const roleDisplayName = role === 'batsman' ? 'batter' : role;
          setSubmitError(`No available slot for this ${roleDisplayName}. Fill required positions first or free up flexible slots.`);
          setTimeout(() => setSubmitError(''), 3000);
          return prev;
        }
      }

      // All validations passed, add the player
      const newPlayer: SelectedPlayer = { ...player, isOverseas: player.isOverseas ?? false, position: targetPosition };
      return [...prev, newPlayer];
    });
  };

  const removePlayerFromSquad = (playerId: string) => {
    // Clear special roles if this player had any
    if (captainId === playerId) setCaptainId(null);
    if (viceCaptainId === playerId) setViceCaptainId(null);
    if (xFactorId === playerId) setXFactorId(null);

    setSelectedPlayers(prev => prev.filter(p => p.id !== playerId));
  };

  const setPlayerAsSpecialRole = (playerId: string, role: 'captain' | 'vice_captain' | 'x_factor' | null) => {
    // Can only set special roles for main squad players (not bench)
    const player = selectedPlayers.find(p => p.id === playerId);
    if (!player || player.position === 'bench') return;

    // If this player already has a different role, remove it first
    if (captainId === playerId && role !== 'captain') {
      setCaptainId(null);
    }
    if (viceCaptainId === playerId && role !== 'vice_captain') {
      setViceCaptainId(null);
    }
    if (xFactorId === playerId && role !== 'x_factor') {
      setXFactorId(null);
    }

    // Toggle off if clicking the same player with the same role
    // Otherwise, set the new role
    if (role === 'captain') {
      setCaptainId(captainId === playerId ? null : playerId);
    } else if (role === 'vice_captain') {
      setViceCaptainId(viceCaptainId === playerId ? null : playerId);
    } else if (role === 'x_factor') {
      setXFactorId(xFactorId === playerId ? null : playerId);
    }
  };

  const updatePlayerPosition = (playerId: string, newPosition: 'regular' | 'bench') => {
    // If moving to bench, clear any special roles
    if (newPosition === 'bench') {
      if (captainId === playerId) setCaptainId(null);
      if (viceCaptainId === playerId) setViceCaptainId(null);
      if (xFactorId === playerId) setXFactorId(null);
    }

    setSelectedPlayers(prev => prev.map(p =>
      p.id === playerId ? { ...p, position: newPosition } : p
    ));
  };

  const getSubmitButtonText = () => {
    if (submitting) return 'Saving...';
    if (isDeadlinePassed) return 'Deadline Passed';
    if (existingSquad?.isSubmitted) return 'Update Squad';
    return 'Submit Squad';
  };

  const StatusBanner = ({ severity, children, onDismiss }: { severity: 'warning' | 'info' | 'success' | 'error'; children: React.ReactNode; onDismiss?: () => void }) => {
    const cfg = {
      warning: { color: '#FF9800', icon: <WarningAmberRounded /> },
      info:    { color: '#2196F3', icon: <InfoOutlined /> },
      success: { color: '#4CAF50', icon: <CheckCircleOutline /> },
      error:   { color: '#F44336', icon: <ErrorOutline /> },
    }[severity];
    return (
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1,
        px: 1.5, py: 0.875,
        borderRadius: 1.5,
        bgcolor: alpha(cfg.color, 0.05),
        border: `1px solid ${alpha(cfg.color, 0.15)}`,
        mb: { xs: 1, sm: 1.5 },
      }}>
        {React.cloneElement(cfg.icon, { sx: { color: alpha(cfg.color, 0.7), fontSize: 15, flexShrink: 0 } })}
        <Typography variant="body2" sx={{ color: alpha('#fff', 0.5), fontSize: { xs: '0.72rem', sm: '0.775rem' }, lineHeight: 1.4, flex: 1 }}>
          {children}
        </Typography>
        {onDismiss && (
          <IconButton size="small" onClick={onDismiss} sx={{ color: alpha(cfg.color, 0.35), p: 0.25, flexShrink: 0, ml: 0.5, '&:hover': { color: alpha(cfg.color, 0.7) } }}>
            <Close sx={{ fontSize: 14 }} />
          </IconButton>
        )}
      </Box>
    );
  };

  const tennisMaxT = Number(league?.tennisSquadRules?.maxTransfers ?? 4);
  const tennisTransfersLeft = (league?.sport === 'tennis' && tennisMaxT > 0)
    ? Math.max(0, tennisMaxT - Number(existingSquad?.transfersUsed ?? 0))
    : null;

  const cricketTransfersLeft = (() => {
    if (!league || league.sport === 'tennis' || !league.transferTypes || !existingSquad) return null;
    const t = league.transferTypes;
    const sq = existingSquad;
    let total = 0;
    if (t.benchTransfers.enabled && league.benchChangesEnabled) total += Math.max(0, t.benchTransfers.maxAllowed - (sq.benchTransfersUsed || 0));
    if (t.flexibleTransfers.enabled && league.flexibleChangesEnabled) total += Math.max(0, t.flexibleTransfers.maxAllowed - (sq.flexibleTransfersUsed || 0));
    if (t.wildcardTransfers?.enabled && league.wildcardChangesEnabled) total += Math.max(0, t.wildcardTransfers.maxAllowed - (sq.wildcardTransfersUsed || 0));
    if (t.midSeasonTransfers.enabled) total += Math.max(0, t.midSeasonTransfers.maxAllowed - (sq.midSeasonTransfersUsed || 0));
    return total;
  })();

  const quickActions = (
    <Box display="flex" gap={{ xs: 1, sm: 2 }} alignItems="center" flexWrap="wrap">
      {/* ⚡ PP quick-activate button — compact, beside Make Transfer */}
      {isDeadlinePassed && league?.powerplayEnabled && (league.ppMatchMode ?? 'fixed') === 'activation' && !ppActivatedAt && !existingSquad?.ppActivatedAt && (
        <Button
          variant="contained"
          size="small"
          disabled={!(league?.ppActivationEnabled)}
          onClick={handleOpenPpDialog}
          sx={{
            minWidth: 0,
            px: 1.5,
            py: { xs: 0.5, sm: 0.75 },
            fontWeight: 700,
            fontSize: '0.85rem',
            color: '#000',
            background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
            boxShadow: '0 2px 8px rgba(245,158,11,0.4)',
            borderRadius: 2,
            '&:hover': { background: 'linear-gradient(135deg, #FBBF24 0%, #F59E0B 100%)', boxShadow: '0 4px 14px rgba(245,158,11,0.5)', transform: 'translateY(-1px)' },
            '&.Mui-disabled': {
              background: alpha('#F59E0B', 0.18),
              color: alpha('#F59E0B', 0.4),
              boxShadow: 'none',
            },
          }}
        >
          <BoltIcon fontSize="small" sx={{ mr: 0.25, color: '#000' }} />PP
        </Button>
      )}
      {isDeadlinePassed && existingSquad ? (
        <Button
          variant="contained"
          startIcon={<SwapHoriz />}
          onClick={() => league?.sport === 'tennis' ? setTennisTransferOpen(true) : setTransferModalOpen(true)}
          disabled={!canMakeTransfer || (league?.sport === 'tennis' && tennisTransfersLeft === 0)}
          sx={{
            minWidth: 0,
            px: 1.5,
            py: { xs: 0.5, sm: 0.75 },
            fontWeight: 700,
            fontSize: '0.85rem',
            color: '#000',
            background: `linear-gradient(135deg, ${themeColors.blue.light} 0%, ${themeColors.blue.electric} 100%)`,
            boxShadow: `0 2px 8px ${alpha(themeColors.blue.electric, 0.4)}`,
            borderRadius: 2,
            '&:hover': { background: `linear-gradient(135deg, #64B5F6 0%, ${themeColors.blue.light} 100%)`, boxShadow: `0 4px 14px ${alpha(themeColors.blue.electric, 0.5)}`, transform: 'translateY(-1px)' },
            '&.Mui-disabled': { background: alpha(themeColors.blue.electric, 0.18), color: alpha('#000', 0.3), boxShadow: 'none' },
          }}
        >
          {league?.sport === 'tennis' && tennisTransfersLeft !== null
            ? `Transfers (${tennisTransfersLeft} left)`
            : cricketTransfersLeft !== null
              ? `Transfers (${cricketTransfersLeft} left)`
              : 'Transfers'}
        </Button>
      ) : (
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            disabled={selectedPlayers.length === 0 || submitting || isDeadlinePassed}
            onClick={handleSaveDraft}
            sx={{
              fontFamily: "'Satoshi', sans-serif",
              fontWeight: 500,
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              px: { xs: 1.5, sm: 2 },
              py: { xs: 0.5, sm: 1 },
              borderColor: themeColors.orange.primary,
              color: themeColors.orange.primary,
              '&:hover': {
                borderColor: themeColors.orange.dark,
                bgcolor: alpha(themeColors.orange.primary, 0.08)
              }
            }}
          >
            Save Draft
          </Button>
          <Button
            variant="contained"
            disabled={!isSquadValid() || submitting || isDeadlinePassed}
            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <Star />}
            onClick={() => { vibrate([10, 50, 10]); handleSubmitSquad(); }}
            sx={{
              fontFamily: "'Satoshi', sans-serif",
              fontWeight: 600,
              letterSpacing: '0.03em',
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              px: { xs: 1.5, sm: 2 },
              py: { xs: 0.5, sm: 1 },
              bgcolor: isDeadlinePassed ? themeColors.grey[700] : themeColors.blue.electric,
              color: 'white',
              '&:hover': {
                bgcolor: isDeadlinePassed ? themeColors.grey[600] : themeColors.blue.deep
              },
              '&:not(:disabled):hover': {
                boxShadow: `0 4px 20px ${alpha(themeColors.blue.electric, 0.5)}`,
              },
            }}
          >
            {getSubmitButtonText()}
          </Button>
        </Box>
      )}
    </Box>
  );

  if (loading || !league) {
    return (
      <Box>
        <AppHeader />
        {leagueId && (
          <LeagueNav
            leagueName="Loading..."
            leagueId={leagueId}
            currentPage="Squad Selection"
          />
        )}
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
          <CircularProgress size={60} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppHeader />
      {league && leagueId && (
        <LeagueNav
          leagueName={league.name}
          leagueId={leagueId}
          currentPage="Squad Selection"
          actions={quickActions}
        />
      )}

      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 }, pb: { xs: '88px', md: 2 } }}>
        {/* Status Alerts */}
        {isDeadlinePassed && !noticeDismissed && (
          canMakeTransfer ? (
            <StatusBanner severity="info" onDismiss={handleDismissNotice}>
              Squad Deadline Passed. Your squad is locked. You can only make changes using available transfers.
            </StatusBanner>
          ) : (
            <StatusBanner severity="warning" onDismiss={handleDismissNotice}>
              Transfers are currently disabled.
              {!isLeagueStarted && ' The league has not started yet.'}
              {isLeagueStarted && !league?.flexibleChangesEnabled && !league?.benchChangesEnabled && ' No transfer types are enabled by the league admin.'}
              {isLeagueStarted && (league?.flexibleChangesEnabled || league?.benchChangesEnabled) && league?.transferTypes?.midSeasonTransfers.enabled && ' Mid-season transfer window is closed.'}
            </StatusBanner>
          )
        )}

        {!existingSquad?.isSubmitted && !isDeadlinePassed && (
          <StatusBanner severity="warning">
            Squad Not Submitted. Please submit your squad before the deadline: {new Date(league.squadDeadline).toLocaleString()}
          </StatusBanner>
        )}

        {/* Enhanced Error Alert */}
        {enhancedError && (
          <Box sx={{ mb: { xs: 2, sm: 3 } }}>
            <EnhancedAlert
              open={true}
              onClose={() => setEnhancedError(null)}
              severity={enhancedError.severity}
              title={enhancedError.title}
              message={enhancedError.message}
              squadStatus={enhancedError.squadStatus}
              suggestions={enhancedError.suggestions}
              actions={enhancedError.actions}
              errorCode={enhancedError.errorCode}
              isCriticalError={enhancedError.isCriticalError}
            />
          </Box>
        )}

        {/* Simple Error Alert (backward compatibility) */}
        {submitError && !enhancedError && (
          <StatusBanner severity="error">
            {submitError}
          </StatusBanner>
        )}

        {/* Tennis Squad UI */}
        {league?.sport === 'tennis' && (
          <TennisSquadUI
            league={league}
            tennisPool={tennisPool}
            tennismenSlots={tennismenSlots}
            tenniswomenSlots={tenniswomenSlots}
            onSetMenSlot={(i, pid) => setTennismenSlots(prev => { const a = [...prev]; a[i] = pid; return a; })}
            onSetWomenSlot={(i, pid) => setTenniswomenSlots(prev => { const a = [...prev]; a[i] = pid; return a; })}
            mensPredictionId={mensPredictionId}
            womensPredictionId={womensPredictionId}
            onMensPrediction={setMensPredictionId}
            onWomensPrediction={setWomensPredictionId}
            isDeadlinePassed={isDeadlinePassed}
            isValid={isTennisSquadValid()}
            hasExistingSquad={!!existingSquad}
            submitting={submitting}
            onSubmit={() => { vibrate([10, 50, 10]); handleSubmitSquad(); }}
          />
        )}

        {/* Tennis Transfer Modal */}
        {league?.sport === 'tennis' && existingSquad && (
          <TennisTransferModal
            open={tennisTransferOpen}
            onClose={() => setTennisTransferOpen(false)}
            league={league}
            existingSquad={existingSquad}
            tennisPool={tennisPool}
            onTransferComplete={async (squad) => {
              setTennisTransferOpen(false);
              await handleTennisTransferComplete(squad);
            }}
          />
        )}

        {/* Cricket Squad Summary - Premium Layout */}
        {league?.sport !== 'tennis' && (() => {
          const regularCount = selectedPlayers.filter(p => p.position !== 'bench').length;
          const squadMax = league?.squadSize || 0;
          const isPlayersValid = regularCount === squadMax;
          const overseasCount = selectedPlayers.filter(p => p.position !== 'bench' && p.isOverseas).length;
          const overseasMax = league?.squadRules?.maxOverseasPlayers || 4;
          const isOverseasOver = overseasCount > overseasMax;
          const hasBench = league?.transferTypes?.benchTransfers?.enabled;
          const elBlue = themeColors.blue.electric;

          return (
            <Card sx={{
              mb: { xs: 2, sm: 3 },
              background: `linear-gradient(145deg, ${alpha('#0D2137', 0.95)}, ${alpha('#060D17', 0.98)})`,
              backdropFilter: 'blur(24px)',
              border: `1px solid ${alpha(elBlue, 0.18)}`,
              borderRadius: 3,
              boxShadow: `0 8px 32px ${alpha('#000', 0.4)}, 0 0 0 1px ${alpha(elBlue, 0.06)}`,
              overflow: 'hidden',
              '&::before': {
                content: '""',
                display: 'block',
                height: '2px',
                background: `linear-gradient(90deg, ${elBlue}, ${alpha(elBlue, 0)})`,
              },
            }}>
              <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                {/* Header — always visible, clickable to expand/collapse */}
                <Box
                  onClick={() => setSquadSummaryOpen((o: boolean) => !o)}
                  sx={{ display: 'flex', flexDirection: 'column', mb: squadSummaryOpen ? 2 : 0, cursor: 'pointer', userSelect: 'none', gap: !squadSummaryOpen ? 0.75 : 0 }}
                >
                  {/* Row 1: compact status line + submitted chip + arrow */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {!squadSummaryOpen && (() => {
                        const benchN = league?.transferTypes?.benchTransfers?.enabled
                          ? (league.transferTypes!.benchTransfers!.maxAllowed) - (existingSquad?.benchTransfersUsed ?? 0) : 0;
                        const flexN = league?.transferTypes?.flexibleTransfers?.enabled
                          ? (league.transferTypes!.flexibleTransfers!.maxAllowed) - (existingSquad?.flexibleTransfersUsed ?? 0) : 0;
                        const wildcardN = league?.transferTypes?.wildcardTransfers?.enabled
                          ? (league.transferTypes!.wildcardTransfers!.maxAllowed) - (existingSquad?.wildcardTransfersUsed ?? 0) : 0;
                        return (
                          <Typography component="div" sx={{
                            fontSize: '0.8rem', fontWeight: 600, lineHeight: 1,
                            color: isPlayersValid ? alpha('#fff', 0.5) : 'error.main',
                            letterSpacing: '0.01em',
                            display: 'flex', alignItems: 'center', flexWrap: 'wrap',
                          }}>
                            <span>{regularCount}/{squadMax}{isPlayersValid ? ' ✓' : ''}</span>
                            {league?.squadRules?.overseasPlayersEnabled && <><Box component="span" sx={{ mx: '3px', opacity: 0.3 }}>•</Box><span>{overseasCount} OS</span></>}
                            {benchN > 0 && <><Box component="span" sx={{ mx: '3px', opacity: 0.3 }}>•</Box><Box component="span" sx={{ color: '#9C27B0' }}>{benchN} bench</Box></>}
                            {flexN > 0 && <><Box component="span" sx={{ mx: '3px', opacity: 0.3 }}>•</Box><Box component="span" sx={{ color: '#2196F3' }}>{flexN} flex</Box></>}
                            {wildcardN > 0 && <><Box component="span" sx={{ mx: '3px', opacity: 0.3 }}>•</Box><span>{wildcardN}W ⚡</span></>}
                          </Typography>
                        );
                      })()}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
                      {existingSquad && (
                        <StatusChip status={existingSquad.isSubmitted ? 'submitted' : 'draft'} />
                      )}
                      {squadSummaryOpen ? <ExpandLessIcon fontSize="small" sx={{ color: 'text.secondary' }} /> : <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
                    </Box>
                  </Box>
                  {/* Row 2: C / VC / XF — colored text, no pill */}
                  {!squadSummaryOpen && (captainId || viceCaptainId || xFactorId) && (
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
                      {[
                        { label: 'C', id: captainId, color: '#FFB300' },
                        { label: 'VC', id: viceCaptainId, color: '#9C27B0' },
                        { label: 'XF', id: xFactorId, color: themeColors.blue.light },
                      ].map(({ label, id, color }) => {
                        const p = id ? availablePlayers.find(pl => pl.id === id) : null;
                        if (!p) return null;
                        const lastName = p.name.split(' ').slice(-1)[0];
                        return (
                          <Box key={label} sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                            <Typography sx={{ fontSize: '0.65rem', fontWeight: 500, color: alpha(color, 0.65), lineHeight: 1 }}>
                              {label}
                            </Typography>
                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color, lineHeight: 1 }}>
                              {lastName}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  )}
                </Box>

                <Collapse in={squadSummaryOpen}>
                {/* Stats + Role assignments — two columns on desktop */}
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { sm: 'stretch' } }}>
                  {/* Section 1 — Stats row */}
                  <Box sx={{ display: 'flex', gap: 1.5, flex: { sm: 1 } }}>
                    {/* Players stat */}
                    <Box sx={{ flex: 1, textAlign: 'center', px: 1.5, py: 1, borderRadius: 2, bgcolor: alpha(elBlue, 0.06) }}>
                      <Typography sx={{ fontSize: { xs: '1.5rem', sm: '1.6rem' }, fontWeight: 700, color: isPlayersValid ? elBlue : 'error.main', lineHeight: 1, letterSpacing: '-0.02em', textShadow: `0 0 20px ${alpha(isPlayersValid ? elBlue : '#F44336', 0.4)}` }}>
                        {regularCount}<Typography component="span" sx={{ fontSize: '0.85rem', color: 'text.secondary', fontWeight: 400 }}>/{squadMax}</Typography>
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.6rem' }}>Players</Typography>
                    </Box>
                    {/* Overseas stat */}
                    {league?.squadRules?.overseasPlayersEnabled && (
                      <Box sx={{ flex: 1, textAlign: 'center', px: 1.5, py: 1, borderRadius: 2, bgcolor: alpha(isOverseasOver ? '#F44336' : '#2196F3', 0.06) }}>
                        <Typography sx={{ fontSize: { xs: '1.5rem', sm: '1.6rem' }, fontWeight: 700, color: isOverseasOver ? 'error.main' : '#2196F3', lineHeight: 1, letterSpacing: '-0.02em', textShadow: `0 0 20px ${alpha(isOverseasOver ? '#F44336' : '#2196F3', 0.4)}` }}>
                          {overseasCount}<Typography component="span" sx={{ fontSize: '0.85rem', color: 'text.secondary', fontWeight: 400 }}>/{overseasMax}</Typography>
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.6rem' }}>Overseas</Typography>
                      </Box>
                    )}
                    {/* Bench slots (pre-deadline) */}
                    {!isDeadlinePassed && hasBench && (
                      <Box sx={{ flex: 1, textAlign: 'center', px: 1.5, py: 1, borderRadius: 2, bgcolor: alpha('#9C27B0', 0.06) }}>
                        <Typography sx={{ fontSize: { xs: '1.5rem', sm: '1.6rem' }, fontWeight: 700, color: '#9C27B0', lineHeight: 1, letterSpacing: '-0.02em', textShadow: `0 0 20px ${alpha('#9C27B0', 0.4)}` }}>
                          {selectedPlayers.filter(p => p.position === 'bench').length}<Typography component="span" sx={{ fontSize: '0.85rem', color: 'text.secondary', fontWeight: 400 }}>/{league.transferTypes!.benchTransfers.benchSlots}</Typography>
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: '0.6rem' }}>Bench</Typography>
                      </Box>
                    )}
                  </Box>

                  <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', sm: 'block' }, borderColor: alpha('#fff', 0.07) }} />
                  <Divider sx={{ display: { xs: 'block', sm: 'none' }, borderColor: alpha('#fff', 0.05) }} />

                  {/* Section 2 — Role assignments (C / VC / XF) */}
                  <Box sx={{ display: 'flex', gap: 1, flex: { sm: 1 } }}>
                    {[
                      { label: 'C', id: captainId, color: '#FFB300', icon: <Star /> },
                      { label: 'VC', id: viceCaptainId, color: '#9C27B0', icon: <Star /> },
                      { label: 'XF', id: xFactorId, color: themeColors.blue.light, icon: <AutoAwesome /> },
                    ].map(({ label, id, color, icon }) => {
                      const player = id ? availablePlayers.find(p => p.id === id) : null;
                      return (
                        <Box key={label} sx={{
                          flex: 1, py: 1, px: 0.75, borderRadius: 2, textAlign: 'center',
                          bgcolor: id ? alpha(color, 0.1) : 'transparent',
                          border: id ? `1px solid ${alpha(color, 0.35)}` : `1px dashed ${alpha('#fff', 0.1)}`,
                          boxShadow: id ? `0 0 12px ${alpha(color, 0.15)}` : 'none',
                        }}>
                          {React.cloneElement(icon, { sx: { fontSize: 14, color: id ? color : 'text.disabled', mb: 0.25 } })}
                          <Typography variant="caption" sx={{ display: 'block', fontSize: '0.6rem', color: id ? color : 'text.disabled', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</Typography>
                          <Typography variant="caption" sx={{ display: 'block', fontSize: '0.65rem', color: id ? 'text.primary' : 'text.disabled', fontWeight: 500, lineHeight: 1.2, mt: 0.25 }} noWrap>
                            {player ? player.name.split(' ').slice(-1)[0] : '—'}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>

                <Divider sx={{ my: 1.5, borderColor: alpha('#fff', 0.05) }} />

                {/* Section 4 — Status chips */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 0.75, sm: 1 } }}>
                  <Chip
                    label={`Predictions: ${topRunScorer && topWicketTaker && winningTeam ? 'Complete' : 'Incomplete'}`}
                    variant={topRunScorer && topWicketTaker && winningTeam ? 'filled' : 'outlined'}
                    size="small"
                    sx={{
                      fontFamily: "'Satoshi', sans-serif",
                      fontWeight: 500,
                      fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                      height: { xs: 26, sm: 28 },
                      bgcolor: topRunScorer && topWicketTaker && winningTeam ? alpha(theme.palette.secondary.main, 0.15) : 'transparent',
                      borderColor: topRunScorer && topWicketTaker && winningTeam ? theme.palette.secondary.main : alpha(theme.palette.text.secondary, 0.3),
                      color: topRunScorer && topWicketTaker && winningTeam ? theme.palette.secondary.main : 'text.secondary'
                    }}
                  />
                  {league?.powerplayEnabled && (
                    <Chip
                      label={
                        powerplayMatch.trim() !== ''
                          ? `PP Match: Match ${powerplayMatch}`
                          : (league.ppMatchMode ?? 'fixed') === 'activation'
                            ? 'PP: Activate anytime'
                            : 'PP Match: Not Selected'
                      }
                      variant={powerplayMatch.trim() !== '' ? 'filled' : 'outlined'}
                      size="small"
                      sx={{
                        fontFamily: "'Satoshi', sans-serif",
                        fontWeight: 500,
                        fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                        height: { xs: 26, sm: 28 },
                        bgcolor: powerplayMatch.trim() !== '' ? alpha(theme.palette.warning.main, 0.15) : 'transparent',
                        borderColor: powerplayMatch.trim() !== ''
                          ? theme.palette.warning.main
                          : (league.ppMatchMode ?? 'fixed') === 'activation'
                            ? alpha(theme.palette.info.main, 0.4)
                            : alpha(theme.palette.text.secondary, 0.3),
                        color: powerplayMatch.trim() !== ''
                          ? theme.palette.warning.main
                          : (league.ppMatchMode ?? 'fixed') === 'activation'
                            ? 'info.main'
                            : 'text.secondary'
                      }}
                    />
                  )}
                  {/* Bench / Flex / Wildcard remaining transfers */}
                  {league?.transferTypes?.benchTransfers?.enabled && (() => {
                    const n = league.transferTypes!.benchTransfers!.maxAllowed - (existingSquad?.benchTransfersUsed ?? 0);
                    if (n <= 0) return null;
                    return (
                      <Chip
                        label={`${n} Bench left`}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontFamily: "'Satoshi', sans-serif", fontWeight: 500,
                          fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                          height: { xs: 26, sm: 28 },
                          bgcolor: 'rgba(156,39,176,0.08)', borderColor: 'rgba(156,39,176,0.35)', color: '#9C27B0',
                        }}
                      />
                    );
                  })()}
                  {league?.transferTypes?.flexibleTransfers?.enabled && (() => {
                    const n = league.transferTypes!.flexibleTransfers!.maxAllowed - (existingSquad?.flexibleTransfersUsed ?? 0);
                    if (n <= 0) return null;
                    return (
                      <Chip
                        label={`${n} Flex left`}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontFamily: "'Satoshi', sans-serif", fontWeight: 500,
                          fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                          height: { xs: 26, sm: 28 },
                          bgcolor: 'rgba(33,150,243,0.08)', borderColor: 'rgba(33,150,243,0.25)', color: '#2196F3',
                        }}
                      />
                    );
                  })()}
                  {league?.transferTypes?.wildcardTransfers?.enabled && (() => {
                    const n = league.transferTypes!.wildcardTransfers!.maxAllowed - (existingSquad?.wildcardTransfersUsed ?? 0);
                    if (n <= 0) return null;
                    return (
                      <Chip
                        label={`${n} Wildcard left`}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontFamily: "'Satoshi', sans-serif", fontWeight: 500,
                          fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                          height: { xs: 26, sm: 28 },
                          bgcolor: 'rgba(255,215,0,0.06)', borderColor: 'rgba(255,215,0,0.3)', color: '#FFD700',
                        }}
                      />
                    );
                  })()}
                  {league?.hiddenPlayerEnabled && hiddenPlayerId && (() => {
                    const hp = availablePlayers.find(p => p.id === hiddenPlayerId);
                    return hp ? (
                      <Chip
                        icon={<LockIcon sx={{ fontSize: '0.875rem !important', color: '#FFB300 !important' }} />}
                        label={`${hp.name} · Hidden`}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontFamily: "'Satoshi', sans-serif",
                          fontWeight: 500,
                          fontSize: { xs: '0.6875rem', sm: '0.75rem' },
                          height: { xs: 26, sm: 28 },
                          bgcolor: 'rgba(255,193,7,0.10)',
                          borderColor: 'rgba(255,193,7,0.45)',
                          color: '#FFB300',
                          '& .MuiChip-label': { color: '#FFB300' },
                        }}
                      />
                    ) : null;
                  })()}
                </Box>
                </Collapse>
              </CardContent>
            </Card>
          );
        })()}

        {/* Cricket Grid UI */}
        {league?.sport !== 'tennis' && <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* Left Panel - Squad Formation */}
          <Grid size={{ xs: 12, lg: (isDeadlinePassed && !canMakeTransfer) ? 12 : 8 }}>
            <CricketPitchFormation
              league={league}
              selectedPlayers={selectedPlayers}
              onRemovePlayer={removePlayerFromSquad}
              onUpdatePosition={updatePlayerPosition}
              powerplayMatch={powerplayMatch}
              setPowerplayMatch={setPowerplayMatch}
              maxPowerplayMatches={league.maxPowerplayMatches || 20}
              ppActivatedAt={ppActivatedAt}
              onActivatePP={async () => {
                const now = new Date();
                setPpActivatedAt(now);
                // If squad already exists, persist the timestamp immediately
                if (existingSquad) {
                  await squadService.update(existingSquad.id, { ppActivatedAt: now });
                }
                // If no squad yet, ppActivatedAt will be included when squad is saved/submitted
              }}
              ppActivationEnabled={!isDeadlinePassed || !!(league.ppActivationEnabled)}
              ppMatchConfirmOpen={ppMatchConfirmOpen}
              setPpMatchConfirmOpen={setPpMatchConfirmOpen}
              ppMatchPending={ppMatchPending}
              setPpMatchPending={setPpMatchPending}
              onConfirmPpMatch={async () => {
                setPowerplayMatch(ppMatchPending);
                setPpMatchConfirmOpen(false);
                setPpMatchPending('');
                if (existingSquad && ppMatchPending) {
                  await squadService.update(existingSquad.id, {
                    powerplayMatchNumber: parseInt(ppMatchPending),
                  });
                }
              }}
              captainId={captainId}
              viceCaptainId={viceCaptainId}
              xFactorId={xFactorId}
              onSetSpecialRole={setPlayerAsSpecialRole}
              existingSquad={existingSquad}
              calculatePlayerContribution={calculatePlayerContribution}
              readOnly={isDeadlinePassed && !canMakeTransfer}
              isDeadlinePassed={isDeadlinePassed}
              hiddenPlayerId={hiddenPlayerId}
              hiddenPlayerSearch={hiddenPlayerSearch}
              setHiddenPlayerSearch={setHiddenPlayerSearch}
              onSelectHiddenPlayer={(id: string) => setHiddenPlayerId(id)}
              availablePlayers={availablePlayers}
              submitting={submitting}
              onSavePredictionsAndHiddenPlayer={handleSavePredictionsAndHiddenPlayer}
              onAddPlayer={addPlayerToSquad}
              ppDialogOpen={ppDialogOpen}
              setPpDialogOpen={setPpDialogOpen}
              ppDialogStep={ppDialogStep}
              setPpDialogStep={setPpDialogStep}
            />
          </Grid>

          {/* Right Panel - Player Selection */}
          {!(isDeadlinePassed && !canMakeTransfer) && (
            <Grid size={{ xs: 12, lg: 4 }}>
              {hiddenConflictAlert && (
                <Alert
                  severity="error"
                  icon={<LockIcon fontSize="small" />}
                  sx={{ mb: 1.5, fontSize: '0.85rem' }}
                  onClose={() => setHiddenConflictAlert('')}
                >
                  {hiddenConflictAlert}
                </Alert>
              )}
              <PlayerSelectionPanel
                availablePlayers={availablePlayers}
                selectedPlayers={selectedPlayers}
                onAddPlayer={addPlayerToSquad}
                filterRole={filterRole}
                setFilterRole={setFilterRole}
                league={league}
              />
            </Grid>
          )}
        </Grid>}

        {/* Predictions Section (Cricket only) */}
        {league?.sport !== 'tennis' && <>
        <Card sx={{
          mt: { xs: 2, sm: 3 },
          background: 'linear-gradient(145deg, rgba(26,58,92,0.6), rgba(6,13,23,0.8))',
          border: `1px solid ${alpha(themeColors.blue.electric, 0.2)}`,
          borderLeft: `3px solid ${isDeadlinePassed ? alpha(themeColors.blue.electric, 0.3) : themeColors.blue.electric}`,
          backdropFilter: 'blur(12px)',
          boxShadow: `0 4px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.05)`,
        }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>

            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: 1.5,
                  background: isDeadlinePassed
                    ? alpha(themeColors.blue.electric, 0.12)
                    : `linear-gradient(135deg, ${themeColors.blue.light} 0%, ${themeColors.blue.electric} 100%)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: isDeadlinePassed ? 'none' : `0 2px 8px ${alpha(themeColors.blue.electric, 0.4)}`,
                }}>
                  <AutoAwesome sx={{ fontSize: '1rem', color: isDeadlinePassed ? alpha('#fff', 0.3) : '#000' }} />
                </Box>
                <Box>
                  <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: { xs: '0.95rem', sm: '1.05rem' }, letterSpacing: '0.01em', lineHeight: 1.2 }}>
                    Make Your Predictions
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: alpha('#fff', 0.4), fontWeight: 500, letterSpacing: '0.02em' }}>
                    {isDeadlinePassed ? 'Locked after deadline' : 'Required to submit squad'}
                  </Typography>
                </Box>
              </Box>
              <Chip
                size="small"
                icon={isDeadlinePassed ? <LockIcon sx={{ fontSize: '0.7rem !important' }} /> : <CheckCircleOutline sx={{ fontSize: '0.7rem !important' }} />}
                label={isDeadlinePassed ? 'Locked' : topRunScorer && topWicketTaker && winningTeam ? 'Complete' : 'Incomplete'}
                sx={{
                  height: 22, fontSize: '0.65rem', fontWeight: 700,
                  bgcolor: isDeadlinePassed
                    ? alpha('#fff', 0.06)
                    : topRunScorer && topWicketTaker && winningTeam
                      ? alpha(themeColors.success?.primary || '#4CAF50', 0.15)
                      : alpha('#F59E0B', 0.15),
                  color: isDeadlinePassed
                    ? alpha('#fff', 0.35)
                    : topRunScorer && topWicketTaker && winningTeam
                      ? themeColors.success?.primary || '#4CAF50'
                      : '#F59E0B',
                  border: `1px solid ${isDeadlinePassed ? alpha('#fff', 0.1) : topRunScorer && topWicketTaker && winningTeam ? alpha(themeColors.success?.primary || '#4CAF50', 0.3) : alpha('#F59E0B', 0.3)}`,
                  '& .MuiChip-icon': { color: 'inherit' },
                }}
              />
            </Box>

            {/* Status message */}
            {isDeadlinePassed && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5, px: 1.5, py: 1, borderRadius: 1.5, bgcolor: alpha(themeColors.blue.electric, 0.06), border: `1px solid ${alpha(themeColors.blue.electric, 0.12)}` }}>
                <LockIcon sx={{ fontSize: '0.8rem', color: alpha('#fff', 0.35), flexShrink: 0 }} />
                <Typography sx={{ fontSize: '0.75rem', color: alpha('#fff', 0.45) }}>
                  Predictions are locked — the squad deadline has passed.
                </Typography>
              </Box>
            )}

            <Grid container spacing={{ xs: 1.5, sm: 2 }}>
              {/* Top Run Scorer */}
              <Grid size={{ xs: 12, sm: 4 }}>
                <Box sx={{ position: 'relative' }}>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: alpha('#F59E0B', isDeadlinePassed ? 0.4 : 0.8), letterSpacing: '0.08em', textTransform: 'uppercase', mb: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box component="span" sx={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', bgcolor: alpha('#F59E0B', isDeadlinePassed ? 0.3 : 0.8) }} />
                    Top Run Scorer
                  </Typography>
                  <Autocomplete
                    freeSolo
                    options={playerNameOptions}
                    value={topRunScorer}
                    onInputChange={(_e, value) => setTopRunScorer(value)}
                    disabled={isDeadlinePassed}
                    size="small"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        required
                        placeholder="e.g., Virat Kohli"
                        variant="outlined"
                        error={!isDeadlinePassed && topRunScorer.trim() === ''}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <BoltIcon sx={{ fontSize: '1rem', color: alpha('#F59E0B', isDeadlinePassed ? 0.3 : 0.85), transform: 'rotate(0deg)' }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            background: alpha('#000', 0.25),
                            borderRadius: 1.5,
                            '& fieldset': { borderColor: alpha('#F59E0B', isDeadlinePassed ? 0.1 : 0.25) },
                            '&:hover fieldset': { borderColor: alpha('#F59E0B', 0.45) },
                            '&.Mui-focused fieldset': { borderColor: '#F59E0B' },
                            '&.Mui-disabled fieldset': { borderColor: alpha('#fff', 0.07) },
                          },
                          '& .MuiInputBase-input': { fontSize: '0.875rem', fontWeight: 500 },
                          '& .MuiInputBase-input::placeholder': { color: alpha('#fff', 0.25) },
                        }}
                      />
                    )}
                  />
                </Box>
              </Grid>

              {/* Top Wicket Taker */}
              <Grid size={{ xs: 12, sm: 4 }}>
                <Box sx={{ position: 'relative' }}>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: alpha(themeColors.blue.light, isDeadlinePassed ? 0.4 : 0.8), letterSpacing: '0.08em', textTransform: 'uppercase', mb: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box component="span" sx={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', bgcolor: alpha(themeColors.blue.light, isDeadlinePassed ? 0.3 : 0.8) }} />
                    Top Wicket Taker
                  </Typography>
                  <Autocomplete
                    freeSolo
                    options={playerNameOptions}
                    value={topWicketTaker}
                    onInputChange={(_e, value) => setTopWicketTaker(value)}
                    disabled={isDeadlinePassed}
                    size="small"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        required
                        placeholder="e.g., Jasprit Bumrah"
                        variant="outlined"
                        error={!isDeadlinePassed && topWicketTaker.trim() === ''}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <BoltIcon sx={{ fontSize: '1rem', color: alpha(themeColors.blue.light, isDeadlinePassed ? 0.3 : 0.85) }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            background: alpha('#000', 0.25),
                            borderRadius: 1.5,
                            '& fieldset': { borderColor: alpha(themeColors.blue.electric, isDeadlinePassed ? 0.1 : 0.25) },
                            '&:hover fieldset': { borderColor: alpha(themeColors.blue.electric, 0.5) },
                            '&.Mui-focused fieldset': { borderColor: themeColors.blue.electric },
                            '&.Mui-disabled fieldset': { borderColor: alpha('#fff', 0.07) },
                          },
                          '& .MuiInputBase-input': { fontSize: '0.875rem', fontWeight: 500 },
                          '& .MuiInputBase-input::placeholder': { color: alpha('#fff', 0.25) },
                        }}
                      />
                    )}
                  />
                </Box>
              </Grid>

              {/* Winning Team */}
              <Grid size={{ xs: 12, sm: 4 }}>
                <Box sx={{ position: 'relative' }}>
                  <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: alpha('#4CAF50', isDeadlinePassed ? 0.4 : 0.8), letterSpacing: '0.08em', textTransform: 'uppercase', mb: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box component="span" sx={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', bgcolor: alpha('#4CAF50', isDeadlinePassed ? 0.3 : 0.8) }} />
                    Winning Team
                  </Typography>
                  <Autocomplete
                    freeSolo
                    options={teamOptions}
                    value={winningTeam}
                    onInputChange={(_e, value) => setWinningTeam(value)}
                    disabled={isDeadlinePassed}
                    size="small"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        required
                        placeholder="e.g., India or Australia"
                        variant="outlined"
                        error={!isDeadlinePassed && winningTeam.trim() === ''}
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <InputAdornment position="start">
                              <CheckCircle sx={{ fontSize: '1rem', color: alpha('#4CAF50', isDeadlinePassed ? 0.3 : 0.85) }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            background: alpha('#000', 0.25),
                            borderRadius: 1.5,
                            '& fieldset': { borderColor: alpha('#4CAF50', isDeadlinePassed ? 0.1 : 0.2) },
                            '&:hover fieldset': { borderColor: alpha('#4CAF50', 0.45) },
                            '&.Mui-focused fieldset': { borderColor: '#4CAF50' },
                            '&.Mui-disabled fieldset': { borderColor: alpha('#fff', 0.07) },
                          },
                          '& .MuiInputBase-input': { fontSize: '0.875rem', fontWeight: 500 },
                          '& .MuiInputBase-input::placeholder': { color: alpha('#fff', 0.25) },
                        }}
                      />
                    )}
                  />
                </Box>
              </Grid>
            </Grid>

            {/* Footer row: tip + save button */}
            <Box sx={{ mt: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
              {!isDeadlinePassed && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <InfoOutlined sx={{ fontSize: '0.8rem', color: alpha('#fff', 0.25), flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '0.7rem', color: alpha('#fff', 0.35) }}>
                    All three predictions are required. Saved with your squad.
                  </Typography>
                </Box>
              )}
              {existingSquad?.isSubmitted && !isDeadlinePassed && (
                <Button
                  variant="contained"
                  size="small"
                  disabled={submitting || topRunScorer.trim() === '' || topWicketTaker.trim() === '' || winningTeam.trim() === ''}
                  onClick={handleSavePredictionsAndHiddenPlayer}
                  sx={{
                    ml: 'auto',
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    px: 2,
                    py: 0.75,
                    borderRadius: 2,
                    color: '#000',
                    background: `linear-gradient(135deg, ${themeColors.blue.light} 0%, ${themeColors.blue.electric} 100%)`,
                    boxShadow: `0 2px 8px ${alpha(themeColors.blue.electric, 0.4)}`,
                    '&:hover': { background: `linear-gradient(135deg, #64B5F6 0%, ${themeColors.blue.light} 100%)`, transform: 'translateY(-1px)', boxShadow: `0 4px 14px ${alpha(themeColors.blue.electric, 0.5)}` },
                    '&.Mui-disabled': { background: alpha(themeColors.blue.electric, 0.15), color: alpha('#000', 0.3) },
                    transition: 'all 0.2s ease',
                  }}
                >
                  Save Predictions
                </Button>
              )}
            </Box>

          </CardContent>
        </Card>
        </>}
      </Container>

      {/* Transfer Modal */}
      {isDeadlinePassed && existingSquad && league && (
        <TransferModal
          open={transferModalOpen}
          onClose={() => setTransferModalOpen(false)}
          league={league}
          existingSquad={existingSquad}
          availablePlayers={availablePlayers}
          onSubmitTransfer={handleTransferSubmit}
        />
      )}

      {/* AI Assistant Widget */}
      <LeagueAssistant leagueId={leagueId} />

      {/* Submit / Update success toast — transient, fires once on return after submit */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={6500}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: '80px', md: '24px' } }}
      >
        <Alert
          severity="success"
          onClose={() => setToastOpen(false)}
          sx={{
            bgcolor: alpha('#0A1929', 0.96),
            border: `1px solid ${alpha('#4CAF50', 0.45)}`,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            color: '#fff',
            fontFamily: "'Satoshi', sans-serif",
            borderRadius: 2,
            boxShadow: `0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px ${alpha('#4CAF50', 0.15)}`,
            minWidth: { xs: '280px', sm: '340px' },
            '& .MuiAlert-icon': { color: '#4CAF50', mt: 0.25 },
            '& .MuiAlert-action': { pt: 0.5, alignItems: 'flex-start' },
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', lineHeight: 1.3 }}>
            {toastIsUpdate ? 'Squad Updated!' : 'Squad Submitted!'}
          </Typography>
          {league?.squadDeadline && (
            <Typography sx={{ fontSize: '0.75rem', color: alpha('#fff', 0.65), mt: 0.25 }}>
              {'Changes allowed until '}
              {new Date(league.squadDeadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              {' at '}
              {new Date(league.squadDeadline).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
            </Typography>
          )}
        </Alert>
      </Snackbar>

      {/* Draft saved toast */}
      <Snackbar
        open={draftSavedOpen}
        autoHideDuration={4000}
        onClose={() => setDraftSavedOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: '80px', md: '24px' } }}
      >
        <Alert
          severity="info"
          onClose={() => setDraftSavedOpen(false)}
          sx={{
            bgcolor: alpha('#0A1929', 0.96),
            border: `1px solid ${alpha(themeColors.orange.primary, 0.45)}`,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            color: '#fff',
            fontFamily: "'Satoshi', sans-serif",
            borderRadius: 2,
            boxShadow: `0 8px 32px rgba(0,0,0,0.5), 0 0 0 1px ${alpha(themeColors.orange.primary, 0.15)}`,
            minWidth: { xs: '280px', sm: '340px' },
            '& .MuiAlert-icon': { color: themeColors.orange.primary, mt: 0.25 },
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: '0.875rem', lineHeight: 1.3 }}>
            Draft Saved
          </Typography>
          <Typography sx={{ fontSize: '0.75rem', color: alpha('#fff', 0.65), mt: 0.25 }}>
            Submit when you're ready.
          </Typography>
        </Alert>
      </Snackbar>
    </Box>
  );
};

const getRoleBadge = (role?: string): { label: string; color: string } => {
  switch (role) {
    case 'batsman':      return { label: 'BAT', color: '#F59E0B' };
    case 'bowler':       return { label: 'BWL', color: '#42A5F5' };
    case 'allrounder':   return { label: 'AR',  color: '#4CAF50' };
    case 'wicketkeeper': return { label: 'WK',  color: '#CE93D8' };
    default:             return { label: (role ?? '???').slice(0, 3).toUpperCase(), color: '#fff' };
  }
};


const getCareerStats = (p: PlayerWithPerformance) => {
  const allBat   = p.battingInnings ?? [];
  const allBowl  = p.bowlingSpells ?? [];
  const allField = p.fieldingPerformances ?? [];
  const totalRuns  = allBat.reduce((s, e) => s + e.runs, 0);
  const totalBalls = allBat.reduce((s, e) => s + e.ballsFaced, 0);
  const totalWkts  = allBowl.reduce((s, e) => s + e.wickets, 0);
  const totalRC    = allBowl.reduce((s, e) => s + e.runsConceded, 0);
  const totalOvers = allBowl.reduce((s, e) => s + e.overs, 0);
  const totalCat   = allField.reduce((s, e) => s + e.catches, 0);
  const totalSt    = allField.reduce((s, e) => s + e.stumpings, 0);
  const allLabels  = [
    ...allBat.map(e => e.matchLabel), ...allBowl.map(e => e.matchLabel), ...allField.map(e => e.matchLabel),
  ].filter(Boolean) as string[];
  const matchCount = new Set(allLabels).size;
  const sr = totalBalls > 0 ? (totalRuns / totalBalls) * 100 : 0;
  const bowlBalls = Math.floor(totalOvers) * 6 + Math.round((totalOvers % 1) * 10);
  const economy = bowlBalls > 0 ? (totalRC / bowlBalls) * 6 : 0;
  return { totalRuns, totalWkts, sr, economy, totalCat, totalSt, matchCount };
};

const getRoleStats = (role: string, career: ReturnType<typeof getCareerStats>): Array<{ label: string; value: string | number }> => {
  const mat = { label: 'MAT', value: career.matchCount };
  switch (role) {
    case 'batsman':      return [mat, { label: 'RUNS', value: career.totalRuns }, { label: 'SR', value: career.sr.toFixed(0) }];
    case 'bowler':       return [mat, { label: 'WKTS', value: career.totalWkts }, { label: 'ECO', value: career.economy.toFixed(1) }];
    case 'allrounder':   return [mat, { label: 'RUNS', value: career.totalRuns }, { label: 'WKTS', value: career.totalWkts }];
    case 'wicketkeeper': return [mat, { label: 'RUNS', value: career.totalRuns }, { label: 'DIS', value: career.totalCat + career.totalSt }];
    default:             return [mat];
  }
};

// Cricket Pitch Formation Component
const CricketPitchFormation: React.FC<{
  league: League;
  selectedPlayers: SelectedPlayer[];
  onRemovePlayer: (playerId: string) => void;
  onUpdatePosition: (playerId: string, position: 'regular' | 'bench') => void;
  powerplayMatch: string;
  setPowerplayMatch: (match: string) => void;
  maxPowerplayMatches: number;
  ppActivatedAt: Date | null;
  onActivatePP: () => Promise<void>;
  ppActivationEnabled: boolean;
  ppMatchConfirmOpen: boolean;
  setPpMatchConfirmOpen: (v: boolean) => void;
  ppMatchPending: string;
  setPpMatchPending: (v: string) => void;
  onConfirmPpMatch: () => void;
  captainId: string | null;
  viceCaptainId: string | null;
  xFactorId: string | null;
  onSetSpecialRole: (playerId: string, role: 'captain' | 'vice_captain' | 'x_factor' | null) => void;
  existingSquad: LeagueSquad | null;
  calculatePlayerContribution: (player: SquadPlayer, role: 'captain' | 'viceCaptain' | 'xFactor' | 'regular') => number;
  readOnly: boolean;
  isDeadlinePassed: boolean;
  hiddenPlayerId: string | null;
  hiddenPlayerSearch: string;
  setHiddenPlayerSearch: (v: string) => void;
  onSelectHiddenPlayer: (id: string) => void;
  availablePlayers: Player[];
  submitting: boolean;
  onSavePredictionsAndHiddenPlayer: () => Promise<void>;
  onAddPlayer: (player: Player, position: 'regular' | 'bench') => void;
  ppDialogOpen: boolean;
  setPpDialogOpen: (v: boolean) => void;
  ppDialogStep: 'select' | 'confirm' | 'success';
  setPpDialogStep: (v: 'select' | 'confirm' | 'success') => void;
}> = ({ league, selectedPlayers, onRemovePlayer, onUpdatePosition, powerplayMatch, setPowerplayMatch, maxPowerplayMatches, ppActivatedAt, onActivatePP, ppActivationEnabled, ppMatchConfirmOpen, setPpMatchConfirmOpen, ppMatchPending, setPpMatchPending, onConfirmPpMatch, captainId, viceCaptainId, xFactorId, onSetSpecialRole, existingSquad, calculatePlayerContribution, readOnly, isDeadlinePassed, hiddenPlayerId, hiddenPlayerSearch, setHiddenPlayerSearch, onSelectHiddenPlayer, availablePlayers, submitting, onSavePredictionsAndHiddenPlayer, onAddPlayer, ppDialogOpen, setPpDialogOpen, ppDialogStep, setPpDialogStep }) => {

  const [slotPickerOpen, setSlotPickerOpen] = useState(false);
  const [slotPickerRole, setSlotPickerRole] = useState<string | null>(null);
  const [slotPickerTarget, setSlotPickerTarget] = useState<'regular' | 'bench'>('regular');
  const [slotPickerSearch, setSlotPickerSearch] = useState('');
  const [transferHistoryOpen, setTransferHistoryOpen] = useState(false);
  const [ppDescOpen, setPpDescOpen] = useState(false);
  const pitchTheme = useTheme();
  const isMobileSlot = useMediaQuery(pitchTheme.breakpoints.down('sm'));

  const handlePpDialogNext = () => {
    setPpDialogStep('confirm');
  };

  const handlePpDialogConfirm = async () => {
    await onActivatePP();
    onConfirmPpMatch();
    setPpDialogStep('success');
    setTimeout(() => setPpDialogOpen(false), 2500);
  };

  const getRequiredSlots = () => {
    const { squadRules } = league;
    return {
      batsmen: Array(squadRules.minBatsmen).fill(null),
      bowlers: Array(squadRules.minBowlers).fill(null),
      allrounders: Array(0).fill(null), // No minimum all-rounders required
      wicketkeepers: Array(squadRules.minWicketkeepers).fill(null),
      flexible: Array(league.squadSize - squadRules.minBatsmen - squadRules.minBowlers - squadRules.minWicketkeepers).fill(null), // Removed minAllrounders from calculation
      bench: league.transferTypes?.benchTransfers?.enabled ? Array(league.transferTypes.benchTransfers.benchSlots).fill(null) : []
    };
  };

  const requiredSlots = getRequiredSlots();

  // Pre-calculate player assignments to prevent any duplication or stacking
  const mainSquadPlayers = selectedPlayers.filter(p => p.position !== 'bench');
  const benchPlayers = selectedPlayers.filter(p => p.position === 'bench');

  // Assign players to slots preserving the order they were added
  // This prevents players from "jumping" between slots when new players are added
  const slotAssignments = {
    batsman: [] as (SelectedPlayer | undefined)[],
    bowler: [] as (SelectedPlayer | undefined)[],
    allrounder: [] as (SelectedPlayer | undefined)[],
    wicketkeeper: [] as (SelectedPlayer | undefined)[],
    flexible: [] as SelectedPlayer[]
  };

  // Track how many players of each role we've assigned to required slots
  const roleCounters = {
    batsman: 0,
    bowler: 0,
    allrounder: 0,
    wicketkeeper: 0
  };

  // Go through players in the order they were added
  mainSquadPlayers.forEach(player => {
    const role = player.role as 'batsman' | 'bowler' | 'allrounder' | 'wicketkeeper';
    const minRequired = league.squadRules[
      role === 'batsman' ? 'minBatsmen' :
      role === 'bowler' ? 'minBowlers' :
      role === 'allrounder' ? 'minAllrounders' :
      'minWicketkeepers'
    ];

    // If this role still has required slots available, assign to required slot
    if (roleCounters[role] < minRequired) {
      slotAssignments[role][roleCounters[role]] = player;
      roleCounters[role]++;
    } else {
      // Otherwise, assign to flexible slot (in order added)
      slotAssignments.flexible.push(player);
    }
  });

  // For backwards compatibility with existing slot rendering
  const playersByRole = {
    batsman: slotAssignments.batsman.filter((p): p is SelectedPlayer => p !== undefined),
    bowler: slotAssignments.bowler.filter((p): p is SelectedPlayer => p !== undefined),
    allrounder: slotAssignments.allrounder.filter((p): p is SelectedPlayer => p !== undefined),
    wicketkeeper: slotAssignments.wicketkeeper.filter((p): p is SelectedPlayer => p !== undefined)
  };

  const flexiblePlayersList = slotAssignments.flexible;

  const getRoleDisplayText = (role?: string): string => {
    if (!role) return '';
    switch (role) {
      case 'batsman': return 'Batter';
      case 'bowler': return 'Bowler';
      case 'allrounder': return 'Allrounder';
      case 'wicketkeeper': return 'Wicketkeeper';
      default: return role.charAt(0).toUpperCase() + role.slice(1);
    }
  };

  const getSlotColors = (_slotType: 'required' | 'flexible' | 'bench', _role?: string) => {
    return { bg: 'rgba(255,255,255,0.03)', border: 'rgba(255,255,255,0.12)' };
  };

  const PlayerSlot: React.FC<{
    player?: SelectedPlayer;
    role?: string;
    slotType: 'required' | 'flexible' | 'bench';
    position: number;
  }> = ({ player, role, slotType, position }) => {
    const [showRoleMenu, setShowRoleMenu] = useState(false);
    const [hideTimeout, setHideTimeout] = React.useState<NodeJS.Timeout | null>(null);
    const [hovered, setHovered] = useState(false);
    const colors = getSlotColors(slotType, role);

    const isCaptain = player && captainId === player.id;
    const isViceCaptain = player && viceCaptainId === player.id;
    const isXFactor = player && xFactorId === player.id;
    const canAssignRoles = player && slotType !== 'bench' && !readOnly;

    const handleMouseEnter = () => {
      setHovered(true);
      if (canAssignRoles) {
        if (hideTimeout) {
          clearTimeout(hideTimeout);
          setHideTimeout(null);
        }
        setShowRoleMenu(true);
      }
    };

    const handleMouseLeave = () => {
      setHovered(false);
      const timeout = setTimeout(() => {
        setShowRoleMenu(false);
      }, 300); // 300ms delay before hiding
      setHideTimeout(timeout);
    };

    return (
      <Paper
        elevation={player ? 8 : 2}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={(e) => {
          if (canAssignRoles) {
            e.stopPropagation();
            const opening = !showRoleMenu;
            setShowRoleMenu(opening);
            if (opening) {
              if (hideTimeout) clearTimeout(hideTimeout);
              const timeout = setTimeout(() => setShowRoleMenu(false), 3000);
              setHideTimeout(timeout);
            }
          } else if (!player && !readOnly && isMobileSlot) {
            // Mobile: tap empty slot → open filtered player picker drawer
            e.stopPropagation();
            setSlotPickerRole(role || null);
            setSlotPickerTarget(slotType === 'bench' ? 'bench' : 'regular');
            setSlotPickerSearch('');
            setSlotPickerOpen(true);
          }
        }}
        sx={{
          pt: { xs: '5px', sm: '6px', md: '8px' },
          pb: { xs: '5px', sm: '6px', md: '8px' },
          px: { xs: '6px', sm: '8px', md: '10px' },
          minHeight: { xs: 82, sm: 90, md: 100 },
          width: '100%',
          maxWidth: { xs: 116, sm: 140, md: 162 },
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center',
          border: (() => {
            if (!player) return `1.5px dashed ${colors.border}`;
            if (isCaptain) return `1.5px solid ${alpha(themeColors.gold, 0.55)}`;
            if (isViceCaptain) return `1.5px solid rgba(156,39,176,0.55)`;
            if (isXFactor) return `1.5px solid rgba(0,188,212,0.55)`;
            return `1px solid ${alpha('#1E88E5', 0.20)}`;
          })(),
          borderRadius: { xs: 2, sm: 3 },
          background: player ? `linear-gradient(145deg, ${alpha('#1A3A5C', 0.95)}, ${alpha('#060D17', 0.98)})` : colors.bg,
          backdropFilter: player ? 'blur(10px)' : 'none',
          position: 'relative',
          cursor: 'pointer',
          overflow: 'visible',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: (() => {
            const base = '0 6px 18px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.08)';
            if (!player) return 'none';
            if (isCaptain) return `${base}, 0 0 0 2px ${alpha(themeColors.gold, 0.2)}, 0 0 14px ${alpha(themeColors.gold, 0.18)}`;
            if (isViceCaptain) return `${base}, 0 0 0 2px rgba(156,39,176,0.2), 0 0 14px rgba(156,39,176,0.18)`;
            if (isXFactor) return `${base}, 0 0 0 2px rgba(0,188,212,0.2), 0 0 14px rgba(0,188,212,0.18)`;
            return `${base}, 0 0 0 1px ${alpha('#1E88E5', 0.15)}, 0 0 14px ${alpha('#1E88E5', 0.10)}`;
          })(),
          '&:hover': {
            background: player ? `linear-gradient(145deg, ${alpha('#102844', 0.97)}, ${alpha('#0A1929', 0.99)})` : colors.bg,
            transform: player ? 'translateY(-6px) scale(1.03)' : 'translateY(-2px)',
            boxShadow: player
              ? '0 14px 32px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.10)'
              : `0 8px 16px ${colors.border}55`,
          }
        }}
      >
        {player ? (
          <>
            {shouldShowRemoveButton(readOnly, !!existingSquad?.isSubmitted) && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemovePlayer(player.id);
                }}
                sx={{
                  position: 'absolute',
                  top: { xs: -6, sm: -8 },
                  right: { xs: -6, sm: -8 },
                  bgcolor: 'error.main',
                  color: 'white',
                  '&:hover': { bgcolor: 'error.dark' },
                  zIndex: 10,
                  width: { xs: 20, sm: 24 },
                  height: { xs: 20, sm: 24 },
                  opacity: hovered ? 1 : 0,
                  pointerEvents: hovered ? 'auto' : 'none',
                  transition: 'opacity 0.15s ease',
                  '@media (hover: none)': {
                    opacity: 1,
                    pointerEvents: 'auto',
                  },
                }}
              >
                <Close sx={{ fontSize: { xs: 14, sm: 18 } }} />
              </IconButton>
            )}

            {/* Card content — 3 sections with dividers */}
            {(() => {
              const lastMatchPts = getLastMatchPoints(player);
              const hasLastMatch = getLastMatchLabel(player) !== null;
              const career = getCareerStats(player);
              const roleStats = getRoleStats(player.role, career);
              const divider = <Box sx={{ width: '100%', height: '1px', bgcolor: 'rgba(255,255,255,0.07)', flexShrink: 0 }} />;
              const lastMatchColor = !hasLastMatch ? 'rgba(255,255,255,0.2)' : lastMatchPts >= 0 ? '#00FF87' : '#FF5252';
              const lastMatchGlow = !hasLastMatch ? 'none' : lastMatchPts >= 0 ? '0 0 5px rgba(0,255,135,0.3)' : '0 0 5px rgba(255,82,82,0.3)';
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '3px' }}>

                  {/* SECTION 1: Identity — logo + flag + last name + role */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, width: '100%' }}>
                    <TeamLogo team={player.team} size={18} />
                    {cricketFlagEmoji(player.team) && (
                      <Box component="span" sx={{
                        fontSize: '0.875rem',
                        lineHeight: 1,
                        borderRadius: '3px',
                        overflow: 'hidden',
                        flexShrink: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                      }}>
                        {cricketFlagEmoji(player.team)}
                      </Box>
                    )}
                    <Typography noWrap sx={{
                      fontFamily: "'Satoshi', sans-serif", fontWeight: 700,
                      fontSize: { xs: '0.625rem', sm: '0.6875rem' },
                      lineHeight: 1.2, letterSpacing: '0.07em', textTransform: 'uppercase',
                      flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {player.name.trim().split(' ').at(-1)}
                    </Typography>
                    <Typography sx={{ fontSize: '0.5rem', fontWeight: 600, letterSpacing: '0.06em', color: 'rgba(255,255,255,0.3)', flexShrink: 0, lineHeight: 1 }}>
                      {getRoleBadge(player.role).label}
                    </Typography>
                  </Box>

                  {divider}

                  {/* SECTION 2: Total pts (left) + last match delta (right) */}
                  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', width: '100%' }}>
                    {/* Left — total points */}
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '2px' }}>
                      <Typography sx={{
                        fontFamily: "'Bebas Neue', monospace",
                        fontSize: { xs: '1rem', sm: '1.0625rem' },
                        lineHeight: 1, letterSpacing: '0.06em', color: '#fff',
                      }}>
                        {(player.stats?.[(league?.format ?? 'T20') as 'T20' | 'ODI' | 'Test']?.recentForm ?? 0).toFixed(1)}
                      </Typography>
                      <Typography sx={{ fontSize: '0.48rem', opacity: 0.45, color: '#42A5F5', letterSpacing: '0.07em', textTransform: 'uppercase', lineHeight: 1 }}>
                        pts
                      </Typography>
                    </Box>
                    {/* Right — last match delta */}
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '2px' }}>
                      <Typography sx={{
                        fontFamily: "'Bebas Neue', monospace",
                        fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                        lineHeight: 1, letterSpacing: '0.05em',
                        color: lastMatchColor, textShadow: lastMatchGlow,
                      }}>
                        {hasLastMatch
                          ? (lastMatchPts >= 0 ? `+${lastMatchPts.toFixed(2)}` : lastMatchPts.toFixed(2))
                          : '—'}
                      </Typography>
                    </Box>
                  </Box>

                  {divider}

                  {/* SECTION 3: Role-adaptive stats — value over label, stacked */}
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-around', width: '100%' }}>
                    {roleStats.map(stat => (
                      <Box key={stat.label} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}>
                        <Typography sx={{
                          fontFamily: "'Bebas Neue', monospace",
                          fontSize: { xs: '0.625rem', sm: '0.6875rem' },
                          letterSpacing: '0.04em', lineHeight: 1, color: 'rgba(255,255,255,0.82)',
                        }}>
                          {stat.value}
                        </Typography>
                        <Typography sx={{
                          fontFamily: "'Satoshi', sans-serif",
                          fontSize: '0.44rem', letterSpacing: '0.07em', textTransform: 'uppercase',
                          lineHeight: 1, color: 'rgba(255,255,255,0.38)',
                        }}>
                          {stat.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>

                </Box>
              );
            })()}

            {/* Captain/Vice Captain/X-Factor badges with special glows */}
            {isCaptain && (
              <Box sx={{
                position: 'absolute',
                top: { xs: -6, sm: -7 },
                left: { xs: -6, sm: -7 },
                zIndex: 5,
                width: { xs: 18, sm: 20 },
                height: { xs: 18, sm: 20 },
                borderRadius: '50%',
                bgcolor: '#090b47',
                border: `1.5px solid ${alpha(themeColors.gold, 0.8)}`,
                boxShadow: `0 0 8px ${alpha(themeColors.gold, 0.7)}, 0 0 16px ${alpha(themeColors.gold, 0.35)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'pulse-gold-c 2.5s ease-in-out infinite',
                '@keyframes pulse-gold-c': {
                  '0%, 100%': { boxShadow: `0 0 8px ${alpha(themeColors.gold, 0.7)}, 0 0 16px ${alpha(themeColors.gold, 0.35)}` },
                  '50%': { boxShadow: `0 0 14px ${alpha(themeColors.gold, 1)}, 0 0 26px ${alpha(themeColors.gold, 0.55)}` },
                },
              }}>
                <svg viewBox="0 0 24 24" width="10" height="10" fill={themeColors.gold}>
                  <path d="M5 16L2 6l5.5 4L12 3l4.5 7L22 6l-3 10H5z" />
                </svg>
              </Box>
            )}
            {isViceCaptain && (
              <Box sx={{
                position: 'absolute',
                top: { xs: -6, sm: -7 },
                left: { xs: -6, sm: -7 },
                zIndex: 5,
                width: { xs: 18, sm: 20 },
                height: { xs: 18, sm: 20 },
                borderRadius: '50%',
                bgcolor: '#090b47',
                border: '1.5px solid rgba(156,39,176,0.8)',
                boxShadow: '0 0 6px rgba(156,39,176,0.5), 0 0 12px rgba(156,39,176,0.25)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                animation: 'pulse-purple-vc 3s ease-in-out infinite',
                '@keyframes pulse-purple-vc': {
                  '0%, 100%': { boxShadow: '0 0 6px rgba(156,39,176,0.5), 0 0 12px rgba(156,39,176,0.25)' },
                  '50%': { boxShadow: '0 0 10px rgba(156,39,176,0.75), 0 0 20px rgba(156,39,176,0.4)' },
                },
              }}>
                <Star sx={{ fontSize: { xs: 10, sm: 11 }, color: 'rgba(156,39,176,0.95)' }} />
              </Box>
            )}
            {isXFactor && (
              <Box sx={{ position: 'absolute', top: { xs: -4, sm: -5 }, left: { xs: -4, sm: -5 }, zIndex: 5 }}>
                <Chip
                  label="X"
                  size="small"
                  sx={{
                    fontSize: { xs: '0.6rem', sm: '0.7rem' },
                    height: { xs: 16, sm: 18 },
                    fontWeight: 'bold',
                    bgcolor: '#090b47',
                    color: '#00BCD4',
                    boxShadow: '0 0 8px rgba(0,188,212,0.6), 0 0 14px rgba(0,188,212,0.3)',
                    animation: 'pulse-cyan-x 3.5s ease-in-out infinite',
                    '@keyframes pulse-cyan-x': {
                      '0%, 100%': { boxShadow: '0 0 8px rgba(0,188,212,0.6), 0 0 14px rgba(0,188,212,0.3)' },
                      '50%': { boxShadow: '0 0 11px rgba(0,188,212,0.85), 0 0 20px rgba(0,188,212,0.45)' }
                    }
                  }}
                />
              </Box>
            )}

            {/* Hover menu for assigning roles */}
            {canAssignRoles && showRoleMenu && (
              <Box
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                sx={{
                  position: 'absolute',
                  bottom: { xs: 'auto', sm: -40 },
                  top: { xs: -48, sm: 'auto' },
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  gap: 0.5,
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  p: 0.5,
                  boxShadow: 3,
                  zIndex: 100,
                  border: '1px solid',
                  borderColor: 'divider'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSetSpecialRole(player.id, 'captain');
                  }}
                  sx={{
                    bgcolor: isCaptain ? 'warning.main' : 'action.hover',
                    color: isCaptain ? 'warning.contrastText' : 'text.primary',
                    '&:hover': { bgcolor: isCaptain ? 'warning.dark' : 'warning.light' },
                    width: 28,
                    height: 28
                  }}
                >
                  <Typography variant="caption" fontWeight="bold">C</Typography>
                </IconButton>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSetSpecialRole(player.id, 'vice_captain');
                  }}
                  sx={{
                    bgcolor: isViceCaptain ? '#7B1FA2' : 'action.hover', // Purple for premium
                    color: isViceCaptain ? 'white' : 'text.primary',
                    '&:hover': { bgcolor: isViceCaptain ? '#6A1B9A' : 'rgba(123, 31, 162, 0.1)' },
                    width: 28,
                    height: 28
                  }}
                >
                  <Typography variant="caption" fontWeight="bold" fontSize="0.65rem">VC</Typography>
                </IconButton>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSetSpecialRole(player.id, 'x_factor');
                  }}
                  sx={{
                    bgcolor: isXFactor ? 'info.main' : 'action.hover',
                    color: isXFactor ? 'white' : 'text.primary',
                    '&:hover': { bgcolor: isXFactor ? 'info.dark' : 'rgba(2, 136, 209, 0.1)' },
                    width: 28,
                    height: 28
                  }}
                >
                  <Typography variant="caption" fontWeight="bold">X</Typography>
                </IconButton>
              </Box>
            )}
          </>
        ) : (
          <>
            <Box sx={{
              width: { xs: 34, sm: 38 },
              height: { xs: 34, sm: 38 },
              borderRadius: '50%',
              border: `1.5px dashed ${colors.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 0.75,
              opacity: 0.6,
            }}>
              <Typography sx={{ fontSize: '1.1rem', color: colors.border, lineHeight: 1, mt: '-1px' }}>+</Typography>
            </Box>
            <Typography variant="caption" textAlign="center" sx={{
              fontFamily: "'Satoshi', sans-serif",
              fontWeight: 500,
              fontSize: '0.625rem',
              color: colors.border,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              opacity: 0.7,
            }}>
              {role ? getRoleDisplayText(role) : `Player ${position + 1}`}
            </Typography>
          </>
        )}
      </Paper>
    );
  };

  const positionCounts = {
    batsman: selectedPlayers.filter(p => p.role === 'batsman' && p.position !== 'bench').length,
    bowler: selectedPlayers.filter(p => p.role === 'bowler' && p.position !== 'bench').length,
    wicketkeeper: selectedPlayers.filter(p => p.role === 'wicketkeeper' && p.position !== 'bench').length,
  };

  return (
    <Card>
      <CardContent sx={{ px: { xs: 1, sm: 1.5, md: 2, lg: 3 }, py: { xs: 1, sm: 1.5, md: 2, lg: 2.5 } }}>
        {/* Powerplay Selection */}
        {league.powerplayEnabled && (
          <Box sx={{ mb: { xs: 1.5, sm: 2, md: 3 } }}>
            <Typography variant="h6" gutterBottom sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 600, fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' }, letterSpacing: '0.01em', display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <BoltIcon fontSize="small" />Powerplay Match Selection
            </Typography>

            {/* Activation Mode */}
            {(league.ppMatchMode ?? 'fixed') === 'activation' ? (
              <Box>
                {!ppActivatedAt && !existingSquad?.ppActivatedAt ? (
                  /* Not yet activated */
                  <>
                  <Box sx={{ p: 1.5, bgcolor: 'rgba(255,193,7,0.06)', border: '1px solid rgba(255,193,7,0.2)', borderRadius: 1.5 }}>
                    <Typography variant="body2" color="warning.main" sx={{ fontWeight: 800, lineHeight: 1.3, mb: 0.5 }}>
                      Score 2x points for a match you select.
                    </Typography>
                    <Collapse in={ppDescOpen}>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6, display: 'block', mb: 0.5 }}>
                        You can use this anytime during the league, just like transfers. After activation, pick any upcoming match as your Powerplay match. Only matches scheduled after your activation time will appear. This is a one-time action and cannot be undone once activated.
                      </Typography>
                      {isDeadlinePassed && !ppActivationEnabled && (
                        <Typography variant="caption" color="error.main" sx={{ mt: 0.5 }}>
                          The admin needs to enable PP activation before you can proceed.
                        </Typography>
                      )}
                    </Collapse>
                    <Link component="button" variant="caption" onClick={() => setPpDescOpen((o: boolean) => !o)} sx={{ color: 'warning.main', opacity: 0.75, fontWeight: 600, cursor: 'pointer' }}>
                      {ppDescOpen ? 'Show less' : 'Read more'}
                    </Link>
                  </Box>

                    {/* Pre-deadline: optional pre-selection */}
                    {!isDeadlinePassed && (
                      <Box sx={{ mt: 1.5 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block' }}>
                          Pre-select your PP match (optional — you can change this before the deadline, or activate a new one once the league starts).
                        </Typography>
                        <FormControl fullWidth size="small">
                          <InputLabel>Select Powerplay Match</InputLabel>
                          <Select
                            value={ppMatchPending}
                            label="Select Powerplay Match"
                            onChange={(e) => setPpMatchPending(e.target.value)}
                            sx={{ bgcolor: '#1a2332' }}
                          >
                            {(league.matchSchedule || []).map((match) => (
                              <MenuItem key={match.matchNumber} value={match.matchNumber.toString()} sx={{ py: 1.5 }}>
                                <Typography variant="body2" fontWeight={600} fontSize="0.875rem">
                                  {formatMatchForDropdown(match)}
                                </Typography>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {ppMatchPending && (
                          <Button
                            variant="contained"
                            color="warning"
                            size="small"
                            sx={{ mt: 1 }}
                            onClick={() => setPpMatchConfirmOpen(true)}
                          >
                            Pre-select this match
                          </Button>
                        )}
                      </Box>
                    )}

                    {/* 2-step PP Activation Dialog */}
                    <Dialog open={ppDialogOpen} onClose={() => ppDialogStep !== 'success' && setPpDialogOpen(false)} maxWidth="xs" fullWidth>
                      {ppDialogStep === 'select' && (
                        <>
                          <DialogTitle sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 0.75 }}><BoltIcon fontSize="small" />Activate Your Powerplay!</DialogTitle>
                          <DialogContent>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              This is a one-time power. Once activated, your double-points match is permanently locked. Only future matches will be available.
                            </Typography>
                            <FormControl fullWidth size="small">
                              <InputLabel>Select Powerplay Match</InputLabel>
                              <Select
                                value={ppMatchPending}
                                label="Select Powerplay Match"
                                onChange={(e) => setPpMatchPending(e.target.value)}
                                sx={{ bgcolor: '#1a2332' }}
                              >
                                {(league.matchSchedule || []).map((match) => (
                                  <MenuItem key={match.matchNumber} value={match.matchNumber.toString()} sx={{ py: 1.5 }}>
                                    <Box>
                                      <Typography variant="body2" fontWeight={600} fontSize="0.875rem">
                                        {formatMatchForDropdown(match)}
                                      </Typography>
                                      {match.team1 !== 'TBC' && match.team2 !== 'TBC' && (
                                        <Typography variant="caption" color="text.secondary" fontSize="0.75rem">
                                          {match.timeGMT} GMT • {match.stadium}
                                        </Typography>
                                      )}
                                    </Box>
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </DialogContent>
                          <DialogActions>
                            <Button onClick={() => setPpDialogOpen(false)} color="inherit">Cancel</Button>
                            <Button
                              variant="contained"
                              disabled={!ppMatchPending}
                              onClick={handlePpDialogNext}
                              sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, letterSpacing: '0.03em', background: 'linear-gradient(135deg, #FFD700, #FF8C00)', color: '#000' }}
                            >
                              Next →
                            </Button>
                          </DialogActions>
                        </>
                      )}
                      {ppDialogStep === 'confirm' && (
                        <>
                          <DialogTitle sx={{ fontWeight: 'bold' }}>Confirm Powerplay Match</DialogTitle>
                          <DialogContent>
                            <Typography variant="body2" sx={{ mb: 1.5 }}>
                              You are about to activate your Powerplay for:
                            </Typography>
                            <Box sx={{ p: 1.5, bgcolor: 'rgba(255,193,7,0.1)', border: '1px solid rgba(255,193,7,0.3)', borderRadius: 1.5, mb: 1.5 }}>
                              <Typography variant="body1" fontWeight="bold" color="warning.main">
                                {ppMatchPending && league.matchSchedule
                                  ? formatMatchForDropdown(league.matchSchedule.find(m => m.matchNumber.toString() === ppMatchPending)!)
                                  : `Match ${ppMatchPending}`}
                              </Typography>
                            </Box>
                            <Alert severity="error" sx={{ fontSize: '0.8rem' }}>
                              This cannot be undone. Once confirmed, your Powerplay is permanently activated.
                            </Alert>
                          </DialogContent>
                          <DialogActions>
                            <Button onClick={() => setPpDialogStep('select')} color="inherit">← Back</Button>
                            <Button variant="contained" color="warning" onClick={handlePpDialogConfirm}>
                              Confirm & Activate
                            </Button>
                          </DialogActions>
                        </>
                      )}
                      {ppDialogStep === 'success' && (
                        <>
                          <DialogTitle sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                            <CheckCircle sx={{ fontSize: 48, color: 'success.main', display: 'block', mx: 'auto', mb: 1 }} />
                            Powerplay Activated!
                          </DialogTitle>
                          <DialogContent>
                            <Typography variant="body2" textAlign="center" color="text.secondary">
                              Your Powerplay match is locked:
                            </Typography>
                            <Box sx={{ p: 1.5, mt: 1, bgcolor: 'rgba(76,175,80,0.1)', border: '1px solid rgba(76,175,80,0.3)', borderRadius: 1.5, textAlign: 'center' }}>
                              <Typography variant="body1" fontWeight="bold" color="success.main">
                                {ppMatchPending && league.matchSchedule
                                  ? formatMatchForDropdown(league.matchSchedule.find(m => m.matchNumber.toString() === ppMatchPending)!)
                                  : `Match ${ppMatchPending}`}
                              </Typography>
                            </Box>
                          </DialogContent>
                          <DialogActions sx={{ justifyContent: 'center' }}>
                            <Button variant="contained" color="success" onClick={() => setPpDialogOpen(false)}>
                              Done
                            </Button>
                          </DialogActions>
                        </>
                      )}
                    </Dialog>
                  </>
                ) : (
                  /* Activated — show dropdown filtered to future matches */
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Alert severity="success" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      Activated on {new Date(ppActivatedAt || existingSquad?.ppActivatedAt || '').toLocaleString()}. Pick any upcoming match below.
                    </Alert>
                    {powerplayMatch && (
                      <Alert severity="info" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        Powerplay match locked: <strong>Match {powerplayMatch}</strong>. This cannot be changed.
                      </Alert>
                    )}
                    {!powerplayMatch && (() => {
                      const activatedTime = ppActivatedAt || (existingSquad?.ppActivatedAt ? new Date(existingSquad.ppActivatedAt) : null);
                      const futureMatches = league.matchSchedule
                        ? league.matchSchedule.filter(m => activatedTime ? new Date(m.date) > activatedTime : true)
                        : [];
                      if (futureMatches.length === 0) {
                        return <Alert severity="warning">No upcoming matches available for Powerplay at this time.</Alert>;
                      }
                      return (
                        <>
                          <FormControl fullWidth>
                            <InputLabel sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' } }}>Select Powerplay Match</InputLabel>
                            <Select
                              value={ppMatchPending}
                              label="Select Powerplay Match"
                              onChange={(e) => setPpMatchPending(e.target.value)}
                              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' }, bgcolor: '#1a2332' }}
                            >
                              {futureMatches.map((match) => (
                                <MenuItem key={match.matchNumber} value={match.matchNumber.toString()} sx={{ py: 1.5 }}>
                                  <Box>
                                    <Typography variant="body2" fontWeight={600} fontSize="0.875rem">
                                      {formatMatchForDropdown(match)}
                                    </Typography>
                                    {match.team1 !== 'TBC' && match.team2 !== 'TBC' && (
                                      <Typography variant="caption" color="text.secondary" fontSize="0.75rem">
                                        {match.timeGMT} GMT • {match.stadium}
                                      </Typography>
                                    )}
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {ppMatchPending && (
                            <Button
                              variant="contained"
                              color="warning"
                              sx={{ alignSelf: 'flex-start' }}
                              onClick={() => setPpMatchConfirmOpen(true)}
                            >
                              Confirm Powerplay Match
                            </Button>
                          )}
                          {/* Confirmation Dialog */}
                          <Dialog open={ppMatchConfirmOpen} onClose={() => setPpMatchConfirmOpen(false)} maxWidth="xs" fullWidth>
                            <DialogTitle sx={{ fontWeight: 'bold' }}>Confirm Powerplay Match</DialogTitle>
                            <DialogContent>
                              <Typography variant="body2" sx={{ mb: 1.5 }}>
                                You are about to lock your Powerplay match as:
                              </Typography>
                              <Box sx={{ p: 1.5, bgcolor: 'rgba(255,193,7,0.1)', border: '1px solid rgba(255,193,7,0.3)', borderRadius: 1.5, mb: 1.5 }}>
                                <Typography variant="body1" fontWeight="bold" color="warning.main">
                                  {ppMatchPending && league.matchSchedule
                                    ? formatMatchForDropdown(league.matchSchedule.find(m => m.matchNumber.toString() === ppMatchPending)!)
                                    : `Match ${ppMatchPending}`}
                                </Typography>
                              </Box>
                              <Alert severity="error" sx={{ fontSize: '0.8rem' }}>
                                This cannot be undone. Once confirmed, your Powerplay match is permanently locked.
                              </Alert>
                            </DialogContent>
                            <DialogActions>
                              <Button onClick={() => setPpMatchConfirmOpen(false)} color="inherit">Cancel</Button>
                              <Button variant="contained" color="warning" onClick={onConfirmPpMatch}>
                                Yes, Lock This Match
                              </Button>
                            </DialogActions>
                          </Dialog>
                        </>
                      );
                    })()}
                  </Box>
                )}
              </Box>
            ) : (
              /* Fixed Mode — existing behavior */
              <FormControl fullWidth>
                <InputLabel sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' } }}>Select Powerplay Match</InputLabel>
                <Select
                  value={powerplayMatch}
                  label="Select Powerplay Match"
                  onChange={(e) => setPowerplayMatch(e.target.value)}
                  disabled={isDeadlinePassed}
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' },
                    bgcolor: '#1a2332',
                    '& .MuiSelect-select': { py: 1.5 }
                  }}
                >
                  {league.matchSchedule && league.matchSchedule.length > 0 ? (
                    league.matchSchedule
                      .filter(match => match.matchNumber <= maxPowerplayMatches)
                      .map((match) => (
                        <MenuItem
                          key={match.matchNumber}
                          value={match.matchNumber.toString()}
                          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' }, py: 1.5, '&:hover': { bgcolor: alpha(themeColors.blue.electric, 0.08) } }}
                        >
                          <Box>
                            <Typography variant="body2" fontWeight={600} fontSize="0.875rem">
                              {formatMatchForDropdown(match)}
                            </Typography>
                            {match.team1 !== 'TBC' && match.team2 !== 'TBC' && (
                              <Typography variant="caption" color="text.secondary" fontSize="0.75rem">
                                {match.timeGMT} GMT • {match.stadium}
                              </Typography>
                            )}
                          </Box>
                        </MenuItem>
                      ))
                  ) : (
                    Array.from({ length: maxPowerplayMatches }, (_, i) => i + 1).map((matchNumber) => (
                      <MenuItem key={matchNumber} value={matchNumber.toString()} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' } }}>
                        Match {matchNumber}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            )}
          </Box>
        )}

        {/* Cricket Field Background */}
        <Box
          sx={{
            background: 'radial-gradient(ellipse 80% 65% at 50% 42%, #1e5c36 0%, #174d28 52%, #0e3318 100%)',
            borderRadius: { xs: 1.5, sm: 2, md: 3 },
            position: 'relative',
            overflow: 'visible',
            boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
            minHeight: { xs: 500, sm: 600, md: 700, lg: 800 },
            pb: { xs: 1.5, sm: 2, md: 3 },
            // Subtle light-blue sky reflection overlay
            backgroundImage: 'radial-gradient(ellipse 70% 55% at 50% 38%, rgba(180,220,255,0.07) 0%, transparent 75%)',
            // Outer boundary rope — much subtler
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '40px',
              left: '5%',
              right: '5%',
              bottom: '40px',
              borderRadius: '50%',
              border: '2.5px solid rgba(57,255,20,0.55)',
              boxShadow: '0 0 6px 1px rgba(57,255,20,0.3), 0 0 14px 3px rgba(57,255,20,0.12)',
              zIndex: 0,
              pointerEvents: 'none',
            },
            // Inner 30-yard circle
            '&::after': {
              content: '""',
              position: 'absolute',
              top: '15%',
              left: '20%',
              right: '20%',
              bottom: '15%',
              borderRadius: '50%',
              border: '1.5px dashed rgba(255,255,255,0.18)',
              zIndex: 0,
              pointerEvents: 'none',
            }
          }}
        >
          {/* Cricket Pitch */}
          <Box sx={{
            position: 'absolute',
            top: '120px',
            left: '44%',
            right: '44%',
            bottom: '120px',
            background: 'linear-gradient(180deg, #C8A96E 0%, #B8975A 50%, #C8A96E 100%)',
            borderRadius: '8px',
            border: '2px solid rgba(180, 140, 80, 0.6)',
            opacity: 0.75,
            boxShadow: 'inset 0 4px 12px rgba(0,0,0,0.3)',
            zIndex: 0,
          }} />
          {/* Batting End - Top with crease and stumps */}
          <Box sx={{
            position: 'absolute',
            top: '125px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px'
          }}>
            {/* Batting Crease */}
            <Box sx={{
              width: '120px',
              height: '5px',
              bgcolor: '#FFFFFF',
              borderRadius: '2px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
              border: '1px solid #DDD'
            }} />
            {/* Stumps */}
            <Box sx={{ display: 'flex', gap: '6px', mt: '4px' }}>
              {[1,2,3].map(i => (
                <Box key={i} sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: '#8B4513',
                  border: '2px solid #5D3A1A',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.4)'
                }} />
              ))}
            </Box>
          </Box>

          {/* Bowling End - Bottom with stumps and crease */}
          <Box sx={{
            position: 'absolute',
            bottom: '125px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px'
          }}>
            {/* Stumps */}
            <Box sx={{ display: 'flex', gap: '6px' }}>
              {[1,2,3].map(i => (
                <Box key={i} sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: '#8B4513',
                  border: '2px solid #5D3A1A',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.4)'
                }} />
              ))}
            </Box>
            {/* Bowling Crease */}
            <Box sx={{
              width: '120px',
              height: '5px',
              bgcolor: '#FFFFFF',
              borderRadius: '2px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
              border: '1px solid #DDD',
              mt: '4px'
            }} />
          </Box>

          <Typography textAlign="center" pt={{ xs: 1, sm: 1.5, md: 2 }} pb={{ xs: 0.5, sm: 0.75 }} sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, letterSpacing: '0.08em', color: 'rgba(255,255,255,0.7)', fontSize: { xs: '0.6875rem', sm: '0.75rem', md: '0.8125rem' }, textTransform: 'uppercase', position: 'relative', zIndex: 2 }}>
            Squad
          </Typography>

          {/* Cricket Field Formation Layout */}
          <Box sx={{ zIndex: 2, position: 'relative', px: { xs: 1, sm: 2, md: 3 } }}>

            {/* Wicket-keepers - ABOVE the batting end stumps and crease */}
            {league.squadRules.minWicketkeepers > 0 && (
              <Box sx={{
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '2px',
                  background: 'rgba(255, 255, 255, 0.3)',
                  zIndex: 1
                }
              }}>
                <Box sx={{ textAlign: 'center', py: { xs: 1, sm: 1.5, md: 2 } }}>
                  <Box sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    bgcolor: 'rgba(255,255,255,0.92)',
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 0.4, sm: 0.6 },
                    borderRadius: '20px',
                    mb: { xs: 1, sm: 1.5, md: 2 },
                    boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                  }}>
                    <Typography sx={{ color: '#0f2412', fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.75rem' }, letterSpacing: '0.08em' }}>
                      KEEPERS
                    </Typography>
                    {positionCounts.wicketkeeper >= league.squadRules.minWicketkeepers ? (
                      <Typography sx={{ color: '#1a7a2e', fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>✓</Typography>
                    ) : (
                      <Typography sx={{ color: '#0f2412', opacity: 0.55, fontSize: { xs: '0.65rem', sm: '0.7rem' }, fontWeight: 500 }}>
                        · {league.squadRules.minWicketkeepers - positionCounts.wicketkeeper} needed
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5, md: 2 }, justifyContent: 'center', flexWrap: 'wrap', pb: { xs: 1, sm: 1.5, md: 2 } }}>
                    {requiredSlots.wicketkeepers.map((_, index) => (
                      <PlayerSlot
                        key={`wicketkeeper-${index}`}
                        player={playersByRole.wicketkeeper[index]}
                        role="wicketkeeper"
                        slotType="required"
                        position={index}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
            )}

            {/* Batters - Near the batting end */}
            <Box sx={{
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '2px',
                background: 'rgba(255, 255, 255, 0.3)',
                zIndex: 1
              }
            }}>
              <Box sx={{ textAlign: 'center', py: { xs: 1, sm: 1.5, md: 2 }, pt: { xs: 1.5, sm: 2, md: 3 } }}>
                <Box sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  bgcolor: 'rgba(255,255,255,0.92)',
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 0.4, sm: 0.6 },
                  borderRadius: '20px',
                  mb: { xs: 1, sm: 1.5, md: 2 },
                  boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                }}>
                  <Typography sx={{ color: '#0f2412', fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.75rem' }, letterSpacing: '0.08em' }}>
                    BATTERS
                  </Typography>
                  {positionCounts.batsman >= league.squadRules.minBatsmen ? (
                    <Typography sx={{ color: '#1a7a2e', fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>✓</Typography>
                  ) : (
                    <Typography sx={{ color: '#0f2412', opacity: 0.55, fontSize: { xs: '0.65rem', sm: '0.7rem' }, fontWeight: 500 }}>
                      · {league.squadRules.minBatsmen - positionCounts.batsman} needed
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5, md: 2 }, justifyContent: 'center', flexWrap: 'wrap', pb: { xs: 1, sm: 1.5, md: 2 } }}>
                  {requiredSlots.batsmen.map((_, index) => (
                    <PlayerSlot
                      key={`batsman-${index}`}
                      player={playersByRole.batsman[index]}
                      role="batsman"
                      slotType="required"
                      position={index}
                    />
                  ))}
                </Box>
              </Box>
            </Box>

            {/* Bowlers - Near the bowling end */}
            <Box sx={{
              position: 'relative',
              '&::after': requiredSlots.flexible.length > 0 ? {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '2px',
                background: 'rgba(255, 255, 255, 0.3)',
                zIndex: 1
              } : {}
            }}>
              <Box sx={{ textAlign: 'center', py: { xs: 1, sm: 1.5, md: 2 } }}>
                <Box sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  bgcolor: 'rgba(255,255,255,0.92)',
                  px: { xs: 1.5, sm: 2 },
                  py: { xs: 0.4, sm: 0.6 },
                  borderRadius: '20px',
                  mb: { xs: 1, sm: 1.5, md: 2 },
                  boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                }}>
                  <Typography sx={{ color: '#0f2412', fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.75rem' }, letterSpacing: '0.08em' }}>
                    BOWLERS
                  </Typography>
                  {positionCounts.bowler >= league.squadRules.minBowlers ? (
                    <Typography sx={{ color: '#1a7a2e', fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>✓</Typography>
                  ) : (
                    <Typography sx={{ color: '#0f2412', opacity: 0.55, fontSize: { xs: '0.65rem', sm: '0.7rem' }, fontWeight: 500 }}>
                      · {league.squadRules.minBowlers - positionCounts.bowler} needed
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5, md: 2 }, justifyContent: 'center', flexWrap: 'wrap', pb: { xs: 1, sm: 1.5, md: 2 } }}>
                  {requiredSlots.bowlers.map((_, index) => (
                    <PlayerSlot
                      key={`bowler-${index}`}
                      player={playersByRole.bowler[index]}
                      role="bowler"
                      slotType="required"
                      position={index}
                    />
                  ))}
                </Box>
              </Box>
            </Box>

            {/* Flexible Slots - Inside field, below pitch */}
            {requiredSlots.flexible.length > 0 && (
              <Box sx={{ py: { xs: 1.5, sm: 2, md: 3 }, pb: { xs: 2, sm: 3, md: 4 } }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Box sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    bgcolor: 'rgba(255,255,255,0.92)',
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 0.4, sm: 0.6 },
                    borderRadius: '20px',
                    mb: { xs: 1, sm: 1.5, md: 2 },
                    boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
                  }}>
                    <Typography sx={{ color: '#0f2412', fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.75rem' }, letterSpacing: '0.08em' }}>
                      FLEX
                    </Typography>
                    {flexiblePlayersList.filter(Boolean).length >= requiredSlots.flexible.length ? (
                      <Typography sx={{ color: '#1a7a2e', fontWeight: 700, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>✓</Typography>
                    ) : (
                      <Typography sx={{ color: '#0f2412', opacity: 0.55, fontSize: { xs: '0.65rem', sm: '0.7rem' }, fontWeight: 500 }}>
                        · {requiredSlots.flexible.length - flexiblePlayersList.filter(Boolean).length} needed
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: { xs: 1, sm: 1.5, md: 2 }, justifyContent: 'center', flexWrap: 'wrap' }}>
                    {requiredSlots.flexible.map((_, index) => (
                      <PlayerSlot
                        key={`flexible-${index}`}
                        player={flexiblePlayersList[index]}
                        slotType="flexible"
                        position={index}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* Outside Field - Bench Slots */}
        {requiredSlots.bench.length > 0 && (
          <Box sx={{ mt: { xs: 2, sm: 2.5, md: 3 } }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, letterSpacing: '0.08em', color: 'rgba(255,255,255,0.7)', fontSize: { xs: '0.6875rem', sm: '0.75rem', md: '0.8125rem' }, textTransform: 'uppercase' }}>
                Bench Players
              </Typography>
              <Box sx={{
                display: 'flex',
                gap: { xs: 1, sm: 1.5, md: 2 },
                justifyContent: 'center',
                flexWrap: 'wrap',
                p: { xs: 1.5, sm: 2, md: 3 },
                bgcolor: 'rgba(255,255,255,0.12)',
                borderRadius: { xs: 2, sm: 2.5, md: 3 },
                border: '1px solid rgba(255,255,255,0.18)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.35)'
              }}>
                {requiredSlots.bench.map((_, index) => (
                  <PlayerSlot
                    key={`bench-${index}`}
                    player={benchPlayers[index]}
                    slotType="bench"
                    position={index}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        )}

        {/* 12th Hidden Player — placed below squad formation */}
        {league.hiddenPlayerEnabled && (
          <Box sx={{ mt: { xs: 2, sm: 2.5, md: 3 }, p: { xs: 1.5, sm: 2 }, border: '1px solid rgba(255,255,255,0.18)', borderRadius: 2, bgcolor: 'rgba(255,255,255,0.055)', boxShadow: '0 0 0 1px rgba(255,193,7,0.12)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="h6" sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' }, fontWeight: 600, letterSpacing: '0.01em', display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <LockIcon fontSize="small" />12th Hidden Player
              </Typography>
              <Chip
                icon={<LockIcon sx={{ fontSize: '0.75rem !important' }} />}
                label="Secret Pick"
                size="small"
                sx={{
                  fontFamily: "'Satoshi', sans-serif",
                  fontWeight: 500,
                  fontSize: '0.625rem',
                  height: 20,
                  bgcolor: 'rgba(255,193,7,0.12)',
                  color: 'warning.main',
                  border: '1px solid rgba(255,193,7,0.35)',
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                }}
              />
            </Box>

            <Box sx={{ p: 1.5, mb: 1.5, bgcolor: 'rgba(255,255,255,0.07)', borderRadius: 1.5, border: '1px solid rgba(255,255,255,0.12)', borderLeft: '3px solid rgba(255,193,7,0.5)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography variant="caption" sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 600, fontSize: '0.6875rem', color: 'rgba(255,193,7,0.75)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  How it works
                </Typography>
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 0.6,
                  px: 1, py: 0.35, borderRadius: '100px',
                  background: 'linear-gradient(135deg, rgba(156,39,176,0.35), rgba(186,104,200,0.2))',
                  border: '1px solid rgba(186,104,200,0.55)',
                  boxShadow: '0 0 8px rgba(156,39,176,0.35)',
                }}>
                  <Box sx={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    bgcolor: '#CE93D8',
                    boxShadow: '0 0 5px rgba(206,147,216,0.9)',
                    animation: 'pulse-dot 2s ease-in-out infinite',
                    '@keyframes pulse-dot': {
                      '0%, 100%': { opacity: 1, boxShadow: '0 0 5px rgba(206,147,216,0.9)' },
                      '50%': { opacity: 0.6, boxShadow: '0 0 9px rgba(206,147,216,1)' }
                    }
                  }} />
                  <Typography variant="caption" sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: '0.6rem', color: '#E1BEE7', letterSpacing: '0.1em', textTransform: 'uppercase', lineHeight: 1 }}>
                    New Feature
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 400, fontSize: '0.75rem', lineHeight: 1.7, display: 'block', color: 'rgba(255,255,255,0.7)' }}>
                Pick a secret player from the pool. They must not be in your playing XI or bench, and they can never be transferred into your squad. Your peers will see only which <strong>team</strong> this player is from — <strong>their name stays hidden from other participants</strong>. <strong>Only you know who they are</strong>. Their <strong>points are added at the end</strong> of the league. This choice is editable until the squad deadline passes. <strong>Note:</strong> Hidden player points are not included in your Powerplay match score.
              </Typography>
            </Box>

            {existingSquad?.hiddenPlayerId && isDeadlinePassed ? (
              /* Locked — show read-only card with points */
              (() => {
                const livePoints = availablePlayers.find(p => p.id === existingSquad.hiddenPlayerId)?.stats?.T20?.recentForm
                  ?? existingSquad.hiddenPlayerPoints
                  ?? null;
                return (
              <Box sx={{ p: 1.5, bgcolor: 'rgba(255,255,255,0.10)', borderRadius: 1.5, border: '1px solid rgba(255,193,7,0.2)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                  <LockIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Box flex={1}>
                    <Typography variant="body2" sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 600, color: 'warning.main', fontSize: '0.875rem' }}>
                      {existingSquad.hiddenPlayerName}
                    </Typography>
                    <Typography variant="caption" sx={{ fontFamily: "'Satoshi', sans-serif", color: 'rgba(255,255,255,0.6)', fontSize: '0.6875rem' }}>
                      {existingSquad.hiddenPlayerTeam} · Locked after deadline
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" sx={{ fontFamily: "'Bebas Neue', monospace", fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: livePoints != null ? 'success.main' : 'text.secondary', fontSize: '0.875rem' }}>
                      {livePoints != null ? `${livePoints.toFixed(2)} pts` : '—'}
                    </Typography>
                    <Typography variant="caption" sx={{ fontFamily: "'Satoshi', sans-serif", color: 'rgba(255,255,255,0.5)', fontSize: '0.625rem' }}>
                      {livePoints != null ? 'pool pts' : 'Pending'}
                    </Typography>
                  </Box>
                </Box>
                {livePoints != null && livePoints > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1, borderTop: '1px solid rgba(255,193,7,0.15)' }}>
                    <Typography variant="caption" sx={{ fontFamily: "'Satoshi', sans-serif", color: 'rgba(255,255,255,0.5)', fontSize: '0.6875rem' }}>
                      Estimated Total (incl. hidden)
                    </Typography>
                    <Typography variant="caption" sx={{ fontFamily: "'Bebas Neue', monospace", fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: 'warning.main', fontSize: '0.75rem' }}>
                      {((existingSquad.totalPoints ?? 0) + livePoints).toFixed(2)} pts
                    </Typography>
                  </Box>
                )}
                <Typography variant="caption" sx={{ fontFamily: "'Satoshi', sans-serif", display: 'block', mt: 1, color: 'rgba(255,255,255,0.4)', fontSize: '0.625rem', fontStyle: 'italic' }}>
                  Note: Hidden player points are not included in Powerplay match scores.
                </Typography>
              </Box>
                );
              })()
            ) : hiddenPlayerId ? (
              /* Selected but not yet locked — show with conflict warning if in squad */
              (() => {
                const hp = availablePlayers.find(p => p.id === hiddenPlayerId);
                const isInSquad = selectedPlayers.some(sp => sp.id === hiddenPlayerId);
                return hp ? (
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, bgcolor: isInSquad ? 'rgba(244,67,54,0.1)' : 'rgba(255,255,255,0.10)', borderRadius: 1.5, border: isInSquad ? '1px solid rgba(244,67,54,0.3)' : '1px solid rgba(255,193,7,0.2)' }}>
                      <LockIcon fontSize="small" sx={{ color: isInSquad ? 'error.main' : 'text.secondary' }} />
                      <Box flex={1}>
                        <Typography variant="body2" fontWeight="bold" color={isInSquad ? 'error.main' : 'warning.main'}>
                          {hp.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {hp.team} • Will be locked at deadline
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" sx={{ fontFamily: "'Bebas Neue', monospace", fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: 'rgba(255,255,255,0.8)', fontSize: '0.8125rem' }}>
                          {(hp.stats.T20.recentForm ?? 0).toFixed(2)} pts
                        </Typography>
                        <Typography variant="caption" sx={{ fontFamily: "'Satoshi', sans-serif", color: 'rgba(255,255,255,0.4)', fontSize: '0.6rem' }}>
                          current
                        </Typography>
                      </Box>
                      {!isDeadlinePassed && (
                        <Button size="small" color="inherit" onClick={() => onSelectHiddenPlayer('')} sx={{ minWidth: 0 }}>✕</Button>
                      )}
                    </Box>
                    {isInSquad && (
                      <Alert severity="error" sx={{ mt: 1, fontSize: '0.75rem' }}>
                        This player is in your squad or bench. Remove them first or pick a different hidden player.
                      </Alert>
                    )}
                    {existingSquad?.isSubmitted && !isDeadlinePassed && (
                      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          variant="outlined"
                          size="small"
                          color="warning"
                          disabled={submitting}
                          onClick={onSavePredictionsAndHiddenPlayer}
                          sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 500 }}
                        >
                          Save Hidden Player
                        </Button>
                      </Box>
                    )}
                  </Box>
                ) : null;
              })()
            ) : (
              /* Not yet picked — show search */
              <Box>
                {!isDeadlinePassed && (
                  <Box>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Search by name or team..."
                      value={hiddenPlayerSearch}
                      onChange={(e) => setHiddenPlayerSearch(e.target.value)}
                      sx={{ mb: 1 }}
                    />
                    <Box sx={{ maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      {availablePlayers
                        .filter(p => {
                          const inSquad = selectedPlayers.some(sp => sp.id === p.id);
                          const matchesSearch = !hiddenPlayerSearch || p.name.toLowerCase().includes(hiddenPlayerSearch.toLowerCase()) || p.team.toLowerCase().includes(hiddenPlayerSearch.toLowerCase());
                          return !inSquad && matchesSearch;
                        })
                        .slice(0, 20)
                        .map(p => (
                          <Box
                            key={p.id}
                            onClick={() => { onSelectHiddenPlayer(p.id); setHiddenPlayerSearch(''); }}
                            sx={{ p: 1, borderRadius: 1, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' } }}
                          >
                            <Box>
                              <Typography variant="body2" fontWeight="medium">{p.name}</Typography>
                              <Typography variant="caption" color="text.secondary">{p.team}</Typography>
                            </Box>
                          </Box>
                        ))}
                    </Box>
                  </Box>
                )}
                {isDeadlinePassed && (
                  <Alert severity="warning" sx={{ fontSize: '0.75rem' }}>
                    Squad deadline has passed. You can no longer select your Hidden Player.
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Transfer History */}
        {existingSquad?.transferHistory && existingSquad.transferHistory.length > 0 && (
          <Box sx={{ mt: { xs: 2, sm: 2.5, md: 3 }, border: '1px solid rgba(255,255,255,0.18)', borderRadius: 2, bgcolor: 'rgba(255,255,255,0.055)', overflow: 'hidden' }}>
            {/* Header — clickable to toggle */}
            <Box
              onClick={() => setTransferHistoryOpen(o => !o)}
              sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: { xs: 1.5, sm: 2 }, cursor: 'pointer', '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' } }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryIcon fontSize="small" sx={{ color: 'rgba(0,229,255,0.7)' }} />
                <Typography variant="h6" sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: { xs: '1rem', sm: '1.125rem' }, fontWeight: 600 }}>
                  Transfer History
                </Typography>
                <Chip
                  label={existingSquad.transferHistory.length}
                  size="small"
                  sx={{ height: 20, fontSize: '0.6875rem', fontWeight: 700, bgcolor: 'rgba(0,229,255,0.12)', color: 'rgba(0,229,255,0.9)', fontFamily: "'Satoshi', sans-serif" }}
                />
              </Box>
              {transferHistoryOpen ? <ExpandLessIcon fontSize="small" sx={{ color: 'text.secondary' }} /> : <ExpandMoreIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
            </Box>

            {/* Collapsible content */}
            {transferHistoryOpen && (
              <Box sx={{ maxHeight: 360, overflowY: 'auto', px: { xs: 1.5, sm: 2 }, pb: 2 }}>
                {[...existingSquad.transferHistory]
                  .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                  .map((entry, idx) => {
                    const typeLabel = entry.transferType === 'bench' ? 'Bench' : entry.transferType === 'flexible' ? 'Flexible' : 'Mid-Season';
                    const accentColor: Record<string, string> = { bench: '#CE93D8', flexible: '#00E5FF', midSeason: '#FFB74D' };
                    const accent = accentColor[entry.transferType] ?? '#ffffff';

                    // Resolve player names from preTransferSnapshot or availablePlayers pool
                    const resolvePlayerName = (playerId: string | undefined, snapshot?: { players: any[] }): string => {
                      if (!playerId) return '';
                      if (snapshot?.players) {
                        const found = snapshot.players.find((p: any) => p.playerId === playerId || p.id === playerId);
                        if (found) return found.playerName || found.name || playerId;
                      }
                      const poolPlayer = availablePlayers.find(p => p.id === playerId);
                      return poolPlayer?.name || playerId.slice(0, 8) + '…';
                    };

                    return (
                      <Box
                        key={idx}
                        sx={{
                          mb: 1.5,
                          display: 'flex',
                          borderRadius: 1.5,
                          overflow: 'hidden',
                          border: '1px solid rgba(255,255,255,0.08)',
                          bgcolor: 'rgba(255,255,255,0.04)',
                        }}
                      >
                        {/* Left accent bar */}
                        <Box sx={{ width: 3, flexShrink: 0, bgcolor: accent, opacity: 0.75 }} />

                        <Box sx={{ flex: 1, p: 1.5 }}>
                          {/* Top row: type badge + change kind + timestamp */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              {/* Transfer type pill — soft gradient tinted to accent */}
                              <Box sx={{
                                px: 1.25, py: 0.3, borderRadius: '100px',
                                background: `linear-gradient(135deg, ${accent}22, ${accent}0A)`,
                                border: `1px solid ${accent}55`,
                                boxShadow: `0 0 8px ${accent}33 inset`,
                              }}>
                                <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: '0.6rem', fontWeight: 700, color: accent, letterSpacing: '0.06em', textTransform: 'uppercase', lineHeight: 1 }}>
                                  {typeLabel}
                                </Typography>
                              </Box>
                              {/* Substitution / Role Change chip — glassmorphism dark */}
                              <Box sx={{
                                px: 1, py: 0.25, borderRadius: 0.75,
                                background: 'linear-gradient(145deg, rgba(26,58,92,0.95), rgba(6,13,23,0.98))',
                                border: '1px solid rgba(30,136,229,0.25)',
                                boxShadow: '-2px 0 8px rgba(30,136,229,0.12)',
                              }}>
                                <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: '0.6rem', fontWeight: 700, color: 'rgba(255,255,255,0.75)', letterSpacing: '0.04em', textTransform: 'uppercase', lineHeight: 1 }}>
                                  {entry.changeType === 'playerSubstitution' ? 'Substitution' : 'Role Change'}
                                </Typography>
                              </Box>
                            </Box>
                            <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: '0.6875rem', color: 'rgba(255,255,255,0.45)', letterSpacing: '0.01em', flexShrink: 0 }}>
                              {new Date(entry.timestamp).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                          </Box>

                          {/* Player substitution row */}
                          {entry.changeType === 'playerSubstitution' && entry.playerOut && entry.playerIn && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                              <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: '0.9375rem', fontWeight: 700, color: 'rgba(255,120,100,0.95)', lineHeight: 1.2 }}>
                                {resolvePlayerName(entry.playerOut, entry.preTransferSnapshot)}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', px: 0.75, py: 0.25, borderRadius: 0.5, bgcolor: 'rgba(255,255,255,0.06)' }}>
                                <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', lineHeight: 1 }}>→</Typography>
                              </Box>
                              <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: '0.9375rem', fontWeight: 700, color: 'rgba(100,220,160,0.95)', lineHeight: 1.2 }}>
                                {resolvePlayerName(entry.playerIn)}
                              </Typography>
                            </Box>
                          )}

                          {/* Role reassignment row */}
                          {entry.changeType === 'roleReassignment' && (
                            <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: '0.875rem', fontWeight: 600, color: 'rgba(255,255,255,0.75)', lineHeight: 1.4 }}>
                              {[
                                entry.newViceCaptainId && `VC → ${resolvePlayerName(entry.newViceCaptainId)}`,
                                entry.newXFactorId && `XF → ${resolvePlayerName(entry.newXFactorId)}`,
                              ].filter(Boolean).join('  ·  ') || 'Role reassignment'}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })}
              </Box>
            )}
          </Box>
        )}

        {/* Slot Picker Drawer — mobile only, opens when tapping empty slot */}
        <Drawer
          anchor="bottom"
          open={slotPickerOpen}
          onClose={() => setSlotPickerOpen(false)}
          PaperProps={{
            sx: {
              bgcolor: 'background.paper',
              borderRadius: '16px 16px 0 0',
              maxHeight: '70vh',
              pb: 'env(safe-area-inset-bottom, 0px)',
            }
          }}
        >
          <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="h6" fontWeight={600} fontSize="1rem">
                {slotPickerRole ? `Add ${slotPickerRole.charAt(0).toUpperCase() + slotPickerRole.slice(1)}` : 'Add Player'}
                {slotPickerTarget === 'bench' ? ' (Bench)' : ''}
              </Typography>
              <IconButton size="small" onClick={() => setSlotPickerOpen(false)}>
                <Close />
              </IconButton>
            </Box>
            <TextField
              size="small"
              fullWidth
              placeholder="Search players..."
              value={slotPickerSearch}
              onChange={(e) => setSlotPickerSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />
            <List dense sx={{ overflowY: 'auto', maxHeight: '50vh' }}>
              {availablePlayers
                .filter(p => {
                  const alreadySelected = selectedPlayers.some(sp => sp.id === p.id);
                  if (alreadySelected) return false;
                  if (slotPickerRole && slotPickerRole !== 'flexible' && p.role !== slotPickerRole) return false;
                  if (slotPickerSearch) {
                    const q = slotPickerSearch.toLowerCase();
                    return p.name.toLowerCase().includes(q) || p.team.toLowerCase().includes(q);
                  }
                  return true;
                })
                .slice(0, 30)
                .map(p => (
                  <ListItem key={p.id} disablePadding>
                    <ListItemButton
                      onClick={() => {
                        onAddPlayer(p, slotPickerTarget);
                        setSlotPickerOpen(false);
                      }}
                      sx={{ borderRadius: 1, mb: 0.25 }}
                    >
                      <ListItemText
                        primary={p.name}
                        secondary={`${p.team} · ${p.role}`}
                        primaryTypographyProps={{ fontWeight: 500, fontSize: '0.875rem' }}
                        secondaryTypographyProps={{ fontSize: '0.75rem' }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
            </List>
          </Box>
        </Drawer>

      </CardContent>
    </Card>
  );
};

// Player Selection Panel Component
const PlayerSelectionPanel: React.FC<{
  availablePlayers: Player[];
  selectedPlayers: SelectedPlayer[];
  onAddPlayer: (player: Player, position: 'regular' | 'bench') => void;
  filterRole: string;
  setFilterRole: (role: string) => void;
  league: League;
}> = ({ availablePlayers, selectedPlayers, onAddPlayer, filterRole, setFilterRole, league }) => {
  const [searchQuery, setSearchQuery] = React.useState('');

  const filteredPlayers = availablePlayers.filter(player => {
    const isSelected = selectedPlayers.find(p => p.id === player.id);
    const roleMatch = filterRole === 'all' || player.role === filterRole;

    // Search functionality - match against name, team, or role
    const searchMatch = !searchQuery ||
      player.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      player.team.toLowerCase().includes(searchQuery.toLowerCase()) ||
      player.role.toLowerCase().includes(searchQuery.toLowerCase());

    return !isSelected && roleMatch && searchMatch;
  });

  const PlayerCard: React.FC<{ player: Player }> = ({ player }) => (
    <Card sx={{ mb: { xs: 0.75, sm: 1 }, cursor: 'pointer', background: 'linear-gradient(145deg, rgba(26,58,92,0.95), rgba(6,13,23,0.98))', border: '1px solid rgba(30,136,229,0.20)', backdropFilter: 'blur(10px)', boxShadow: '0 6px 18px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.08)', '&:hover': { background: 'linear-gradient(145deg, rgba(16,40,68,0.97), rgba(10,25,41,0.99))', transform: 'translateY(-2px)', boxShadow: '0 10px 28px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.10)', borderColor: 'rgba(30,136,229,0.35)' }, transition: 'all 0.2s ease' }}>
      <CardContent sx={{ p: { xs: 1, sm: 1.5, md: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 0.75, sm: 1 }, gap: { xs: 1, sm: 2 } }}>
          <Box sx={{ width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 }, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, bgcolor: 'rgba(9,11,71,0.55)', borderRadius: '50%', border: '1px solid rgba(255,255,255,0.1)', overflow: 'hidden' }}>
            {cricketFlagEmoji(player.team) ? (
              <Box component="span" sx={{ fontSize: { xs: '3.5rem', sm: '4rem' }, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {cricketFlagEmoji(player.team)}
              </Box>
            ) : (
              <TeamLogo team={player.team} size={26} />
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 500, fontSize: { xs: '0.8rem', sm: '0.9rem' }, letterSpacing: '0.01em' }} noWrap>
              {player.name}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
              <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: { xs: '0.6rem', sm: '0.65rem' }, opacity: 0.4 }} noWrap>
                {player.team}
              </Typography>
              <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: { xs: '0.6rem', sm: '0.65rem' }, opacity: 0.25 }}>·</Typography>
              <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontSize: { xs: '0.6rem', sm: '0.65rem' }, opacity: 0.4, fontWeight: 600, letterSpacing: '0.04em' }}>
                {getRoleBadge(player.role).label}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={Number(player.stats[league.format as 'T20' | 'ODI' | 'Test'].recentForm).toFixed(2)}
            size="small"
            sx={{
              fontFamily: "'Bebas Neue', monospace",
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
              height: { xs: 20, sm: 22 },
              letterSpacing: '0.04em',
              fontVariantNumeric: 'tabular-nums',
              bgcolor: player.stats[league.format as 'T20' | 'ODI' | 'Test'].recentForm > 80
                ? 'rgba(30,136,229,0.18)'
                : player.stats[league.format as 'T20' | 'ODI' | 'Test'].recentForm > 60
                  ? 'rgba(255,152,0,0.15)'
                  : 'rgba(183,28,28,0.18)',
              color: player.stats[league.format as 'T20' | 'ODI' | 'Test'].recentForm > 80
                ? '#64B5F6'
                : player.stats[league.format as 'T20' | 'ODI' | 'Test'].recentForm > 60
                  ? '#FFB74D'
                  : '#EF9A9A',
              border: '1px solid',
              borderColor: player.stats[league.format as 'T20' | 'ODI' | 'Test'].recentForm > 80
                ? 'rgba(30,136,229,0.30)'
                : player.stats[league.format as 'T20' | 'ODI' | 'Test'].recentForm > 60
                  ? 'rgba(255,152,0,0.28)'
                  : 'rgba(183,28,28,0.30)',
            }}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 } }}>
          <Button
            size="small"
            variant="contained"
            onClick={() => onAddPlayer(player, 'regular')}
            sx={{
              minWidth: 'auto',
              px: { xs: 1.5, sm: 2 },
              py: { xs: 0.6, sm: 0.8 },
              width: league.transferTypes?.benchTransfers?.enabled ? '65%' : '100%',
              bgcolor: themeColors.blue.electric,
              color: 'white',
              '&:hover': {
                bgcolor: themeColors.blue.deep,
                transform: 'translateY(-1px)',
                boxShadow: themeColors.shadows.blue.md
              },
              transition: 'all 0.2s ease'
            }}
          >
            <PersonAdd sx={{ fontSize: { xs: 18, sm: 20 } }} />
          </Button>
          {league.transferTypes?.benchTransfers?.enabled && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => onAddPlayer(player, 'bench')}
              startIcon={<SwapHoriz sx={{ fontSize: { xs: 14, sm: 16 } }} />}
              sx={{
                fontSize: { xs: '0.65rem', sm: '0.75rem', md: '0.8rem' },
                fontWeight: 500,
                px: { xs: 0.75, sm: 1 },
                py: { xs: 0.4, sm: 0.6 },
                width: '35%',
                borderColor: themeColors.orange.primary,
                color: themeColors.orange.primary,
                '&:hover': {
                  borderColor: themeColors.orange.dark,
                  bgcolor: alpha(themeColors.orange.primary, 0.08),
                  color: themeColors.orange.dark
                }
              }}
            >
              Bench
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Card>
      <CardContent sx={{ px: { xs: 1, sm: 1.5, md: 2 }, py: { xs: 1, sm: 1.5, md: 2 } }}>
        <Typography variant="h6" gutterBottom sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 600, fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' }, letterSpacing: '0.01em' }}>
          Player Selection
        </Typography>

        {/* Search Bar */}
        <TextField
          fullWidth
          placeholder="Search by name, team, or role..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: { xs: 1.5, sm: 2 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')}>
                  <Close fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {/* Role Filter */}
        <FormControl fullWidth sx={{ mb: { xs: 1.5, sm: 2 } }}>
          <InputLabel sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' } }}>Filter by Role</InputLabel>
          <Select
            value={filterRole}
            label="Filter by Role"
            onChange={(e) => setFilterRole(e.target.value)}
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' } }}
          >
            <MenuItem value="all" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' } }}>All Players</MenuItem>
            <MenuItem value="batsman" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' } }}>Batters</MenuItem>
            <MenuItem value="bowler" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' } }}>Bowlers</MenuItem>
            <MenuItem value="allrounder" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' } }}>All-rounders</MenuItem>
            <MenuItem value="wicketkeeper" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem', md: '1rem' } }}>Wicket-keepers</MenuItem>
          </Select>
        </FormControl>

        {/* Available Players */}
        <Box sx={{ maxHeight: { xs: 400, sm: 500, md: 600 }, overflow: 'auto' }}>
          {filteredPlayers.length > 0 ? (
            filteredPlayers.map(player => (
              <PlayerCard key={player.id} player={player} />
            ))
          ) : (
            <Box sx={{ textAlign: 'center', py: { xs: 3, sm: 4 } }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                No players available for selection
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

// =============================================================================
// Tennis Squad UI
// =============================================================================
const colors = themeColors;

interface TennisSquadUIProps {
  league: import('../types/database').League;
  tennisPool: import('../types/database').PlayerPoolEntry[];
  tennismenSlots: (string | null)[];
  tenniswomenSlots: (string | null)[];
  onSetMenSlot: (i: number, pid: string | null) => void;
  onSetWomenSlot: (i: number, pid: string | null) => void;
  mensPredictionId: string;
  womensPredictionId: string;
  onMensPrediction: (id: string) => void;
  onWomensPrediction: (id: string) => void;
  isDeadlinePassed: boolean;
  isValid: boolean;
  hasExistingSquad: boolean;
  submitting: boolean;
  onSubmit: () => void;
}

const tennisGlassCard = (accentColor: string) => ({
  background: `linear-gradient(145deg, ${alpha(colors.blue.navy, 0.95)} 0%, ${alpha('#0A1929', 0.98)} 100%)`,
  border: `1px solid ${alpha(accentColor, 0.22)}`,
  borderRadius: 3,
  boxShadow: `0 16px 48px rgba(0,0,0,0.4)`,
  overflow: 'hidden' as const,
  position: 'relative' as const,
  '&::before': {
    content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: '2px',
    background: `linear-gradient(90deg, transparent, ${alpha(accentColor, 0.7)}, transparent)`,
  },
});



const TennisSquadUI: React.FC<TennisSquadUIProps> = ({
  league,
  tennisPool,
  tennismenSlots,
  tenniswomenSlots,
  onSetMenSlot,
  onSetWomenSlot,
  mensPredictionId,
  womensPredictionId,
  onMensPrediction,
  onWomensPrediction,
  isDeadlinePassed,
  isValid,
  hasExistingSquad,
  submitting,
  onSubmit,
}) => {
  const menSlots  = league.tennisSquadRules?.menSlots   ?? 5;
  const womenSlots = league.tennisSquadRules?.womenSlots ?? 5;
  const menMultipliers   = league.tennisSquadRules?.menMultipliers   ?? [2, 1.75, 1.5, 1.25, 1];
  const womenMultipliers = league.tennisSquadRules?.womenMultipliers ?? [2, 1.75, 1.5, 1.25, 1];
  const isMobile = useMediaQuery('(max-width:600px)');
  const budget = league.tennisSquadRules?.budget ?? 0;
  const hasBudget = budget > 0;

  // Sticky HUD: show compact bar when the Men's Picks section scrolls behind the sticky navbar
  const mensPicksTriggerRef = useRef<HTMLDivElement>(null);
  const [showStickyHUD, setShowStickyHUD] = useState(false);
  useEffect(() => {
    const el = mensPicksTriggerRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => setShowStickyHUD(!entry.isIntersecting),
      { threshold: 0, rootMargin: '-124px 0px 0px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  // Player picker dialog state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerGender, setPickerGender] = useState<'men' | 'women'>('men');
  const [pickerSlotIdx, setPickerSlotIdx] = useState(0);
  const [pickerSearch, setPickerSearch] = useState('');

  const openPicker = (gender: 'men' | 'women', idx: number) => {
    setPickerGender(gender);
    setPickerSlotIdx(idx);
    setPickerSearch('');
    setPickerOpen(true);
  };

  const handlePickPlayer = (pid: string) => {
    if (pickerGender === 'men') onSetMenSlot(pickerSlotIdx, pid);
    else onSetWomenSlot(pickerSlotIdx, pid);
    setPickerOpen(false);
  };

  const allMen   = tennisPool.filter(p => p.gender === 'men'   || p.role === 'men');
  const allWomen = tennisPool.filter(p => p.gender === 'women' || p.role === 'women');

  // Knocked-out detection: player's most recent perf was a loss
  const knockedOutIds = useMemo<Set<string>>(() => {
    const ids = new Set<string>();
    for (const entry of tennisPool) {
      const perfs = entry.tennisMatchPerformances ?? [];
      if (perfs.length === 0) continue;
      const sorted = [...perfs].sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      );
      if (sorted[0].won === false) ids.add(entry.playerId);
    }
    return ids;
  }, [tennisPool]);

  const sortBySeadThenPoints = (a: import('../types/database').PlayerPoolEntry, b: import('../types/database').PlayerPoolEntry) => {
    if (a.seed !== undefined && b.seed !== undefined) return a.seed - b.seed;
    if (a.seed !== undefined) return -1;
    if (b.seed !== undefined) return 1;
    return b.points - a.points;
  };

  const menFilled   = tennismenSlots.filter(Boolean).length;
  const womenFilled = tenniswomenSlots.filter(Boolean).length;

  // Budget calculations
  const allSelectedIds = [...tennismenSlots, ...tenniswomenSlots].filter(Boolean) as string[];
  const spentCredits = hasBudget
    ? allSelectedIds.reduce((sum, pid) => {
        const p = tennisPool.find(x => x.playerId === pid);
        return sum + (p?.price ?? 0);
      }, 0)
    : 0;
  const remainingCredits = budget - spentCredits;
  const isOverBudget = hasBudget && spentCredits > budget;
  const budgetPct = hasBudget ? Math.min((spentCredits / budget) * 100, 100) : 0;
  const budgetBarColor = budgetPct > 90 ? '#EF4444' : budgetPct > 70 ? '#F59E0B' : colors.success.primary;

  const renderSlots = (
    slots: (string | null)[],
    count: number,
    players: import('../types/database').PlayerPoolEntry[],
    multipliers: number[],
    prefix: 'M' | 'W',
    onSet: (i: number, pid: string | null) => void,
    accentColor: string,
    gender: 'men' | 'women',
  ) => Array.from({ length: count }, (_, i) => {
    const selectedId = slots[i] ?? null;
    const selected   = selectedId ? players.find(p => p.playerId === selectedId) : null;
    const isKnockedOut = selectedId ? knockedOutIds.has(selectedId) : false;
    const fillPct    = Math.round(((count - i) / count) * 100);
    const mult       = multipliers[i];

    return (
      <Box
        key={i}
        sx={{
          display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 },
          p: { xs: 1, sm: 1.25 },
          mb: 1,
          borderRadius: 2,
          bgcolor: selected ? alpha(accentColor, 0.05) : 'transparent',
          border: `1px solid ${selected ? alpha(accentColor, 0.18) : 'rgba(255,255,255,0.05)'}`,
          transition: 'all 0.2s ease',
          opacity: isKnockedOut ? 0.55 : 1,
        }}
      >
        {/* Slot label + multiplier bar */}
        <Tooltip title={`${mult}x multiplier - higher rank earns more`} placement="top" arrow>
          <Box sx={{ flexShrink: 0, width: { xs: 36, sm: 42 }, textAlign: 'center', cursor: 'default' }}>
            <Typography sx={{
              fontFamily: "'Bebas Neue', sans-serif", fontSize: { xs: '1rem', sm: '1.1rem' },
              color: selected ? accentColor : alpha(accentColor, 0.55), lineHeight: 1,
              transition: 'color 0.2s ease',
            }}>
              {prefix}{i + 1}
            </Typography>
            <Box sx={{ width: '100%', height: 3, borderRadius: '2px', bgcolor: alpha(accentColor, 0.1), mt: 0.4 }}>
              <Box sx={{ width: `${fillPct}%`, height: '100%', borderRadius: '2px', bgcolor: alpha(accentColor, selected ? 0.8 : 0.35), transition: 'all 0.2s ease' }} />
            </Box>
            <Typography sx={{ fontSize: '0.55rem', color: alpha(accentColor, 0.5), mt: 0.35, letterSpacing: '0.03em' }}>
              {mult}x
            </Typography>
          </Box>
        </Tooltip>

        {/* Player area */}
        {selected ? (
          // Filled: show player card inline
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
              {selected.seed !== undefined && (
                <Box sx={{
                  width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                  bgcolor: selected.seed <= 8 ? alpha(colors.orange.primary, 0.15) : alpha(colors.text.secondary, 0.08),
                  border: `1px solid ${selected.seed <= 8 ? alpha(colors.orange.primary, 0.35) : 'rgba(255,255,255,0.1)'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Typography sx={{ fontSize: '0.55rem', fontWeight: 800, color: selected.seed <= 8 ? colors.orange.light : alpha(colors.text.secondary, 0.55) }}>
                    {selected.seed}
                  </Typography>
                </Box>
              )}
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: { xs: '0.8rem', sm: '0.88rem' }, color: colors.text.primary, lineHeight: 1.2 }} noWrap>
                    {selected.name}
                  </Typography>
                  {isKnockedOut && (
                    <Chip
                      label="OUT"
                      size="small"
                      color="error"
                      sx={{ height: 16, fontSize: '0.55rem', fontWeight: 700, '& .MuiChip-label': { px: 0.6 } }}
                    />
                  )}
                </Box>
                <Typography sx={{ fontSize: '0.68rem', color: alpha(colors.text.secondary, 0.78), mt: 0.1 }}>
                  {flagEmoji(selected.team)} {selected.team}
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
              {hasBudget && selected.price !== undefined && (
                <Chip
                  size="small"
                  label={`${selected.price}cr`}
                  sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: alpha(accentColor, 0.12), color: accentColor, border: `1px solid ${alpha(accentColor, 0.3)}` }}
                />
              )}
              <Typography sx={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '0.9rem', color: accentColor }}>
                {selected.points} pts
              </Typography>
              {!isDeadlinePassed && (
                <IconButton
                  size="small"
                  onClick={() => onSet(i, null)}
                  sx={{ p: 0.4, '&:hover': { bgcolor: alpha(colors.error.primary, 0.1) } }}
                >
                  <Close sx={{ fontSize: 13, color: alpha(colors.text.secondary, 0.35) }} />
                </IconButton>
              )}
            </Box>
          </Box>
        ) : (
          // Empty: open picker dialog
          <ButtonBase
            onClick={() => openPicker(gender, i)}
            disabled={isDeadlinePassed}
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 1.5, py: 1,
              borderRadius: 1.5,
              border: `1.5px dashed ${alpha(accentColor, 0.2)}`,
              bgcolor: alpha(accentColor, 0.02),
              transition: 'all 0.2s ease',
              '&:hover:not(:disabled)': {
                border: `1.5px dashed ${alpha(accentColor, 0.5)}`,
                bgcolor: alpha(accentColor, 0.07),
                transform: 'translateY(-1px)',
              },
              '&:active:not(:disabled)': { transform: 'scale(0.99)' },
              '&:disabled': { opacity: 0.35, cursor: 'not-allowed' },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonAdd sx={{ fontSize: 15, color: alpha(accentColor, 0.45) }} />
              <Typography sx={{ fontSize: { xs: '0.75rem', sm: '0.82rem' }, fontFamily: "'Satoshi', sans-serif", fontWeight: 600, color: alpha(colors.text.primary, 0.35) }}>
                Pick player
              </Typography>
            </Box>
            <KeyboardArrowDown sx={{ fontSize: 16, color: alpha(accentColor, 0.3) }} />
          </ButtonBase>
        )}
      </Box>
    );
  });

  // Prediction picker dialog state
  const [predPickerOpen, setPredPickerOpen] = useState(false);
  const [predPickerGender, setPredPickerGender] = useState<'men' | 'women'>('men');
  const [predPickerSearch, setPredPickerSearch] = useState('');
  const [predPickerOnChange, setPredPickerOnChange] = useState<((v: string) => void) | null>(null);
  const [predPickerValue, setPredPickerValue] = useState('');
  const [predPickerAccent, setPredPickerAccent] = useState('');

  const openPredPicker = (gender: 'men' | 'women', currentVal: string, accent: string, cb: (v: string) => void) => {
    setPredPickerGender(gender);
    setPredPickerSearch('');
    setPredPickerValue(currentVal);
    setPredPickerAccent(accent);
    setPredPickerOnChange(() => cb);
    setPredPickerOpen(true);
  };

  const PredictionSelect = ({
    value, onChange, players, label, accentColor,
  }: {
    value: string; onChange: (v: string) => void;
    players: import('../types/database').PlayerPoolEntry[];
    label: string; accentColor: string;
  }) => {
    const gender: 'men' | 'women' = players === allMen ? 'men' : 'women';
    const selectedPlayer = value ? players.find(p => p.playerId === value) : null;
    return (
      <ButtonBase
        onClick={() => !isDeadlinePassed && openPredPicker(gender, value, accentColor, onChange)}
        disabled={isDeadlinePassed}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5, py: 1,
          borderRadius: 1.5,
          border: `1px solid ${selectedPlayer ? alpha(accentColor, 0.3) : alpha(accentColor, 0.15)}`,
          bgcolor: selectedPlayer ? alpha(accentColor, 0.06) : alpha(accentColor, 0.02),
          transition: 'all 0.2s ease',
          '&:hover:not(:disabled)': {
            border: `1px solid ${alpha(accentColor, 0.45)}`,
            bgcolor: alpha(accentColor, 0.08),
          },
          '&:disabled': { opacity: 0.35 },
        }}
      >
        {selectedPlayer ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
            {selectedPlayer.seed !== undefined && (
              <Box sx={{
                width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                bgcolor: selectedPlayer.seed <= 4 ? alpha('#FFD700', 0.15) : alpha(colors.orange.primary, 0.12),
                border: `1.5px solid ${selectedPlayer.seed <= 4 ? alpha('#FFD700', 0.38) : alpha(colors.orange.primary, 0.3)}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Typography sx={{ fontSize: '0.52rem', fontWeight: 800, color: selectedPlayer.seed <= 4 ? '#FFD700' : colors.orange.light }}>
                  {selectedPlayer.seed}
                </Typography>
              </Box>
            )}
            <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 600, fontSize: '0.85rem', color: colors.text.primary }} noWrap>
              {flagEmoji(selectedPlayer.team)} {selectedPlayer.name}
            </Typography>
          </Box>
        ) : (
          <Typography sx={{ fontSize: '0.82rem', fontFamily: "'Satoshi', sans-serif", color: alpha(colors.text.primary, 0.35), flex: 1, textAlign: 'left' }}>
            {label}
          </Typography>
        )}
        <KeyboardArrowDown sx={{ fontSize: 16, color: alpha(accentColor, 0.35), flexShrink: 0, ml: 0.5 }} />
      </ButtonBase>
    );
  };

  const SectionHeader = ({ title, filled, total, accentColor, icon }: {
    title: string; filled: number; total: number; accentColor: string; icon: React.ReactNode;
  }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box sx={{
          width: 32, height: 32, borderRadius: '50%',
          bgcolor: alpha(accentColor, 0.12), border: `1px solid ${alpha(accentColor, 0.3)}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {icon}
        </Box>
        <Typography sx={{
          fontFamily: "'Montserrat', sans-serif", fontWeight: 800,
          fontSize: { xs: '0.95rem', sm: '1.05rem' },
          background: `linear-gradient(90deg, ${accentColor}, ${alpha(accentColor, 0.7)})`,
          backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {Array.from({ length: total }, (_, i) => (
          <Box
            key={i}
            sx={{
              width: i < filled ? 14 : 10, height: i < filled ? 6 : 6,
              borderRadius: '3px',
              bgcolor: i < filled ? accentColor : alpha(accentColor, 0.15),
              transition: 'all 0.25s ease',
              boxShadow: i < filled ? `0 0 6px ${alpha(accentColor, 0.5)}` : 'none',
            }}
          />
        ))}
        <Typography sx={{ ml: 0.75, fontSize: '0.7rem', fontFamily: "'Satoshi', sans-serif", fontWeight: 700, color: filled === total ? accentColor : alpha(colors.text.secondary, 0.5) }}>
          {filled}/{total}
        </Typography>
      </Box>
    </Box>
  );

  const menColor   = colors.blue.electric;
  const womenColor = '#E91E63';

  return (
    <Box sx={{ mt: { xs: 1.5, sm: 2 } }}>
      <Grid container spacing={{ xs: 2, sm: 3 }}>

        {/* Team Summary - budget leagues only */}
        {hasBudget && (
          <Grid size={12}>
            <Box sx={{
              ...tennisGlassCard(isOverBudget ? '#EF4444' : budgetBarColor),
              p: { xs: 2, sm: 2.5 },
            }}>
              {/* Stats row */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 0, mb: 2.5 }}>
                {/* Budget Remaining */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 1.25, px: 1, borderRight: `1px solid rgba(255,255,255,0.07)` }}>
                  <AccountBalance sx={{ fontSize: 18, color: budgetBarColor, mb: 0.75 }} />
                  <Typography sx={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: { xs: '1.6rem', sm: '1.9rem' }, color: isOverBudget ? '#EF4444' : budgetBarColor, lineHeight: 1 }}>
                    {remainingCredits < 0 ? `-${Math.abs(remainingCredits)}` : remainingCredits}
                  </Typography>
                  <Typography sx={{ fontSize: '0.65rem', color: alpha(colors.text.secondary, 0.5), fontFamily: "'Satoshi', sans-serif", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', mt: 0.35 }}>
                    cr remaining
                  </Typography>
                  <Typography sx={{ fontSize: '0.6rem', color: alpha(colors.text.secondary, 0.35), fontFamily: "'Satoshi', sans-serif", mt: 0.25 }}>
                    of {budget}cr total
                  </Typography>
                </Box>

                {/* Spent */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 1.25, px: 1, borderRight: { xs: 'none', sm: `1px solid rgba(255,255,255,0.07)` } }}>
                  <ShoppingCart sx={{ fontSize: 18, color: alpha(colors.text.secondary, 0.5), mb: 0.75 }} />
                  <Typography sx={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: { xs: '1.6rem', sm: '1.9rem' }, color: colors.text.primary, lineHeight: 1 }}>
                    {spentCredits}
                  </Typography>
                  <Typography sx={{ fontSize: '0.65rem', color: alpha(colors.text.secondary, 0.5), fontFamily: "'Satoshi', sans-serif", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', mt: 0.35 }}>
                    cr spent
                  </Typography>
                </Box>

                {/* Men's picks */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 1.25, px: 1, borderRight: `1px solid rgba(255,255,255,0.07)`, borderTop: { xs: `1px solid rgba(255,255,255,0.07)`, sm: 'none' } }}>
                  <Male sx={{ fontSize: 18, color: alpha(colors.blue.electric, 0.7), mb: 0.75 }} />
                  <Typography sx={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: { xs: '1.6rem', sm: '1.9rem' }, color: menFilled === menSlots ? colors.blue.electric : colors.text.primary, lineHeight: 1 }}>
                    {menFilled}/{menSlots}
                  </Typography>
                  <Typography sx={{ fontSize: '0.65rem', color: alpha(colors.text.secondary, 0.5), fontFamily: "'Satoshi', sans-serif", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', mt: 0.35 }}>
                    men picked
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.4, mt: 0.6 }}>
                    {Array.from({ length: menSlots }, (_, i) => (
                      <Box key={i} sx={{ width: i < menFilled ? 10 : 7, height: 4, borderRadius: '2px', bgcolor: i < menFilled ? colors.blue.electric : alpha(colors.blue.electric, 0.15), transition: 'all 0.2s ease', boxShadow: i < menFilled ? `0 0 4px ${alpha(colors.blue.electric, 0.5)}` : 'none' }} />
                    ))}
                  </Box>
                </Box>

                {/* Women's picks */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 1.25, px: 1, borderTop: { xs: `1px solid rgba(255,255,255,0.07)`, sm: 'none' } }}>
                  <Female sx={{ fontSize: 18, color: alpha('#E91E63', 0.7), mb: 0.75 }} />
                  <Typography sx={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: { xs: '1.6rem', sm: '1.9rem' }, color: womenFilled === womenSlots ? '#E91E63' : colors.text.primary, lineHeight: 1 }}>
                    {womenFilled}/{womenSlots}
                  </Typography>
                  <Typography sx={{ fontSize: '0.65rem', color: alpha(colors.text.secondary, 0.5), fontFamily: "'Satoshi', sans-serif", fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', mt: 0.35 }}>
                    women picked
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.4, mt: 0.6 }}>
                    {Array.from({ length: womenSlots }, (_, i) => (
                      <Box key={i} sx={{ width: i < womenFilled ? 10 : 7, height: 4, borderRadius: '2px', bgcolor: i < womenFilled ? '#E91E63' : alpha('#E91E63', 0.15), transition: 'all 0.2s ease', boxShadow: i < womenFilled ? `0 0 4px ${alpha('#E91E63', 0.4)}` : 'none' }} />
                    ))}
                  </Box>
                </Box>
              </Box>

              {/* Progress bar */}
              <Box sx={{ px: 0.5 }}>
                <LinearProgress
                  variant="determinate"
                  value={budgetPct}
                  sx={{
                    height: 6, borderRadius: 3,
                    bgcolor: alpha(colors.blue.navy, 0.5),
                    '& .MuiLinearProgress-bar': { bgcolor: budgetBarColor, borderRadius: 3, transition: 'transform 0.4s ease, background-color 0.3s ease' },
                  }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.75 }}>
                  <Typography sx={{ fontSize: '0.65rem', fontFamily: "'Satoshi', sans-serif", color: alpha(colors.text.secondary, 0.45) }}>
                    {spentCredits} / {budget} credits used
                  </Typography>
                  {isOverBudget && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                      <WarningAmber sx={{ fontSize: 12, color: '#EF4444' }} />
                      <Typography sx={{ fontSize: '0.65rem', fontFamily: "'Satoshi', sans-serif", fontWeight: 700, color: '#EF4444' }}>
                        {spentCredits - budget}cr over budget
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Grid>
        )}

        {/* Men's Picks */}
        <Grid size={{ xs: 12, md: 6 }} ref={mensPicksTriggerRef}>
          <Card sx={tennisGlassCard(menColor)}>
            <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
              <SectionHeader
                title={`Men's Picks`}
                filled={menFilled}
                total={menSlots}
                accentColor={menColor}
                icon={<SportsTennis sx={{ fontSize: 15, color: menColor }} />}
              />
              <Typography sx={{ fontSize: '0.72rem', fontFamily: "'Satoshi', sans-serif", color: alpha(colors.text.secondary, 0.45), mb: 2, letterSpacing: '0.01em' }}>
                M1 earns {menMultipliers[0]}x points. Rank your best picks first.
              </Typography>
              {renderSlots(tennismenSlots, menSlots, allMen, menMultipliers, 'M', onSetMenSlot, menColor, 'men')}
            </CardContent>
          </Card>
        </Grid>

        {/* Women's Picks */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card sx={tennisGlassCard(womenColor)}>
            <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
              <SectionHeader
                title={`Women's Picks`}
                filled={womenFilled}
                total={womenSlots}
                accentColor={womenColor}
                icon={<SportsTennis sx={{ fontSize: 15, color: womenColor }} />}
              />
              <Typography sx={{ fontSize: '0.72rem', fontFamily: "'Satoshi', sans-serif", color: alpha(colors.text.secondary, 0.45), mb: 2, letterSpacing: '0.01em' }}>
                W1 earns {womenMultipliers[0]}x points. Rank your best picks first.
              </Typography>
              {renderSlots(tenniswomenSlots, womenSlots, allWomen, womenMultipliers, 'W', onSetWomenSlot, womenColor, 'women')}
            </CardContent>
          </Card>
        </Grid>

        {/* Bonus Predictions */}
        <Grid size={12}>
          <Card sx={tennisGlassCard(colors.gold)}>
            <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <Box sx={{
                    width: 32, height: 32, borderRadius: '50%',
                    bgcolor: alpha(colors.gold, 0.1), border: `1px solid ${alpha(colors.gold, 0.3)}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <EmojiEvents sx={{ fontSize: 15, color: colors.gold }} />
                  </Box>
                  <Box>
                    <Typography sx={{
                      fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: { xs: '0.95rem', sm: '1.05rem' },
                      background: `linear-gradient(90deg, ${colors.gold}, ${alpha(colors.gold, 0.6)})`,
                      backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
                    }}>
                      Bonus Prediction
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', fontFamily: "'Satoshi', sans-serif", color: (!mensPredictionId || !womensPredictionId) ? alpha('#EF4444', 0.75) : alpha(colors.success.primary, 0.8) }}>
                      {(!mensPredictionId || !womensPredictionId) ? 'Required to submit' : 'Done ✓'}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 0.6,
                  px: 1.25, py: 0.4, borderRadius: 1.5,
                  border: `1px solid ${alpha(colors.gold, 0.3)}`, bgcolor: alpha(colors.gold, 0.07),
                }}>
                  <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: colors.gold, fontFamily: "'Satoshi', sans-serif" }}>
                    +{league.tennisConfig?.predictionBonus ?? 10} pts each
                  </Typography>
                </Box>
              </Box>
              <Typography sx={{ fontSize: '0.72rem', fontFamily: "'Satoshi', sans-serif", color: alpha(colors.text.secondary, 0.45), mb: 2.5 }}>
                Required to submit your squad. Earn bonus points for correct predictions — no deduction for wrong picks.
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography sx={{ fontSize: '0.68rem', fontFamily: "'Satoshi', sans-serif", fontWeight: 700, color: alpha(menColor, 0.7), textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.75 }}>
                    Men's Singles Winner
                  </Typography>
                  <PredictionSelect
                    value={mensPredictionId}
                    onChange={onMensPrediction}
                    players={allMen}
                    label="Who will win?"
                    accentColor={menColor}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Typography sx={{ fontSize: '0.68rem', fontFamily: "'Satoshi', sans-serif", fontWeight: 700, color: alpha(womenColor, 0.7), textTransform: 'uppercase', letterSpacing: '0.08em', mb: 0.75 }}>
                    Women's Singles Winner
                  </Typography>
                  <PredictionSelect
                    value={womensPredictionId}
                    onChange={onWomensPrediction}
                    players={allWomen}
                    label="Who will win?"
                    accentColor={womenColor}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Submit */}
        <Grid size={12}>
          <Card sx={{
            ...tennisGlassCard(colors.blue.electric),
            border: `1px solid ${alpha(isValid && !isOverBudget ? colors.blue.electric : isOverBudget ? '#EF4444' : colors.orange.primary, 0.25)}`,
          }}>
            <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
              {/* Checklist */}
              {(() => {
                const checks = [
                  { label: `Men's squad (${menFilled}/${menSlots})`, done: menFilled === menSlots },
                  { label: `Women's squad (${womenFilled}/${womenSlots})`, done: womenFilled === womenSlots },
                  { label: "Men's winner prediction", done: !!mensPredictionId },
                  { label: "Women's winner prediction", done: !!womensPredictionId },
                  ...(hasBudget ? [{ label: `Budget (${spentCredits}/${budget}cr)`, done: !isOverBudget }] : []),
                ];
                return (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 0.75, sm: 1 }, mb: 2 }}>
                    {checks.map(c => (
                      <Box
                        key={c.label}
                        sx={{
                          display: 'flex', alignItems: 'center', gap: 0.6,
                          px: 1.25, py: 0.5, borderRadius: 1.5,
                          border: `1px solid ${alpha(c.done ? colors.success.primary : colors.orange.primary, 0.35)}`,
                          bgcolor: alpha(c.done ? colors.success.primary : colors.orange.primary, 0.07),
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: c.done ? colors.success.primary : colors.orange.primary, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.68rem', fontFamily: "'Satoshi', sans-serif", fontWeight: 600, color: c.done ? colors.success.primary : colors.orange.primary, whiteSpace: 'nowrap' }}>
                          {c.label}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                );
              })()}

              {isOverBudget && !isDeadlinePassed && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5, px: 1.25, py: 0.75, borderRadius: 1.5, bgcolor: alpha('#EF4444', 0.07), border: `1px solid ${alpha('#EF4444', 0.2)}` }}>
                  <WarningAmber sx={{ fontSize: 14, color: '#EF4444', flexShrink: 0 }} />
                  <Typography sx={{ fontSize: '0.72rem', fontFamily: "'Satoshi', sans-serif", fontWeight: 600, color: '#EF4444' }}>
                    Over budget by {spentCredits - budget}cr — remove a player to continue
                  </Typography>
                </Box>
              )}

              {isDeadlinePassed ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, py: 1.25, borderRadius: 2, bgcolor: alpha('#fff', 0.03), border: `1px solid ${alpha('#fff', 0.07)}` }}>
                  <LockIcon sx={{ fontSize: 13, color: alpha('#fff', 0.22) }} />
                  <Typography sx={{ fontSize: '0.72rem', fontFamily: "'Satoshi', sans-serif", fontWeight: 500, color: alpha('#fff', 0.22), letterSpacing: '0.03em' }}>
                    Deadline passed · squad locked
                  </Typography>
                </Box>
              ) : (
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={!isValid || isOverBudget || submitting}
                  startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : isOverBudget ? <WarningAmber sx={{ fontSize: 18 }} /> : <CheckCircle sx={{ fontSize: 18 }} />}
                  onClick={onSubmit}
                  sx={{
                    background: isValid && !isOverBudget
                      ? `linear-gradient(135deg, ${colors.blue.electric}, ${colors.blue.deep})`
                      : isOverBudget ? `linear-gradient(135deg, #EF4444, #B91C1C)` : undefined,
                    color: (isValid && !isOverBudget) || isOverBudget ? '#fff' : undefined,
                    fontWeight: 700, fontFamily: "'Satoshi', sans-serif",
                    borderRadius: 2.5, py: { xs: 1.4, sm: 1.6 },
                    fontSize: { xs: '0.9rem', sm: '1rem' }, letterSpacing: '0.02em',
                    textTransform: 'none',
                    boxShadow: isValid && !isOverBudget ? colors.shadows.blue.md : undefined,
                    transition: 'all 0.25s ease',
                    '&:not(:disabled):hover': {
                      background: `linear-gradient(135deg, ${colors.blue.light}, ${colors.blue.electric})`,
                      transform: 'translateY(-1px)',
                      boxShadow: colors.shadows.blue.lg,
                    },
                  }}
                >
                  {submitting
                    ? (hasExistingSquad ? 'Updating...' : 'Submitting...')
                    : isOverBudget
                      ? 'Over Budget'
                      : hasExistingSquad
                        ? 'Update Squad'
                        : 'Submit Squad'}
                </Button>
              )}

            </CardContent>
          </Card>
        </Grid>

      </Grid>

      {/* ── Prediction Picker Dialog ── */}
      {(() => {
        const predPool = (predPickerGender === 'men' ? allMen : allWomen)
          .sort(sortBySeadThenPoints);
        const predFiltered = predPool.filter(p => {
          if (!predPickerSearch) return true;
          const q = predPickerSearch.toLowerCase();
          return p.name.toLowerCase().includes(q) || (p.team ?? '').toLowerCase().includes(q);
        });
        return (
          <Dialog
            open={predPickerOpen}
            onClose={() => setPredPickerOpen(false)}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
              sx: {
                bgcolor: '#0A0F1A',
                backgroundImage: 'none',
                border: `1px solid ${alpha(predPickerAccent || menColor, 0.22)}`,
                borderRadius: { xs: '12px 12px 0 0', sm: 2.5 },
                m: { xs: 0, sm: 2 },
                mt: { xs: 'auto', sm: 2 },
                maxHeight: { xs: '85vh', sm: '80vh' },
                display: 'flex',
                flexDirection: 'column',
              },
            }}
          >
            {/* Header */}
            <Box sx={{
              px: { xs: 1.5, sm: 2.5 }, pt: { xs: 2, sm: 2.5 }, pb: 2,
              background: `linear-gradient(135deg, ${alpha(predPickerAccent || menColor, 0.1)}, transparent 60%)`,
              borderBottom: `1px solid rgba(255,255,255,0.06)`,
              flexShrink: 0,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{
                    width: 38, height: 38, borderRadius: '50%',
                    bgcolor: alpha(predPickerAccent || menColor, 0.15),
                    border: `1.5px solid ${alpha(predPickerAccent || menColor, 0.4)}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <EmojiEvents sx={{ fontSize: 20, color: predPickerAccent || menColor }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: '1rem', color: colors.text.primary, lineHeight: 1 }}>
                      {predPickerGender === 'men' ? "Men's" : "Women's"} Winner Prediction
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: alpha(colors.text.secondary, 0.45), mt: 0.3 }}>
                      {predPool.length} players · bonus points for correct pick
                    </Typography>
                  </Box>
                </Box>
                <IconButton size="small" onClick={() => setPredPickerOpen(false)} sx={{ color: alpha(colors.text.secondary, 0.45), '&:hover': { color: colors.text.primary } }}>
                  <Close sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>

            {/* Search */}
            <Box sx={{ px: { xs: 1.5, sm: 2.5 }, py: { xs: 1.25, sm: 1.75 }, borderBottom: `1px solid rgba(255,255,255,0.05)`, flexShrink: 0 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name or country..."
                value={predPickerSearch}
                onChange={e => setPredPickerSearch(e.target.value)}
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 16, color: alpha(colors.text.secondary, 0.4) }} />
                    </InputAdornment>
                  ),
                  endAdornment: predPickerSearch ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setPredPickerSearch('')} sx={{ p: 0.3, color: alpha(colors.text.secondary, 0.4) }}>
                        <Close sx={{ fontSize: 14 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: alpha(predPickerAccent || menColor, 0.04),
                    borderRadius: 1.5,
                    fontSize: '0.875rem',
                    '& fieldset': { borderColor: alpha(predPickerAccent || menColor, 0.18) },
                    '&:hover fieldset': { borderColor: alpha(predPickerAccent || menColor, 0.38) },
                    '&.Mui-focused fieldset': { borderColor: predPickerAccent || menColor },
                  },
                  '& .MuiInputBase-input': { color: colors.text.primary },
                }}
              />
              {predPickerSearch && (
                <Typography sx={{ fontSize: '0.68rem', color: alpha(colors.text.secondary, 0.38), mt: 0.75 }}>
                  {predFiltered.length} result{predFiltered.length !== 1 ? 's' : ''} for "{predPickerSearch}"
                </Typography>
              )}
            </Box>

            {/* Player list */}
            <Box sx={{ overflowY: 'auto', flex: 1, px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 1.5 } }}>
              {/* No prediction row */}
              <ButtonBase
                onClick={() => { predPickerOnChange?.(''); setPredPickerOpen(false); }}
                sx={{
                  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  px: 1.5, py: 1.25, borderRadius: 2, mb: 0.75, textAlign: 'left',
                  bgcolor: !predPickerValue ? alpha(predPickerAccent || menColor, 0.08) : 'transparent',
                  border: `1px solid ${!predPickerValue ? alpha(predPickerAccent || menColor, 0.2) : 'rgba(255,255,255,0.05)'}`,
                  transition: 'all 0.15s ease',
                  '&:hover': { bgcolor: alpha(colors.text.secondary, 0.06), border: `1px solid rgba(255,255,255,0.1)` },
                }}
              >
                <Typography sx={{ fontSize: '0.85rem', fontFamily: "'Satoshi', sans-serif", color: alpha(colors.text.secondary, 0.5) }}>
                  No prediction
                </Typography>
                {!predPickerValue && <CheckCircle sx={{ fontSize: 15, color: alpha(predPickerAccent || menColor, 0.6) }} />}
              </ButtonBase>

              {predFiltered.length === 0 && predPickerSearch ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography sx={{ color: alpha(colors.text.secondary, 0.38), fontSize: '0.875rem' }}>
                    No players found for "{predPickerSearch}"
                  </Typography>
                </Box>
              ) : predFiltered.map(p => {
                const isSelected = p.playerId === predPickerValue;
                return (
                  <ButtonBase
                    key={p.playerId}
                    onClick={() => { predPickerOnChange?.(p.playerId); setPredPickerOpen(false); }}
                    sx={{
                      width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      px: 1.5, py: 1.1, borderRadius: 2, mb: 0.75, textAlign: 'left',
                      bgcolor: isSelected ? alpha(predPickerAccent || menColor, 0.1) : 'transparent',
                      border: `1px solid ${isSelected ? alpha(predPickerAccent || menColor, 0.28) : 'rgba(255,255,255,0.05)'}`,
                      transition: 'all 0.15s ease',
                      '&:hover': {
                        bgcolor: alpha(predPickerAccent || menColor, 0.07),
                        border: `1px solid ${alpha(predPickerAccent || menColor, 0.22)}`,
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 14px ${alpha(predPickerAccent || menColor, 0.1)}`,
                      },
                      '&:active': { transform: 'scale(0.99)' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                      {p.seed !== undefined ? (
                        <Box sx={{
                          width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                          bgcolor: p.seed <= 4 ? alpha('#FFD700', 0.15) : p.seed <= 8 ? alpha(colors.orange.primary, 0.12) : alpha(colors.text.secondary, 0.07),
                          border: `1.5px solid ${p.seed <= 4 ? alpha('#FFD700', 0.38) : p.seed <= 8 ? alpha(colors.orange.primary, 0.3) : 'rgba(255,255,255,0.09)'}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                          <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: p.seed <= 4 ? '#FFD700' : p.seed <= 8 ? colors.orange.light : alpha(colors.text.secondary, 0.5) }}>
                            {p.seed}
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ width: 28, height: 28, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Typography sx={{ fontSize: '0.65rem', color: alpha(colors.text.secondary, 0.2) }}>—</Typography>
                        </Box>
                      )}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: '0.9rem', color: colors.text.primary, lineHeight: 1.25 }} noWrap>
                          {p.name}
                        </Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: alpha(colors.text.secondary, 0.78) }}>
                          {flagEmoji(p.team) || '🏳️'} {p.team === 'Neutral' ? 'Neutral' : p.team}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                      {isSelected && <CheckCircle sx={{ fontSize: 15, color: predPickerAccent || menColor }} />}
                      <Typography sx={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '0.92rem', color: isSelected ? predPickerAccent || menColor : alpha(colors.text.secondary, 0.55), minWidth: 44, textAlign: 'right' }}>
                        {p.points} pts
                      </Typography>
                    </Box>
                  </ButtonBase>
                );
              })}
            </Box>
          </Dialog>
        );
      })()}

      {/* ── Player Picker Dialog ── */}
      {(() => {
        const pickerAccentColor = pickerGender === 'men' ? menColor : womenColor;
        const pickerMultipliers = pickerGender === 'men' ? menMultipliers : womenMultipliers;
        const pickerSlotLabel   = `${pickerGender === 'men' ? 'M' : 'W'}${pickerSlotIdx + 1}`;
        const pickerCurrentPid  = pickerGender === 'men'
          ? (tennismenSlots[pickerSlotIdx] ?? null)
          : (tenniswomenSlots[pickerSlotIdx] ?? null);
        const takenInOtherSlots = new Set(
          [...tennismenSlots, ...tenniswomenSlots].filter(Boolean).filter(pid => pid !== pickerCurrentPid) as string[]
        );
        const pickerPool = (pickerGender === 'men' ? allMen : allWomen)
          .filter(p => !takenInOtherSlots.has(p.playerId));
        const pickerFiltered = pickerPool
          .filter(p => {
            if (!pickerSearch) return true;
            const q = pickerSearch.toLowerCase();
            return p.name.toLowerCase().includes(q) || (p.team ?? '').toLowerCase().includes(q);
          })
          .sort(sortBySeadThenPoints);
        const pickerMaxPrice = Math.max(...pickerPool.map(p => p.price ?? 0), 1);
        const otherSlotsSpent = hasBudget
          ? [...tennismenSlots, ...tenniswomenSlots]
              .filter((pid): pid is string => !!pid && pid !== pickerCurrentPid)
              .reduce((sum, pid) => {
                const p = tennisPool.find(x => x.playerId === pid);
                return sum + (p?.price ?? 0);
              }, 0)
          : 0;

        return (
          <Dialog
            open={pickerOpen}
            onClose={() => setPickerOpen(false)}
            maxWidth="sm"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
              sx: {
                bgcolor: '#0A0F1A',
                backgroundImage: 'none',
                border: `1px solid ${alpha(pickerAccentColor, 0.22)}`,
                borderRadius: { xs: '12px 12px 0 0', sm: 2.5 },
                m: { xs: 0, sm: 2 },
                mt: { xs: 'auto', sm: 2 },
                maxHeight: { xs: '90vh', sm: '85vh' },
                display: 'flex',
                flexDirection: 'column',
              },
            }}
          >
            {/* Header */}
            <Box sx={{
              px: { xs: 1.5, sm: 2.5 }, pt: { xs: 2, sm: 2.5 }, pb: 2,
              background: `linear-gradient(135deg, ${alpha(pickerAccentColor, 0.1)}, transparent 60%)`,
              borderBottom: `1px solid rgba(255,255,255,0.06)`,
              flexShrink: 0,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{
                    width: 38, height: 38, borderRadius: '50%',
                    bgcolor: alpha(pickerAccentColor, 0.15),
                    border: `1.5px solid ${alpha(pickerAccentColor, 0.4)}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {pickerGender === 'men'
                      ? <Male sx={{ fontSize: 20, color: pickerAccentColor }} />
                      : <Female sx={{ fontSize: 20, color: pickerAccentColor }} />
                    }
                  </Box>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontSize: '1rem', color: colors.text.primary, lineHeight: 1 }}>
                        {pickerSlotLabel} — {pickerGender === 'men' ? "Men's" : "Women's"} Pick
                      </Typography>
                      <Chip
                        size="small"
                        label={`${pickerMultipliers[pickerSlotIdx]}x`}
                        sx={{ height: 18, fontSize: '0.62rem', fontWeight: 800, bgcolor: alpha(pickerAccentColor, 0.15), color: pickerAccentColor, border: `1px solid ${alpha(pickerAccentColor, 0.3)}` }}
                      />
                    </Box>
                    <Typography sx={{ fontSize: '0.7rem', color: alpha(colors.text.secondary, 0.45), mt: 0.3 }}>
                      {pickerPool.length} players available
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {hasBudget && (
                    <Chip
                      size="small"
                      label={`${remainingCredits}cr left`}
                      sx={{
                        height: 22, fontSize: '0.72rem', fontWeight: 700,
                        bgcolor: alpha(budgetBarColor, 0.15), color: budgetBarColor,
                        border: `1px solid ${alpha(budgetBarColor, 0.3)}`,
                      }}
                    />
                  )}
                  <IconButton size="small" onClick={() => setPickerOpen(false)} sx={{ color: alpha(colors.text.secondary, 0.45), '&:hover': { color: colors.text.primary } }}>
                    <Close sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              </Box>
            </Box>

            {/* Search */}
            <Box sx={{ px: { xs: 1.5, sm: 2.5 }, py: { xs: 1.25, sm: 1.75 }, borderBottom: `1px solid rgba(255,255,255,0.05)`, flexShrink: 0 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name or country..."
                value={pickerSearch}
                onChange={e => setPickerSearch(e.target.value)}
                autoFocus
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 16, color: alpha(colors.text.secondary, 0.4) }} />
                    </InputAdornment>
                  ),
                  endAdornment: pickerSearch ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setPickerSearch('')} sx={{ p: 0.3, color: alpha(colors.text.secondary, 0.4) }}>
                        <Close sx={{ fontSize: 14 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: alpha(pickerAccentColor, 0.04),
                    borderRadius: 1.5,
                    fontSize: '0.875rem',
                    '& fieldset': { borderColor: alpha(pickerAccentColor, 0.18) },
                    '&:hover fieldset': { borderColor: alpha(pickerAccentColor, 0.38) },
                    '&.Mui-focused fieldset': { borderColor: pickerAccentColor },
                  },
                  '& .MuiInputBase-input': { color: colors.text.primary },
                }}
              />
              {pickerSearch && (
                <Typography sx={{ fontSize: '0.68rem', color: alpha(colors.text.secondary, 0.38), mt: 0.75 }}>
                  {pickerFiltered.length} result{pickerFiltered.length !== 1 ? 's' : ''} for "{pickerSearch}"
                </Typography>
              )}
            </Box>

            {/* Player list */}
            <Box sx={{ overflowY: 'auto', flex: 1, px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 1.5 } }}>
              {pickerFiltered.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 5 }}>
                  <Typography sx={{ color: alpha(colors.text.secondary, 0.38), fontSize: '0.875rem' }}>
                    {pickerSearch ? `No players found for "${pickerSearch}"` : 'No players available'}
                  </Typography>
                </Box>
              ) : pickerFiltered.map(p => {
                const isCurrentSlot = p.playerId === pickerCurrentPid;
                const costAfterPick = otherSlotsSpent + (p.price ?? 0);
                const wouldExceed   = hasBudget && p.price !== undefined && !isCurrentSlot && costAfterPick > budget;
                const unpriced      = hasBudget && p.price === undefined;
                const priceBarPct   = p.price !== undefined ? Math.round((p.price / pickerMaxPrice) * 100) : 0;
                const tierColor = (price: number) =>
                  price >= 11 ? '#F59E0B' : price >= 8 ? pickerAccentColor : alpha(colors.text.secondary, 0.5);

                return (
                  <ButtonBase
                    key={p.playerId}
                    onClick={() => !unpriced && handlePickPlayer(p.playerId)}
                    disabled={unpriced}
                    sx={{
                      width: '100%', display: 'block', textAlign: 'left',
                      borderRadius: 2, mb: 0.75,
                      bgcolor: isCurrentSlot ? alpha(pickerAccentColor, 0.1) : 'transparent',
                      border: `1px solid ${isCurrentSlot ? alpha(pickerAccentColor, 0.3) : wouldExceed ? alpha('#EF4444', 0.12) : 'rgba(255,255,255,0.05)'}`,
                      opacity: unpriced ? 0.4 : 1,
                      transition: 'all 0.15s ease',
                      '&:hover:not(:disabled)': {
                        bgcolor: alpha(pickerAccentColor, 0.07),
                        border: `1px solid ${alpha(pickerAccentColor, 0.25)}`,
                        transform: 'translateY(-1px)',
                        boxShadow: `0 4px 16px ${alpha(pickerAccentColor, 0.12)}`,
                      },
                      '&:active:not(:disabled)': { transform: 'scale(0.99)' },
                    }}
                  >
                    <Box sx={{ px: { xs: 1, sm: 1.5 }, pt: { xs: 1, sm: 1.25 }, pb: hasBudget && p.price !== undefined ? 0.5 : 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5 }}>
                      {/* Left: seed + name + country */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                        {p.seed !== undefined ? (
                          <Box sx={{
                            width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                            bgcolor: p.seed <= 4 ? alpha('#FFD700', 0.15) : p.seed <= 8 ? alpha(colors.orange.primary, 0.12) : alpha(colors.text.secondary, 0.07),
                            border: `1.5px solid ${p.seed <= 4 ? alpha('#FFD700', 0.38) : p.seed <= 8 ? alpha(colors.orange.primary, 0.3) : 'rgba(255,255,255,0.09)'}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: p.seed <= 4 ? '#FFD700' : p.seed <= 8 ? colors.orange.light : alpha(colors.text.secondary, 0.5) }}>
                              {p.seed}
                            </Typography>
                          </Box>
                        ) : (
                          <Box sx={{ width: 28, height: 28, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography sx={{ fontSize: '0.65rem', color: alpha(colors.text.secondary, 0.2) }}>—</Typography>
                          </Box>
                        )}
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontFamily: "'Satoshi', sans-serif", fontWeight: 700, fontSize: { xs: '0.85rem', sm: '0.9rem' }, color: colors.text.primary, lineHeight: 1.25 }} noWrap>
                            {p.name}
                          </Typography>
                          <Typography sx={{ fontSize: '0.7rem', color: alpha(colors.text.secondary, 0.78), lineHeight: 1.3 }}>
                            {flagEmoji(p.team) || '🏳️'} {p.team === 'Neutral' ? 'Neutral' : p.team}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Right: price + points + check */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexShrink: 0 }}>
                        {isCurrentSlot && <CheckCircle sx={{ fontSize: 15, color: pickerAccentColor }} />}
                        {hasBudget && (
                          unpriced ? (
                            <Chip size="small" label="No price" sx={{ height: 18, fontSize: '0.6rem', bgcolor: alpha('#EF4444', 0.1), color: '#EF4444', border: `1px solid ${alpha('#EF4444', 0.25)}` }} />
                          ) : (
                            <Chip
                              size="small"
                              label={`${p.price}cr`}
                              sx={{
                                height: 20, fontSize: '0.7rem', fontWeight: 700,
                                bgcolor: wouldExceed ? alpha('#EF4444', 0.1) : alpha(tierColor(p.price!), 0.12),
                                color: wouldExceed ? '#EF4444' : tierColor(p.price!),
                                border: `1px solid ${wouldExceed ? alpha('#EF4444', 0.25) : alpha(tierColor(p.price!), 0.28)}`,
                              }}
                            />
                          )
                        )}
                        <Typography sx={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: '0.92rem', color: isCurrentSlot ? pickerAccentColor : alpha(colors.text.secondary, 0.55), minWidth: 44, textAlign: 'right' }}>
                          {p.points} pts
                        </Typography>
                      </Box>
                    </Box>

                    {/* Mini price bar */}
                    {hasBudget && p.price !== undefined && !unpriced && (
                      <Box sx={{ px: 1.5, pb: 1 }}>
                        <Box sx={{ width: '100%', height: 2, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.04)' }}>
                          <Box sx={{
                            width: `${priceBarPct}%`, height: '100%', borderRadius: 1,
                            bgcolor: wouldExceed ? alpha('#EF4444', 0.45) : alpha(tierColor(p.price!), 0.55),
                            transition: 'width 0.3s ease',
                          }} />
                        </Box>
                      </Box>
                    )}
                  </ButtonBase>
                );
              })}
            </Box>
          </Dialog>
        );
      })()}
      {/* ── Compact Sticky HUD ── */}
      <Fade in={showStickyHUD} timeout={220}>
        <Box
          sx={{
            position: 'fixed',
            top: { xs: '124px', sm: '140px', md: '148px' },
            left: 0,
            right: 0,
            zIndex: 999,
            background: `linear-gradient(180deg, ${alpha('#060D17', 0.97)} 0%, ${alpha('#0A1929', 0.97)} 100%)`,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderBottom: `1px solid rgba(255,255,255,0.07)`,
            borderTop: `1px solid rgba(255,255,255,0.04)`,
            boxShadow: '0 6px 24px rgba(0,0,0,0.45)',
            pointerEvents: showStickyHUD ? 'auto' : 'none',
          }}
        >
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            px: { xs: 2, sm: 3 },
            py: { xs: 0.9, sm: 1 },
            gap: 0,
          }}>
            {/* Budget remaining — budget leagues only */}
            {hasBudget && (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 }, flex: '0 0 auto', minWidth: { xs: 72, sm: 80 } }}>
                  <AccountBalance sx={{ fontSize: { xs: 13, sm: 15 }, color: isOverBudget ? '#EF4444' : budgetBarColor, flexShrink: 0 }} />
                  <Box>
                    <Typography sx={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: { xs: '1.15rem', sm: '1.3rem' }, color: isOverBudget ? '#EF4444' : budgetBarColor, lineHeight: 1 }}>
                      {remainingCredits < 0 ? `-${Math.abs(remainingCredits)}` : remainingCredits}
                      <Typography component="span" sx={{ fontSize: '0.55rem', fontFamily: "'Satoshi', sans-serif", fontWeight: 700, color: alpha(isOverBudget ? '#EF4444' : budgetBarColor, 0.65), ml: 0.3, letterSpacing: '0.04em' }}>CR</Typography>
                    </Typography>
                    <Typography sx={{ fontSize: '0.52rem', color: alpha(colors.text.secondary, 0.5), fontFamily: "'Satoshi', sans-serif", fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', lineHeight: 1 }}>
                      left
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ width: '1px', height: 28, bgcolor: 'rgba(255,255,255,0.07)', mx: { xs: 1.5, sm: 2 }, flexShrink: 0 }} />
              </>
            )}

            {/* Men's slots */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 }, flex: 1, minWidth: 0 }}>
              <Male sx={{ fontSize: { xs: 13, sm: 15 }, color: alpha(menColor, 0.85), flexShrink: 0 }} />
              <Box>
                <Typography sx={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: { xs: '1.15rem', sm: '1.3rem' }, color: menFilled === menSlots ? menColor : colors.text.primary, lineHeight: 1 }}>
                  {menFilled}<Typography component="span" sx={{ fontSize: '0.75rem', color: alpha(colors.text.secondary, 0.5) }}>/{menSlots}</Typography>
                </Typography>
                <Box sx={{ display: 'flex', gap: '2px', mt: '2px' }}>
                  {Array.from({ length: menSlots }, (_, i) => (
                    <Box key={i} sx={{ width: i < menFilled ? 8 : 5, height: 3, borderRadius: '2px', bgcolor: i < menFilled ? menColor : alpha(menColor, 0.18), transition: 'all 0.2s ease', boxShadow: i < menFilled ? `0 0 4px ${alpha(menColor, 0.55)}` : 'none' }} />
                  ))}
                </Box>
              </Box>
            </Box>

            <Box sx={{ width: '1px', height: 28, bgcolor: 'rgba(255,255,255,0.07)', mx: { xs: 1.5, sm: 2 }, flexShrink: 0 }} />

            {/* Women's slots */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 }, flex: 1, minWidth: 0 }}>
              <Female sx={{ fontSize: { xs: 13, sm: 15 }, color: alpha(womenColor, 0.85), flexShrink: 0 }} />
              <Box>
                <Typography sx={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: { xs: '1.15rem', sm: '1.3rem' }, color: womenFilled === womenSlots ? womenColor : colors.text.primary, lineHeight: 1 }}>
                  {womenFilled}<Typography component="span" sx={{ fontSize: '0.75rem', color: alpha(colors.text.secondary, 0.5) }}>/{womenSlots}</Typography>
                </Typography>
                <Box sx={{ display: 'flex', gap: '2px', mt: '2px' }}>
                  {Array.from({ length: womenSlots }, (_, i) => (
                    <Box key={i} sx={{ width: i < womenFilled ? 8 : 5, height: 3, borderRadius: '2px', bgcolor: i < womenFilled ? womenColor : alpha(womenColor, 0.18), transition: 'all 0.2s ease', boxShadow: i < womenFilled ? `0 0 4px ${alpha(womenColor, 0.4)}` : 'none' }} />
                  ))}
                </Box>
              </Box>
            </Box>

            <Box sx={{ width: '1px', height: 28, bgcolor: 'rgba(255,255,255,0.07)', mx: { xs: 1.5, sm: 2 }, flexShrink: 0 }} />

            {/* Predictions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, sm: 1 }, flex: '0 0 auto' }}>
              <EmojiEvents sx={{ fontSize: { xs: 13, sm: 15 }, color: alpha(colors.gold, 0.85), flexShrink: 0 }} />
              <Box>
                <Typography sx={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: { xs: '1.15rem', sm: '1.3rem' }, color: (mensPredictionId && womensPredictionId) ? colors.gold : colors.text.primary, lineHeight: 1 }}>
                  {[mensPredictionId, womensPredictionId].filter(Boolean).length}<Typography component="span" sx={{ fontSize: '0.75rem', color: alpha(colors.text.secondary, 0.5) }}>/2</Typography>
                </Typography>
                <Typography sx={{ fontSize: '0.52rem', color: alpha(colors.text.secondary, 0.5), fontFamily: "'Satoshi', sans-serif", fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', lineHeight: 1 }}>
                  picks
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Budget bar at bottom edge */}
          {hasBudget && (
            <Box sx={{ height: '3px', bgcolor: alpha('#0A1929', 0.8) }}>
              <Box sx={{ height: '100%', width: `${budgetPct}%`, bgcolor: budgetBarColor, transition: 'width 0.4s ease, background-color 0.3s ease', boxShadow: `0 0 6px ${alpha(budgetBarColor, 0.6)}` }} />
            </Box>
          )}
        </Box>
      </Fade>

    </Box>
  );
};

export default SquadSelectionPage;